import { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import ResellerShell from './ResellerShell'
import CommunicationHistory from '../../components/CommunicationHistory'
import Toggle from '../../components/Toggle'
import QuoteSettingsModal from './QuoteSettingsModal'
import PartialRefundModal from '../../components/PartialRefundModal'
import RefundInProgressPanel from '../../components/RefundInProgressPanel'
import RefundApprovalPanel from '../../components/RefundApprovalPanel'
import ProviderBadge, { ratesByProvider, providerMeta } from '../../components/ProviderBadge'
import HotelLocationMap from '../../components/HotelLocationMap'
import { tournamentApi, portalApi } from '../../services/api'
import { useAuth } from '../../context/AuthContext'
import { useLanguage } from '../../context/LanguageContext'
import { formatPhone, displayPhone } from '../../utils/phone'
import { newIdempotencyKey } from '../../utils/idempotency'
import { TIME_WINDOWS } from '../../utils/timeWindows'

/**
 * Custom Trip Builder (CTQ #152) — the reseller assembles a bespoke trip on a
 * day grid: set start date / days / golfers, then add and drag hotels (spanning
 * nights) and rounds day-to-day, plus custom line items. A live running total
 * (per-golfer vs budget) updates as you build.
 *
 * Talks to /api/portal/reseller/quotes/:id (+ /items). Hotels/rounds are entered
 * manually for now; live Expedia/course search hooks in later.
 *
 * CTQ feature branch — not deployed to production until the feature is done.
 */
const money = (n, cur = 'CAD') =>
  n == null ? '—' : new Intl.NumberFormat('en-CA', { style: 'currency', currency: cur, currencyDisplay: 'narrowSymbol' }).format(Number(n))
// Same, but with the currency code appended ("$558.90 USD") so the reseller can
// verify the currency at a glance on hotel + golf prices.
const moneyc = (n, cur = 'CAD') => n == null ? money(n, cur) : `${money(n, cur)} ${cur}`
// Number part only, comma-grouped with 2 decimals ($###,###.##) — for the
// "{cur} ${amt}" i18n templates that bake in their own $ sign.
const amt2 = (n) => Number(n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

function toYmd(v) { if (!v) return null; const d = new Date(v); return isNaN(d) ? null : d.toISOString().slice(0, 10) }
function addDays(ymd, n) { if (!ymd) return null; const d = new Date(ymd + 'T00:00:00Z'); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10) }
function fmtDay(ymd) { if (!ymd) return ''; try { return new Date(ymd + 'T00:00:00Z').toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' }) } catch { return ymd } }
// Same as fmtDay but with the year — used on the itinerary day-column headers
// so a reseller building a trip that spans a year boundary (or far out) sees it.
function fmtDayY(ymd) { if (!ymd) return ''; try { return new Date(ymd + 'T00:00:00Z').toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) } catch { return ymd } }
// Whole days between two YYYY-MM-DD dates (checkout - checkin = nights = numDays).
function daysBetween(a, b) { if (!a || !b) return 0; return Math.round((new Date(b + 'T00:00:00Z') - new Date(a + 'T00:00:00Z')) / 86400000) }
// Tee-time helpers: values are 24h "HH:MM[:SS]" (a leading ~ marks a manual
// estimate). teeMinutes parses the first time for sorting (TBD/none sorts last);
// fmtTee renders it as "h:MM AM/PM", preserving any ~ prefix.
function teeMinutes(tt) { if (!tt) return Infinity; const m = String(tt).match(/(\d{1,2}):(\d{2})/); return m ? Number(m[1]) * 60 + Number(m[2]) : Infinity }
function fmtTee(tt) { if (!tt) return null; return String(tt).replace(/(\d{1,2}):(\d{2})(?::\d{2})?/g, (_, h, mm) => { let hr = Number(h); const ap = hr >= 12 ? 'PM' : 'AM'; hr = hr % 12 || 12; return `${hr}:${mm} ${ap}` }) }
export default function CustomTripBuilder() {
  const { id } = useParams()
  const { t } = useLanguage()
  const navigate = useNavigate()
  const [quote, setQuote] = useState(null)
  const [items, setItems] = useState([])
  const [totals, setTotals] = useState(null)
  const [paymentPlan, setPaymentPlan] = useState(null)
  const [refundContext, setRefundContext] = useState(null)
  const [refunds, setRefunds] = useState([])
  const [refundedTotal, setRefundedTotal] = useState(0)
  const [pendingRefund, setPendingRefund] = useState(null)
  const [canIssueRefunds, setCanIssueRefunds] = useState(false)
  const [canApproveRefunds, setCanApproveRefunds] = useState(false)
  const [refundLimit, setRefundLimit] = useState(null)
  // Stable idempotency key for approve-cancel; cleared on success (#237).
  const cancelKeyRef = useRef(null)
  const [cancelBusy, setCancelBusy] = useState(false)
  const [golfers, setGolfers] = useState([])
  const [payments, setPayments] = useState([])
  const [requestCourses, setRequestCourses] = useState([])
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)
  const [dragId, setDragId] = useState(null)
  const [addFor, setAddFor] = useState(null) // { dayIndex, type } | 'custom' | null
  const [editRound, setEditRound] = useState(null) // { itemId, dayIndex } — round being re-picked via live search
  const [rechecking, setRechecking] = useState(false)
  const [verifying, setVerifying] = useState(false)
  const [verifyResult, setVerifyResult] = useState(null)
  const [sending, setSending] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [showPartialRefund, setShowPartialRefund] = useState(false)
  const [autoBuilding, setAutoBuilding] = useState(false)
  const [buildReport, setBuildReport] = useState(null)

  // silent=true refreshes data WITHOUT flipping the loading screen — so a
  // re-render after saving a field doesn't unmount the inputs and steal focus.
  const load = useCallback((silent = false) => {
    if (!silent) setLoading(true)
    tournamentApi.get(`/portal/reseller/quotes/${id}`)
      .then(r => { setQuote(r.data.quote); setItems(r.data.items || []); setTotals(r.data.totals); setPaymentPlan(r.data.paymentPlan || null); setRefundContext(r.data.refundContext || null); setRefunds(r.data.refunds || []); setRefundedTotal(Number(r.data.refundedTotal) || 0); setPendingRefund(r.data.pendingRefund || null); setCanIssueRefunds(!!r.data.canIssueRefunds); setCanApproveRefunds(!!r.data.canApproveRefunds); setRefundLimit(r.data.refundLimit ?? null); setGolfers(r.data.golfers || []); setPayments(r.data.payments || []); setRequestCourses(r.data.requestCourses || []); setMessages(r.data.messages || []) })
      .catch(e => setErr(e?.response?.data?.message || e.message))
      .finally(() => { if (!silent) setLoading(false) })
  }, [id])
  useEffect(() => { load() }, [load])

  // Self-heal legacy hotels whose stay overflows the trip (e.g. a 4-night hotel
  // dragged onto Day 2 of a 4-day trip). Clamp each such hotel once — re-verify
  // shrinks it to fit + re-prices. Guarded so an unavailable re-check can't loop.
  const healedRef = useRef(new Set())
  useEffect(() => {
    if (!quote?.numDays || !quote.tripStartDate || !items.length) return
    const overflow = items.find(i =>
      i.ItemType === 'hotel' && !healedRef.current.has(i.ID_NO) &&
      Number(i.Nights) > Math.max(1, quote.numDays - Number(i.DayIndex)))
    if (overflow) {
      healedRef.current.add(overflow.ID_NO)
      reverify(overflow.ID_NO, { nights: Math.max(1, quote.numDays - Number(overflow.DayIndex)) })
    }
  }, [items, quote]) // eslint-disable-line

  async function patchQuote(body) {
    try {
      const { data } = await tournamentApi.patch(`/portal/reseller/quotes/${id}`, body)
      setQuote(data.quote); load(true)
    } catch (e) {
      // Never fail silently — a swallowed 401 (expired session) or validation
      // error made edits look saved when they weren't. Surface it.
      alert(e?.response?.status === 401
        ? t('sp_ctq_session_expired', 'Your session expired — please sign in again. Your last change was NOT saved.')
        : (e?.response?.data?.message || e.message))
    }
  }
  async function addItem(body) {
    const { data } = await tournamentApi.post(`/portal/reseller/quotes/${id}/items`, body)
    setItems(x => [...x, data.item]); setTotals(data.totals); setAddFor(null)
  }
  // Convert a customer request into an editable in-progress quote.
  async function startBuilding() {
    try { await tournamentApi.post(`/portal/reseller/quotes/${id}/start`); load(true) }
    catch (e) { alert(e?.response?.data?.message || e.message) }
  }
  // Auto-assemble a draft from the customer's request (rounds from their picks +
  // a central hotel). Reseller verifies every line — nothing books or sends.
  async function autoBuild() {
    setAutoBuilding(true); setBuildReport(null)
    try {
      const { data } = await tournamentApi.post(`/portal/reseller/quotes/${id}/auto-build`)
      setBuildReport(data.report || null)
      load(true)
    } catch (e) { alert(e?.response?.data?.message || e.message) }
    finally { setAutoBuilding(false) }
  }
  // Move/resize re-verifies live inventory for the new date so prices never go
  // stale. On 'unavailable' (409) the item stays put; we reload to restore truth.
  async function reverify(itemId, body) {
    setRechecking(true)
    try {
      const { data } = await tournamentApi.patch(`/portal/reseller/quotes/${id}/items/${itemId}/reverify`, body)
      setItems(x => x.map(i => i.ID_NO === itemId ? data.item : i)); setTotals(data.totals)
    } catch (e) {
      alert(e?.response?.data?.message || e.message)
      load()
    } finally { setRechecking(false) }
  }
  const moveItem = (itemId, dayIndex) => reverify(itemId, { dayIndex })
  async function removeItem(itemId) {
    const { data } = await tournamentApi.delete(`/portal/reseller/quotes/${id}/items/${itemId}`)
    setItems(x => x.filter(i => i.ID_NO !== itemId)); setTotals(data.totals)
  }
  // Inline field edit (rounds): course name / tee time / golfers / price. Uses
  // the same updateItem endpoint as move/resize; recomputes totals server-side.
  async function updateItem(itemId, body) {
    const { data } = await tournamentApi.patch(`/portal/reseller/quotes/${id}/items/${itemId}`, body)
    setItems(x => x.map(i => i.ID_NO === itemId ? data.item : i)); setTotals(data.totals)
  }
  // Change a hotel's checkout (nights) / room count — re-verifies the real rate
  // via Expedia (no estimate), same re-check path as a drag.
  const setHotelNights = (item, nights) => reverify(item.ID_NO, { nights })
  const setHotelRooms = (item, rooms) => reverify(item.ID_NO, { rooms })

  // Edge-resize a hotel bar to grow/shrink the stay: right edge changes the
  // checkout (nights), left edge changes the check-in day (keeping checkout).
  // Live preview while dragging; commits via reverify (real re-price) on release.
  const [resizing, setResizing] = useState(null) // { itemId, edge, startX, d0, n0, d, n }
  function startResize(e, h, edge) {
    e.preventDefault(); e.stopPropagation()
    const d0 = Number(h.DayIndex), n0 = Math.max(1, Number(h.Nights) || 1)
    setResizing({ itemId: h.ID_NO, edge, startX: e.clientX, d0, n0, d: d0, n: n0 })
  }
  useEffect(() => {
    if (!resizing) return
    const PITCH = 222 // column width (210) + gap (12)
    const onMove = (e) => {
      const delta = Math.round((e.clientX - resizing.startX) / PITCH)
      let d = resizing.d0, n = resizing.n0
      if (resizing.edge === 'right') n = Math.min(Math.max(1, resizing.n0 + delta), (quote.numDays || 1) - resizing.d0)
      else { d = Math.min(Math.max(0, resizing.d0 + delta), resizing.d0 + resizing.n0 - 1); n = (resizing.d0 + resizing.n0) - d }
      setResizing(r => (r ? { ...r, d, n } : r))
    }
    const onUp = () => {
      setResizing(r => {
        if (r && (r.d !== r.d0 || r.n !== r.n0)) {
          if (r.edge === 'right') reverify(r.itemId, { nights: r.n })
          else reverify(r.itemId, { dayIndex: r.d, nights: r.n })
        }
        return null
      })
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    return () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp) }
  }, [resizing?.itemId, resizing?.startX, resizing?.edge]) // eslint-disable-line
  async function deleteTrip() {
    if (!window.confirm(t('sp_ctq_delete_confirm', 'Delete {q}? This cannot be undone.', { q: quote?.quoteNumber || 'this trip' }))) return
    try {
      await tournamentApi.delete(`/portal/reseller/quotes/${id}`)
      navigate('/portal/reseller/custom-trips')
    } catch (e) { alert(e?.response?.data?.message || e.message) }
  }

  // Change the trip's check-in and/or length. The server recomputes every
  // item's dates + prunes anything outside the new range; we then re-verify so
  // availability + pricing reflect the new dates.
  async function rescheduleTrip(body) {
    const target = body.numDays != null ? body.numDays : Number(quote.numDays)
    if (!(target >= 1 && target <= 60)) { alert(t('sp_ctq_bad_range', 'Trips must be 1 to 60 nights.')); return }
    if (target < Number(quote.numDays)) {
      const willRemove = items.filter(i => { const di = Number(i.DayIndex); return i.ItemType === 'round' ? di > target : (i.ItemType === 'hotel' ? di >= target : false) })
      if (willRemove.length && !window.confirm(t('sp_ctq_reschedule_confirm', 'These dates drop {c} item(s) outside the trip. Continue?', { c: willRemove.length }))) return
    }
    try {
      const { data } = await tournamentApi.patch(`/portal/reseller/quotes/${id}/reschedule`, body)
      setQuote(data.quote); setItems(data.items || []); setTotals(data.totals)
      healedRef.current = new Set()
      verifyAvailability()
    } catch (e) { alert(e?.response?.data?.message || e.message) }
  }

  // Send the quote to the customer: status -> sent, starts the 14-day clock,
  // and emails them a link to the read-only quote page. Requires an email.
  async function sendQuote() {
    if (!quote.masterEmail) { alert(t('sp_ctq_need_email', 'Add a customer email before sending.')); return }
    const resend = quote.status !== 'in_progress'
    if (!window.confirm(resend
      ? t('sp_ctq_resend_confirm', 'Re-send this quote to {e}?', { e: quote.masterEmail })
      : t('sp_ctq_send_confirm', 'Send this quote to {e}? They will get an email with a link to view it.', { e: quote.masterEmail }))) return
    setSending(true)
    try {
      const { data } = await tournamentApi.post(`/portal/reseller/quotes/${id}/send`)
      setQuote(data.quote)
      alert(data.email?.ok
        ? t('sp_ctq_sent_ok', 'Quote sent to {e}.', { e: quote.masterEmail })
        : t('sp_ctq_sent_no_email', 'Quote marked as sent, but the email may not have gone out. You can copy the link below and send it manually.'))
    } catch (e) { alert(e?.response?.data?.message || e.message) }
    finally { setSending(false) }
  }

  // Bulk re-check: are all the live items (hotels + tee-sheet rounds) still
  // available at their current dates? Refreshes prices in place and shows a
  // summary of what stayed put, what moved, and what's gone.
  async function verifyAvailability() {
    setVerifying(true); setVerifyResult(null)
    try {
      const { data } = await tournamentApi.post(`/portal/reseller/quotes/${id}/verify`)
      if (data.items) setItems(data.items)
      if (data.totals) setTotals(data.totals)
      setVerifyResult(data)
    } catch (e) {
      alert(e?.response?.data?.message || e.message)
    } finally { setVerifying(false) }
  }

  // Mark a customer change request handled (or re-open it) — updates in place.
  async function resolveMessage(msgId, resolved) {
    try {
      const { data } = await tournamentApi.post(`/portal/reseller/quotes/${id}/messages/${msgId}/resolve`, { resolved })
      setMessages(x => x.map(m => m.id === msgId ? data.message : m))
    } catch (e) { alert(e?.response?.data?.message || e.message) }
  }
  // Approve a customer's cancellation request: refund (capped) + cancel the trip.
  async function approveCancel(refundAmount) {
    if (!cancelKeyRef.current) cancelKeyRef.current = newIdempotencyKey()
    setCancelBusy(true)
    const body = refundAmount == null ? {} : { refundAmount }
    const doPost = (override) => tournamentApi.post(`/portal/reseller/quotes/${id}/cancel/approve`, { ...body, override }, { headers: { 'Idempotency-Key': cancelKeyRef.current } })
    try {
      let data
      try { data = (await doPost(false)).data }
      catch (e) {
        const code = e?.response?.data?.code
        // Completed / past trip (guardrail #239): owner confirms an override; staff blocked.
        if (code === 'NEEDS_OVERRIDE') {
          if (!window.confirm(t('pr_override_confirm', 'This trip is already completed or its dates have passed. Issue the refund anyway?'))) return
          data = (await doPost(true)).data
        } else if (code === 'OWNER_OVERRIDE_REQUIRED') {
          alert(t('pr_override_owner_only', 'This trip is completed or its dates have passed — only the account owner can refund it.')); return
        } else throw e
      }
      cancelKeyRef.current = null
      if (data.awaitingApproval) {
        alert(t('pr_submitted_pending_approval', "Refund set up. It's above your limit, so it was sent to the account owner for approval — the customer won't be refunded until they approve."))
      } else {
        const parts = [t('sp_ctq_cancel_done', 'Trip cancelled.')]
        if (data.refunded > 0) parts.push(t('sp_ctq_cancel_refunded', 'Refunded {cur} ${amt}.', { cur: quote.currency, amt: amt2(data.refunded) }))
        if (data.errors && data.errors.length) parts.push(data.errors.join('; '))
        alert(parts.join(' '))
      }
      load(true)
    } catch (e) {
      alert(e?.response?.data?.message || e.message)
    } finally { setCancelBusy(false) }
  }
  // Post a reply the customer will see on their quote page. When notify is true
  // the customer is also emailed a snippet + link (the reply box checkbox).
  async function replyToCustomer(body, notify) {
    const { data } = await tournamentApi.post(`/portal/reseller/quotes/${id}/messages`, { body, notify })
    setMessages(x => [...x, data.message])
    // An emailed reply on a change-requested quote flips it back to Sent — pull
    // the fresh quote so the status badge + "needs re-send" banner update.
    if (data.statusChanged) load(true)
  }

  if (loading) return <ResellerShell title={t('sp_nav_custom_trips', 'Custom Trips')}><div style={{ padding: 20, color: '#64748b' }}>Loading…</div></ResellerShell>
  if (err) return <ResellerShell title={t('sp_nav_custom_trips', 'Custom Trips')}><div style={{ padding: 12, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#991b1b' }}>{err}</div></ResellerShell>
  if (!quote) return null

  const cur = quote.currency || 'CAD'
  const setupDone = quote.tripStartDate && quote.numDays && quote.numGolfers
  const start = toYmd(quote.tripStartDate)
  const customItems = items.filter(i => i.ItemType === 'custom')

  // Stack hotels into lanes so different hotels / overlapping dates (e.g. two
  // rooms at the same place, or two groups in different hotels) don't collide.
  const hotelItems = items.filter(i => i.ItemType === 'hotel')
  const hotelLane = {}
  let hotelLaneCount = 0
  {
    const laneEnds = []
    for (const h of [...hotelItems].sort((a, b) => Number(a.DayIndex) - Number(b.DayIndex) || a.ID_NO - b.ID_NO)) {
      const s = Number(h.DayIndex), e = s + Math.max(1, Number(h.Nights) || 1)
      let lane = laneEnds.findIndex(end => end <= s)
      if (lane === -1) { lane = laneEnds.length; laneEnds.push(e) } else laneEnds[lane] = e
      hotelLane[h.ID_NO] = lane
    }
    hotelLaneCount = laneEnds.length
  }

  // CTQ multi-stop legs (display-only): group located items (hotels + rounds,
  // which carry StopLabel) by city/first-part, each with its day span, so the
  // timeline can show a leg bar when a trip visits >1 location. Mirrors the
  // backend computeLegs grouping (normalized on the first comma-part).
  const builderLegs = (() => {
    const located = items.filter(i => i.StopLabel && (i.ItemType === 'hotel' || i.ItemType === 'round'))
    if (!located.length) return []
    const norm = s => String(s || '').split(',')[0].trim().toLowerCase()
    const byKey = new Map()
    for (const i of located) {
      const key = norm(i.StopLabel)
      if (!byKey.has(key)) byKey.set(key, { label: i.StopLabel, minDay: Infinity, maxDay: -Infinity })
      const leg = byKey.get(key)
      const d = Number(i.DayIndex) || 0
      const endD = i.ItemType === 'hotel' ? d + Math.max(1, Number(i.Nights) || 1) - 1 : d
      leg.minDay = Math.min(leg.minDay, d); leg.maxDay = Math.max(leg.maxDay, endD)
    }
    return [...byKey.values()].sort((a, b) => a.minDay - b.minDay)
  })()

  return (
    <ResellerShell title={t('sp_nav_custom_trips', 'Custom Trips')}>
      {rechecking && (
        <div style={{ position: 'fixed', top: 12, left: '50%', transform: 'translateX(-50%)', zIndex: 100, background: '#0f172a', color: '#fff', padding: '8px 16px', borderRadius: 999, fontSize: 13, boxShadow: '0 4px 20px rgba(0,0,0,0.25)' }}>
          {t('sp_ctq_rechecking', 'Re-checking availability & price…')}
        </div>
      )}
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        <div>
          <Link to="/portal/reseller/custom-trips" style={{ fontSize: 13, color: '#2563eb', textDecoration: 'none' }}>&larr; {t('sp_ctq_all', 'All custom trips')}</Link>
          <input
            defaultValue={quote.name || ''} placeholder={t('sp_ctq_name_ph', 'Trip name')}
            onBlur={e => { if ((e.target.value || '') !== (quote.name || '')) patchQuote({ name: e.target.value }) }}
            style={{ display: 'block', width: '100%', maxWidth: 380, marginTop: 6, fontSize: 20, fontWeight: 700, color: '#0f172a', border: 'none', borderBottom: '1px dashed #cbd5e1', padding: '2px 0' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, color: '#94a3b8' }}>{quote.quoteNumber} · {cur}</span>
            <StatusBadge status={quote.status} />
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 12, color: '#94a3b8' }}>{t('sp_ctq_send_in', 'Send in')}</span>
              <div style={{ display: 'inline-flex', border: '1px solid #cbd5e1', borderRadius: 6, overflow: 'hidden' }}>
                {['en', 'fr'].map(l => (
                  <button key={l} onClick={() => { if ((quote.language || 'en') !== l) patchQuote({ language: l }) }}
                    style={{ padding: '2px 10px', border: 'none', fontSize: 12, fontWeight: 600, cursor: 'pointer', background: (quote.language || 'en') === l ? '#0f172a' : '#fff', color: (quote.language || 'en') === l ? '#fff' : '#475569' }}>
                    {l === 'en' ? 'EN' : 'FR'}
                  </button>
                ))}
              </div>
            </span>
          </div>
          {setupDone && <HeaderLocation quote={quote} onSave={patchQuote} t={t} />}
          {setupDone && <HeaderContact quote={quote} onSave={patchQuote} onReschedule={rescheduleTrip} t={t} />}
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {setupDone && (
            <button onClick={() => setShowSettings(true)}
              title={t('sp_ctq_settings_title', 'Quote message, contact, photo & language')}
              style={{ padding: '8px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', cursor: 'pointer' }}>
              ⚙ {t('sp_ctq_quote_settings', 'Quote settings')}
            </button>
          )}
          {setupDone && (
            <button onClick={() => window.open(`/portal/reseller/custom-trips/${id}/preview`, '_blank')}
              title={t('sp_ctq_preview_title', 'See what the customer will get, before sending')}
              style={{ padding: '8px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', cursor: 'pointer' }}>
              👁 {t('sp_ctq_preview', 'Preview')}
            </button>
          )}
          {setupDone && (
            <button onClick={sendQuote} disabled={sending}
              style={{ padding: '8px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: 'none', background: '#0f172a', color: '#fff', cursor: sending ? 'default' : 'pointer', opacity: sending ? 0.7 : 1 }}>
              {sending ? t('sp_ctq_sending', 'Sending…') : (quote.status === 'in_progress' ? `✈ ${t('sp_ctq_send', 'Send quote')}` : `↻ ${t('sp_ctq_resend', 'Re-send')}`)}
            </button>
          )}
          {setupDone && (
            <button onClick={verifyAvailability} disabled={verifying} title={t('sp_ctq_verify_title', 'Re-check that all hotels & tee times are still available at these dates')}
              style={{ padding: '8px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: '1px solid #c7d2fe', background: verifying ? '#eef2ff' : '#fff', color: '#4338ca', cursor: verifying ? 'default' : 'pointer' }}>
              {verifying ? t('sp_ctq_verifying', 'Checking…') : `✓ ${t('sp_ctq_verify', 'Verify availability')}`}
            </button>
          )}
          {setupDone && !pendingRefund && canIssueRefunds && ['accepted_partial', 'accepted_paid'].includes(quote.status) && (
            <button onClick={() => setShowPartialRefund(true)}
              title={t('pr_partial_refund_title', 'Refund one golfer who backed out — up to the refundable portion')}
              style={{ padding: '8px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: '1px solid #fcd34d', background: '#fff', color: '#b45309', cursor: 'pointer' }}>
              {t('pr_partial_refund_btn', 'Partial refund')}
            </button>
          )}
          <button onClick={deleteTrip} title={t('sp_ctq_delete', 'Delete')}
            style={{ padding: '8px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: '1px solid #fecaca', background: '#fff', color: '#ef4444', cursor: 'pointer' }}>
            🗑 {t('sp_ctq_delete', 'Delete')}
          </button>
        </div>
      </div>

      {showPartialRefund && (
        <PartialRefundModal
          kind="quote"
          id={id}
          t={t}
          onClose={() => setShowPartialRefund(false)}
          onDone={() => load(true)}
        />
      )}

      {quote.hasCustomerRequest && (
        <CustomerRequestPanel quote={quote} requestCourses={requestCourses}
          onStart={quote.status === 'requested' ? startBuilding : null}
          onAutoBuild={['requested', 'in_progress'].includes(quote.status) ? autoBuild : null}
          autoBuilding={autoBuilding} buildReport={buildReport} t={t} />
      )}

      {verifyResult && <VerifyBanner result={verifyResult} cur={cur} onClose={() => setVerifyResult(null)} t={t} />}

      {setupDone && quote.status === 'change_requested' && (
        <div style={{ marginBottom: 12, padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, fontSize: 13, color: '#92400e', display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 700 }}>⚠ {t('sp_ctq_cr_banner_title', 'Changes to send back to the customer')}</span>
          <span>{t('sp_ctq_cr_banner_body', 'The customer requested changes. Reply to them with “Email the customer” on, or Re-send the updated quote — either one moves this back to Sent.')}</span>
        </div>
      )}

      {setupDone && quote.status === 'declined_cancelled' && quote.refundedAt && (
        <div style={{ marginBottom: 12, padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 13, color: '#475569' }}>
          {t('sp_ctq_cancelled_note', 'This trip was cancelled.')}{' '}
          {quote.refundAmount > 0
            ? t('sp_ctq_cancelled_refund', 'Refunded {cur} ${amt} to the customer.', { cur: quote.currency, amt: amt2(quote.refundAmount) })
            : t('sp_ctq_cancelled_norefund', 'No refund was issued.')}
        </div>
      )}

      {setupDone && quote.cancelRequestedAt && canIssueRefunds && ['accepted_partial', 'accepted_paid'].includes(quote.status) && (
        <CancelRequestPanel quote={quote} refundContext={refundContext} busy={cancelBusy} onApprove={approveCancel} refundLimit={refundLimit} t={t} />
      )}

      {setupDone && quote.publicToken && quote.status !== 'in_progress' && (
        <SentLinkBanner quote={quote} link={`${window.location.origin}/quote/${quote.publicToken}`} t={t} />
      )}

      {pendingRefund && pendingRefund.status === 'awaiting_approval' && <RefundApprovalPanel pending={pendingRefund} canApprove={canApproveRefunds} onDone={() => load(true)} t={t} />}
      {pendingRefund && pendingRefund.status !== 'awaiting_approval' && <RefundInProgressPanel pending={pendingRefund} base={`/portal/reseller/quotes/${id}`} onDone={() => load(true)} t={t} canAct={canIssueRefunds} />}

      {refundedTotal > 0 && <RefundsPanel refunds={refunds} refundedTotal={refundedTotal} cur={cur} t={t} />}

      {messages.length > 0 && (
        <CustomerRequestsPanel messages={messages} onResolve={resolveMessage} onReply={replyToCustomer} t={t} />
      )}

      {setupDone && (
        <GolferRoster quoteId={id} golfers={golfers}
          locked={['accepted_partial', 'accepted_paid', 'expired', 'declined_cancelled'].includes(quote.status)}
          onSaved={setGolfers} t={t} />
      )}

      {setupDone && <SplitCollectionPanel golfers={golfers} payments={payments} paymentPlan={paymentPlan} totals={totals} quote={quote} quoteId={id} cur={cur} t={t} />}

      {!setupDone ? (
        <SetupForm quote={quote} onSave={patchQuote} t={t} />
      ) : (
       <>
        {quote.centerLat == null && <DestinationBanner quote={quote} onSave={patchQuote} t={t} />}
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 300px', gap: 20, alignItems: 'start' }}>
          {/* Itinerary card */}
          <div>
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, color: '#0f172a' }}>{t('sp_ctq_itinerary', 'Itinerary')}</div>
              <div style={{ overflowX: 'auto', paddingBottom: 8 }}>
                <div style={{ minWidth: 'min-content' }}>
              {/* Multi-stop legs bar — a line over the day columns showing each
                  stop and the days it covers. Only when the trip visits >1 place.
                  Aligned to the 222px day-column pitch (210 width + 12 gap). */}
              {builderLegs.length > 1 && (
                <div style={{ position: 'relative', height: 22, marginBottom: 8, width: (quote.numDays + 1) * 222 - 12 }}>
                  {builderLegs.map((leg, i) => {
                    const span = Math.max(1, leg.maxDay - leg.minDay + 1)
                    return (
                      <div key={i} title={leg.label}
                        style={{ position: 'absolute', left: leg.minDay * 222, width: Math.max(80, span * 222 - 12), top: 0, height: 20, background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 6, color: '#3730a3', fontSize: 11, fontWeight: 600, padding: '2px 8px', boxSizing: 'border-box', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center' }}>
                        📍 {leg.label.split(',').slice(0, 2).join(',')}
                      </div>
                    )
                  })}
                </div>
              )}
              {/* Day headers (+ a Departure column so a checkout on the morning
                  after the last day has a place to land) */}
              <div style={{ display: 'flex', gap: 12, marginBottom: 8 }}>
                {Array.from({ length: quote.numDays + 1 }, (_, d) => (
                  <div key={d} style={{ width: 210, flexShrink: 0 }}>
                    {d < quote.numDays ? (
                      <>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{t('sp_ctq_day', 'Day')} {d + 1}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>{fmtDayY(addDays(start, d))}</div>
                      </>
                    ) : (
                      <>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#7c3aed' }}>{t('sp_ctq_departure', 'Departure')}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>{fmtDayY(addDays(start, quote.numDays))}</div>
                      </>
                    )}
                  </div>
                ))}
              </div>

              {/* Stays band — hotels are bars spanning the nights they cover;
                  overlapping stays stack into lanes so different hotels/rooms
                  for different golfers read clearly. */}
              <div style={{ fontSize: 10.5, fontWeight: 700, color: '#7c3aed', letterSpacing: 0.4, marginBottom: 4 }}>{t('sp_ctq_stays', 'STAYS')}</div>
              <div style={{ position: 'relative', marginBottom: 8 }}>
                {/* one subtle day-cell lane per stack (drag targets); >=1 when empty */}
                {Array.from({ length: Math.max(hotelLaneCount, 1) }, (_, lane) => (
                  <div key={lane} style={{ display: 'flex', gap: 12, marginBottom: lane < Math.max(hotelLaneCount, 1) - 1 ? 8 : 0 }}>
                    {Array.from({ length: quote.numDays + 1 }, (_, d) => (
                      <div key={d}
                        onDragOver={d < quote.numDays ? (e => e.preventDefault()) : undefined}
                        onDrop={d < quote.numDays ? (e => { e.preventDefault(); if (dragId != null) { moveItem(dragId, d); setDragId(null) } }) : undefined}
                        style={{ width: 210, flexShrink: 0, height: 76, border: '1px dashed #eef2f7', borderRadius: 8, background: d === quote.numDays ? '#faf9ff' : '#fcfcfe' }} />
                    ))}
                  </div>
                ))}
                {hotelItems.map(h => {
                  const isRz = resizing?.itemId === h.ID_NO
                  const d = isRz ? resizing.d : Number(h.DayIndex)
                  const n = isRz ? resizing.n : Math.max(1, Number(h.Nights) || 1)
                  const rm = Math.max(1, Number(h.Rooms) || 1)
                  return (
                    <div key={h.ID_NO} draggable onDragStart={() => setDragId(h.ID_NO)}
                      style={{ position: 'absolute', top: hotelLane[h.ID_NO] * 84, left: d * 222 + 105, width: n * 222, height: 76, boxSizing: 'border-box', background: '#fff', border: '1px solid #ddd6fe', borderLeft: '3px solid #7c3aed', borderRadius: 8, padding: '6px 9px', cursor: 'grab', overflow: 'hidden', boxShadow: isRz ? '0 4px 14px rgba(124,58,237,0.25)' : 'none' }}>
                      <div onMouseDown={e => startResize(e, h, 'left')} title={t('sp_ctq_resize', 'Drag to resize the stay')} style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 9, cursor: 'ew-resize', zIndex: 2 }} />
                      <div onMouseDown={e => startResize(e, h, 'right')} title={t('sp_ctq_resize', 'Drag to resize the stay')} style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 9, cursor: 'ew-resize', zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div style={{ width: 3, height: 22, background: '#c4b5fd', borderRadius: 2 }} />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                        <div style={{ fontSize: 12.5, fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>{h.HotelName || t('sp_ctq_hotel', 'Hotel')}</div>
                        <b style={{ fontSize: 12.5, color: '#7c3aed', flexShrink: 0 }}>{moneyc(h.TotalPrice, cur)}</b>
                        <button onClick={() => removeItem(h.ID_NO)} title={t('sp_ctq_remove', 'Remove')} style={{ border: 'none', background: 'none', color: '#cbd5e1', cursor: 'pointer', fontSize: 15, lineHeight: 1, flexShrink: 0 }}>&times;</button>
                      </div>
                      {/* Dates below the name: check-in -> check-out. */}
                      <div style={{ fontSize: 11, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {fmtDay(toYmd(h.CheckInDate))} → {fmtDay(toYmd(h.CheckOutDate))}
                        {Number(h.RefundedRooms) > 0 && <span style={{ color: '#b45309', fontWeight: 600 }}> · ↩ {Number(h.RefundedRooms) === 1
                          ? t('pr_room_refunded_one', '{n} room refunded', { n: h.RefundedRooms })
                          : t('pr_room_refunded_many', '{n} rooms refunded', { n: h.RefundedRooms })}</span>}
                      </div>
                      {/* Controls: room count + room type next to it, then the checkout (nights) picker. */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                        <select value={rm} title={t('sp_ctq_rooms_full', 'rooms')} onMouseDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()} onChange={e => setHotelRooms(h, Number(e.target.value))}
                          style={{ fontSize: 10.5, border: '1px solid #e2e8f0', borderRadius: 4, padding: '1px 3px', color: '#475569', cursor: 'pointer', flexShrink: 0 }}>
                          {Array.from({ length: Math.max(rm, quote.numGolfers || 8) }, (_, i) => i + 1).map(x => <option key={x} value={x}>{x} {t('sp_ctq_rm', 'rm')}</option>)}
                        </select>
                        <span title={h.RoomType || t('sp_ctq_room', 'Room')} style={{ fontSize: 10.5, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>{h.RoomType || t('sp_ctq_room', 'Room')}</span>
                        <select value={n} title={t('sp_ctq_checkout', 'checkout')} onMouseDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()} onChange={e => setHotelNights(h, Number(e.target.value))}
                          style={{ fontSize: 10.5, border: '1px solid #e2e8f0', borderRadius: 4, padding: '1px 3px', color: '#475569', cursor: 'pointer', flexShrink: 0, maxWidth: 112 }}>
                          {Array.from({ length: Math.max(1, quote.numDays - d) }, (_, i) => i + 1).map(nn => <option key={nn} value={nn}>{t('sp_ctq_out', 'out')} {fmtDay(addDays(start, d + nn))}</option>)}
                        </select>
                      </div>
                    </div>
                  )
                })}
              </div>
              <button onClick={() => setAddFor({ dayIndex: 0, type: 'hotel' })}
                style={{ marginBottom: 16, padding: '6px 12px', fontSize: 12.5, fontWeight: 600, border: '1px dashed #c4b5fd', borderRadius: 8, background: '#faf5ff', color: '#7c3aed', cursor: 'pointer' }}>
                + {t('sp_ctq_add_hotel_row', 'Add hotel')}
              </button>

              {/* Rounds per day */}
              <div style={{ fontSize: 10.5, fontWeight: 700, color: '#16a34a', letterSpacing: 0.4, marginBottom: 4 }}>{t('sp_ctq_rounds_lbl', 'ROUNDS')}</div>
              <div style={{ display: 'flex', gap: 12 }}>
                {Array.from({ length: quote.numDays + 1 }, (_, d) => {
                  const rounds = items.filter(i => i.ItemType === 'round' && Number(i.DayIndex) === d)
                    .sort((a, b) => teeMinutes(a.TeeTime) - teeMinutes(b.TeeTime))
                  const isDeparture = d === quote.numDays
                  return (
                    <div key={d}
                      onDragOver={e => e.preventDefault()}
                      onDrop={e => { e.preventDefault(); if (dragId != null) { moveItem(dragId, d); setDragId(null) } }}
                      style={{ width: 210, flexShrink: 0, background: isDeparture ? '#faf9ff' : '#f8fafc', border: `1px solid ${isDeparture ? '#ede9fe' : '#e2e8f0'}`, borderRadius: 10, padding: 10, minHeight: 140 }}>
                      {rounds.map(r => {
                        // Auto-build adds a placeholder round when it can't find a live
                        // tee time — no price set. Flag it amber (not the "done" green)
                        // so the reseller sees it still needs a tee time + price booked.
                        const needsSetup = r.TotalPrice == null
                        return (
                        <ItemCard key={r.ID_NO} item={r} cur={cur} accent={needsSetup ? '#d97706' : '#16a34a'} t={t} onDragStart={() => setDragId(r.ID_NO)} onEdit={() => setEditRound({ itemId: r.ID_NO, dayIndex: d, coursesId: r.Courses_ID, golfers: r.GolfersOnRound, courseName: r.CourseName, teeTime: r.TeeTime, pricePerGolfer: r.PricePerGolfer, netRate: r.NetRateRound })} onRemove={() => removeItem(r.ID_NO)}>
                          <div style={{ fontWeight: 600 }}>{r.CourseName || t('sp_ctq_round', 'Round')}</div>
                          <div style={{ color: '#64748b' }}>{fmtTee(r.TeeTime) || t('sp_ctq_tbd', 'tee time TBD')} · {r.GolfersOnRound || quote.numGolfers} {(r.GolfersOnRound || quote.numGolfers) === 1 ? t('sp_ctq_golfer_one', 'golfer') : t('sp_ctq_golfers_word', 'golfers')}</div>
                          {!!r.NetRateRound && (
                            <div style={{ marginTop: 3, display: 'inline-block', fontSize: 10, fontWeight: 700, color: '#6d28d9', background: '#f3e8ff', border: '1px solid #e9d5ff', borderRadius: 5, padding: '1px 6px' }}>
                              {t('sp_ctq_round_netrate_badge', 'Net rate')}
                            </div>
                          )}
                          {needsSetup && (
                            <div style={{ marginTop: 3, color: '#b45309', fontWeight: 700, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 5, padding: '2px 5px', lineHeight: 1.3 }}>
                              ⚠ {t('sp_ctq_round_needs_setup', 'Book a tee time & set the price')}
                            </div>
                          )}
                          {Number(r.RefundedGolfers) > 0 && (
                            <div style={{ marginTop: 2, color: '#b45309', fontWeight: 600 }}>
                              ↩ {Number(r.RefundedGolfers) === 1
                                ? t('pr_golfer_refunded_one', '{n} golfer refunded', { n: r.RefundedGolfers })
                                : t('pr_golfer_refunded_many', '{n} golfers refunded', { n: r.RefundedGolfers })}
                            </div>
                          )}
                        </ItemCard>
                        )
                      })}
                      <MiniAdd onClick={() => setAddFor({ dayIndex: d, type: 'round' })} style={{ marginTop: rounds.length ? 6 : 0 }}>+ {t('sp_ctq_round', 'Round')}</MiniAdd>
                    </div>
                  )
                })}
              </div>
                </div>
              </div>
            </div>

            {/* Custom line items */}
            <div style={{ marginTop: 16, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, maxWidth: 560 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>{t('sp_ctq_extras', 'Custom line items')}</div>
              {customItems.length === 0 && <div style={{ fontSize: 12.5, color: '#94a3b8', marginBottom: 8 }}>{t('sp_ctq_extras_hint', 'Transfers, club rental, or anything not in inventory.')}</div>}
              {customItems.map(c => (
                <div key={c.ID_NO} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderTop: '1px solid #f1f5f9', fontSize: 13 }}>
                  <span>{c.Label || t('sp_ctq_custom', 'Custom')}</span>
                  <span style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <b>{money(c.TotalPrice, cur)}</b>
                    <button onClick={() => removeItem(c.ID_NO)} style={{ border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 15 }}>&times;</button>
                  </span>
                </div>
              ))}
              {addFor === 'custom'
                ? <AddCustomForm t={t} onCancel={() => setAddFor(null)} onAdd={f => addItem({ itemType: 'custom', ...f })} />
                : <MiniAdd onClick={() => setAddFor('custom')} style={{ marginTop: 8 }}>+ {t('sp_ctq_add_custom', 'Add line item')}</MiniAdd>}
            </div>
          </div>

          {/* Summary + payment options */}
          <div style={{ display: 'grid', gap: 16 }}>
            <SummaryCard quote={quote} totals={totals} cur={cur} refundedTotal={refundedTotal} onServiceFee={patchQuote} t={t}
              hasExpediaHotel={items.some(i => i.ItemType === 'hotel'
                && String(i.HotelSource || 'expedia').toLowerCase() === 'expedia'
                && String(i.HotelSalesEnvironment || 'hotel_package').toLowerCase() !== 'hotel_only')} />
            <PaymentOptionsCard quote={quote} paymentPlan={paymentPlan} cur={cur} onSave={patchQuote} t={t} />
          </div>
        </div>
       </>
      )}

      <CommunicationHistory quoteId={id} />

      {showSettings && (
        <QuoteSettingsModal quote={quote} onSaveQuote={patchQuote} onClose={() => setShowSettings(false)} t={t} />
      )}

      {addFor && addFor.type === 'hotel' && (
        <HotelSearchModal quote={quote} cur={cur} start={start} initialDayIndex={addFor.dayIndex || 0} t={t}
          onClose={() => setAddFor(null)}
          onPick={fields => addItem({ itemType: 'hotel', ...fields })} />
      )}
      {addFor && addFor.type === 'round' && (
        <RoundSearchModal quote={quote} cur={cur} playDate={addDays(start, addFor.dayIndex)} t={t}
          onClose={() => setAddFor(null)}
          onPick={fields => addItem({ itemType: 'round', dayIndex: addFor.dayIndex, ...fields })} />
      )}
      {/* Editing an existing round = re-pick a live tee time / course; PATCH in place. */}
      {editRound && (
        <RoundSearchModal quote={quote} cur={cur} playDate={addDays(start, editRound.dayIndex)} t={t}
          heading={t('sp_ctq_edit_round', 'Edit round')}
          preselectCourseId={editRound.coursesId || null} initialGolfers={editRound.golfers || null}
          presetCourseName={editRound.courseName || ''} presetTeeTime={editRound.teeTime || ''} presetPrice={editRound.pricePerGolfer}
          presetNetRate={!!editRound.netRate}
          onClose={() => setEditRound(null)}
          onPick={fields => updateItem(editRound.itemId, { dayIndex: editRound.dayIndex, ...fields }).then(() => setEditRound(null))} />
      )}
    </ResellerShell>
  )
}

// Shown once a quote has been sent: the customer link (copyable) + validity.
// Reseller-entered trip roster (golfer names/emails/phones). The lead/master
// (if set from split-pay) shows read-only; the rest are editable and saved as a
// batch. Locked once the trip is accepted so it can't disturb split-pay state.
function GolferRoster({ quoteId, golfers, locked, onSaved, t }) {
  const master = golfers.find(g => g.IsMaster)
  const toRows = (gs) => gs.filter(g => !g.IsMaster).map(g => ({ name: g.Name || '', email: g.Email || '', phone: displayPhone(g.Phone || '') }))
  const [rows, setRows] = useState(toRows(golfers))
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  useEffect(() => { setRows(toRows(golfers)) }, [golfers]) // eslint-disable-line

  const setRow = (i, k, v) => { setRows(rs => rs.map((r, j) => j === i ? { ...r, [k]: v } : r)); setSaved(false) }
  const addRow = () => { setRows(rs => [...rs, { name: '', email: '', phone: '' }]); setSaved(false) }
  const removeRow = (i) => { setRows(rs => rs.filter((_, j) => j !== i)); setSaved(false) }
  async function save() {
    setSaving(true); setSaved(false)
    try {
      const { data } = await tournamentApi.put(`/portal/reseller/quotes/${quoteId}/golfers`, { golfers: rows.filter(r => r.name.trim()) })
      onSaved?.(data.golfers || [])
      setSaved(true)
    } catch (e) { alert(e?.response?.data?.message || e.message) }
    finally { setSaving(false) }
  }

  const inp = { padding: '7px 9px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }
  return (
    <div style={{ marginBottom: 16, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>{t('sp_ctq_roster_title', 'Golfers on this trip')}</div>
      <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 12 }}>{t('sp_ctq_roster_hint', 'Optional — the party going on the trip. Used for split-pay invites and confirmations.')}</div>
      {master && (
        <div style={{ fontSize: 12.5, color: '#475569', marginBottom: 10 }}>
          {t('sp_ctq_roster_lead', 'Lead')}: <strong>{master.Name}</strong>{master.Email ? ` · ${master.Email}` : ''}
        </div>
      )}
      {rows.map((r, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input disabled={locked} value={r.name} onChange={e => setRow(i, 'name', e.target.value)} placeholder={t('sp_name_label', 'Name')} style={{ ...inp, flex: '1 1 140px' }} />
          <input disabled={locked} value={r.email} onChange={e => setRow(i, 'email', e.target.value)} placeholder="email@example.com" type="email" style={{ ...inp, flex: '1 1 170px' }} />
          <input disabled={locked} value={r.phone} onChange={e => setRow(i, 'phone', formatPhone(e.target.value))} placeholder={t('sp_store_phone', 'Phone')} style={{ ...inp, flex: '1 1 130px' }} />
          {!locked && <button type="button" onClick={() => removeRow(i)} title={t('sp_remove', 'Remove')} style={{ border: 'none', background: 'none', color: '#cbd5e1', cursor: 'pointer', fontSize: 16 }}>✕</button>}
        </div>
      ))}
      {!locked && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
          <button type="button" onClick={addRow} style={{ border: '1px dashed #cbd5e1', background: '#fff', color: '#475569', borderRadius: 6, fontSize: 12.5, fontWeight: 600, padding: '6px 12px', cursor: 'pointer' }}>+ {t('sp_ctq_roster_add', 'Add golfer')}</button>
          <button type="button" onClick={save} disabled={saving} style={{ border: 'none', background: '#0f172a', color: '#fff', borderRadius: 6, fontSize: 12.5, fontWeight: 700, padding: '7px 14px', cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1 }}>{saving ? t('saving', 'Saving…') : t('sp_ctq_roster_save', 'Save roster')}</button>
          {saved && <span style={{ fontSize: 12, color: '#16a34a' }}>✓ {t('saved', 'Saved')}</span>}
        </div>
      )}
    </div>
  )
}

// Live split-pay collection status for the trip planner (reseller): which golfers
// have paid their first-charge share and who is still outstanding — so stragglers
// are visible on the planning page WITHOUT waiting for the trip to book. Read-only.
// Mirrors the backend's "paid" rule exactly (golferFirstChargePaid in
// resellerQuotesController.js: deposit/full bucket, first installment,
// authorized|paid) plus CoveredByLead counts as paid. Only renders once a split
// has actually been set up — i.e. golfers carry a FirstChargeShare.
function SplitCollectionPanel({ golfers, payments, paymentPlan, totals, quote, quoteId, cur, t }) {
  const [open, setOpen] = useState({})       // golferId -> expanded?
  const [copied, setCopied] = useState(null) // golferId whose link was just copied
  const [remind, setRemind] = useState({})   // golferId -> 'sending' | 'sent' | 'error'
  const splitGolfers = (golfers || []).filter(g => g.FirstChargeShare != null)
  if (splitGolfers.length === 0) return null

  const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100
  const tripTotal = Number(totals?.total) || 0
  const plan = quote?.splitPlan || null
  const planSchedule = (plan && paymentPlan?.plans?.[plan]?.schedule) || []

  const rowsOf = (gid) => (payments || []).filter(p => Number(p.Golfer_ID) === Number(gid))
  const firstIn = (rows) => rows.some(p => ['deposit', 'full'].includes(p.Bucket) && ['authorized', 'paid'].includes(p.Status) && (p.InstallmentNo == null || Number(p.InstallmentNo) === 1))

  // Each golfer's payment schedule: match the chosen plan's steps to the golfer's
  // real payment rows (status + due date), projecting the amount from their share
  // fraction when a future installment doesn't exist as a row yet (pre-book).
  function scheduleFor(g) {
    const rows = rowsOf(g.ID_NO)
    const frac = (g.AmountOwed != null && tripTotal > 0) ? (Number(g.AmountOwed) / tripTotal) : 0
    const steps = planSchedule.length ? planSchedule : [{ bucket: 'full', installmentNo: 1, when: 'now', amount: g.FirstChargeShare }]
    return steps.map((s, i) => {
      // A golfer can have MORE than one row for a step — e.g. a stale 'voided' hold
      // from a lapsed/reset window plus the live re-paid one (the builder GET returns
      // voided/declined rows too). Prefer the LIVE row (authorized/paid) so a paid
      // golfer never reads as "awaiting"; else the most recent attempt; else projected.
      const matches = rows.filter(p => p.Bucket === s.bucket && Number(p.InstallmentNo || 1) === Number(s.installmentNo || 1))
      const real = matches.find(p => ['authorized', 'paid'].includes(p.Status))
        || matches.find(p => p.Status === 'scheduled')
        || matches[matches.length - 1] || null
      const amount = real ? Number(real.Amount) : r2(Number(s.amount || 0) * frac)
      const status = g.CoveredByLead ? 'covered' : (real ? real.Status : 'scheduled')
      const when = (real && real.DueDate) ? toYmd(real.DueDate) : (s.when && s.when !== 'now' ? toYmd(s.when) : null)
      return { bucket: s.bucket, installmentNo: s.installmentNo, amount, status, when, isFirst: i === 0 }
    })
  }

  const rows = splitGolfers.map(g => {
    const sched = scheduleFor(g)
    const secured = g.CoveredByLead || firstIn(rowsOf(g.ID_NO))
    const outstanding = g.CoveredByLead ? 0 : r2(sched.filter(s => !['paid', 'authorized'].includes(s.status)).reduce((a, s) => a + s.amount, 0))
    return {
      id: g.ID_NO, name: g.Name || '—', email: g.Email || '', isLead: !!g.IsMaster, covered: !!g.CoveredByLead,
      last4: g.VaultCardLast4 || null, secured, outstanding, sched,
      payLink: g.MagicToken ? `${window.location.origin}/pay-share/${g.MagicToken}` : null,
      // Remindable: a non-lead, non-covered golfer whose share isn't in yet + has email.
      canRemind: !g.IsMaster && !g.CoveredByLead && !!g.Email && !firstIn(rowsOf(g.ID_NO)),
      // Resendable: any non-lead, non-covered golfer with an email + a pay-share link —
      // the reseller can re-send the invite at will (even after paying; the server
      // short-circuits a paid golfer to "already paid" rather than re-emailing).
      canResend: !g.IsMaster && !g.CoveredByLead && !!g.Email && !!g.MagicToken,
    }
  })
  const paidCount = rows.filter(r => r.secured).length
  const pct = rows.length ? Math.round((paidCount / rows.length) * 100) : 0
  const allPaid = paidCount === rows.length

  const payBadge = (status) => {
    const map = {
      paid:       ['#dcfce7', '#166534', t('sp_ctq_split_paid', 'Paid')],
      authorized: ['#eff6ff', '#1e40af', t('sp_ctq_split_authorized', 'Authorized')],
      scheduled:  ['#f1f5f9', '#475569', t('sp_ctq_split_scheduled', 'Scheduled')],
      declined:   ['#fee2e2', '#b91c1c', t('sp_ctq_split_declined', 'Declined')],
      covered:    ['#f1f5f9', '#475569', t('sp_ctq_split_covered', 'Covered by lead')],
    }
    const [bg, fg, label] = map[status] || ['#fff7ed', '#9a3412', t('sp_ctq_split_unpaid', 'Awaiting payment')]
    return <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 7px', borderRadius: 999, background: bg, color: fg, whiteSpace: 'nowrap' }}>{label}</span>
  }
  const stepLabel = (s) => s.bucket === 'full' ? t('sp_ctq_pl_full', 'Full payment')
    : s.bucket === 'deposit' ? t('sp_ctq_pl_deposit', 'Deposit')
      : (s.installmentNo != null ? t('sp_ctq_pl_installment', 'Installment {n}', { n: s.installmentNo }) : t('sp_ctq_pl_payment', 'Payment'))

  async function copyLink(r) {
    if (!r.payLink) return
    try { await navigator.clipboard.writeText(r.payLink); setCopied(r.id); setTimeout(() => setCopied(c => (c === r.id ? null : c)), 1800) }
    catch { /* clipboard blocked — the link is still visible to select + copy */ }
  }
  async function sendReminder(r) {
    setRemind(m => ({ ...m, [r.id]: 'sending' }))
    try {
      const { data } = await tournamentApi.post(`/portal/reseller/quotes/${quoteId}/golfers/${r.id}/remind`)
      setRemind(m => ({ ...m, [r.id]: (data && data.alreadyPaid) ? 'alreadyPaid' : 'sent' }))
    } catch { setRemind(m => ({ ...m, [r.id]: 'error' })) }
  }

  return (
    <div style={{ marginBottom: 16, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{t('sp_ctq_split_title', 'Split payment collection')}</div>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: allPaid ? '#16a34a' : '#64748b' }}>
          {t('sp_ctq_split_progress', '{paid} of {total} paid', { paid: paidCount, total: rows.length })}
        </div>
      </div>
      <div style={{ height: 6, borderRadius: 999, background: '#e2e8f0', overflow: 'hidden', marginBottom: 4 }}>
        <div style={{ height: '100%', width: pct + '%', background: '#16a34a', transition: 'width 0.3s' }} />
      </div>

      {rows.map(r => {
        const isOpen = !!open[r.id]
        const rstate = remind[r.id]
        return (
          <div key={r.id} style={{ borderTop: '1px solid #f1f5f9' }}>
            <div onClick={() => setOpen(o => ({ ...o, [r.id]: !o[r.id] }))}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', cursor: 'pointer' }}>
              <span style={{ color: '#94a3b8', fontSize: 11, width: 12, flexShrink: 0 }}>{isOpen ? '▾' : '▸'}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>
                  {r.name}{r.isLead && <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>{t('sp_ctq_split_lead_tag', ' · lead')}</span>}
                </div>
                {(r.email || r.last4) && (
                  <div style={{ fontSize: 11.5, color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {[r.email, r.last4 ? `•••• ${r.last4}` : null].filter(Boolean).join(' · ')}
                  </div>
                )}
              </div>
              <div style={{ flexShrink: 0, textAlign: 'right', fontSize: 11.5, fontWeight: 700, color: r.covered ? '#475569' : (r.outstanding > 0 ? '#9a3412' : '#16a34a') }}>
                {r.covered ? t('sp_ctq_split_covered', 'Covered by lead')
                  : r.outstanding > 0 ? `${cur} $${amt2(r.outstanding)} ${t('sp_ctq_split_outstanding', 'outstanding')}`
                    : t('sp_ctq_split_all_in', 'All in')}
              </div>
            </div>

            {isOpen && (
              <div style={{ padding: '0 0 12px 22px' }}>
                {r.sched.map((s, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, fontSize: 12, padding: '3px 0' }}>
                    <span style={{ color: '#475569' }}>{stepLabel(s)}{s.when ? ` · ${t('sp_ctq_split_due', 'due')} ${fmtDay(s.when)}` : (s.isFirst ? ` · ${t('sp_ctq_split_due_now', 'due now')}` : '')}</span>
                    <span style={{ whiteSpace: 'nowrap' }}>
                      <span style={{ color: '#0f172a', fontWeight: 600 }}>{cur} ${amt2(s.amount)}</span>
                      <span style={{ marginLeft: 6 }}>{payBadge(s.status)}</span>
                    </span>
                  </div>
                ))}
                {r.payLink && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
                    <input readOnly value={r.payLink} onFocus={e => e.target.select()}
                      style={{ flex: 1, minWidth: 0, fontSize: 11.5, color: '#475569', padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#f8fafc' }} />
                    <button type="button" onClick={() => copyLink(r)}
                      style={{ flexShrink: 0, border: '1px solid #e2e8f0', background: '#fff', color: '#0f766e', borderRadius: 6, fontSize: 11.5, fontWeight: 700, padding: '5px 10px', cursor: 'pointer' }}>
                      {copied === r.id ? t('sp_ctq_split_copied', 'Copied ✓') : t('sp_ctq_split_copy', 'Copy link')}
                    </button>
                    {r.canResend && (
                      <button type="button" disabled={rstate === 'sending'} onClick={() => sendReminder(r)}
                        style={{ flexShrink: 0, border: '1px solid #e2e8f0', background: rstate === 'sent' ? '#f0fdf4' : '#fff', color: rstate === 'sent' ? '#166534' : rstate === 'error' ? '#b91c1c' : rstate === 'alreadyPaid' ? '#64748b' : '#0f766e', borderRadius: 6, fontSize: 11.5, fontWeight: 700, padding: '5px 10px', cursor: rstate === 'sending' ? 'default' : 'pointer', whiteSpace: 'nowrap' }}>
                        {rstate === 'sending' ? t('sp_ctq_split_reminding', 'Sending…')
                          : rstate === 'sent' ? t('sp_ctq_split_resent', 'Resent ✓')
                            : rstate === 'alreadyPaid' ? t('sp_ctq_split_already_paid', 'Already paid')
                              : rstate === 'error' ? t('sp_ctq_split_remind_retry', 'Failed — try again')
                                : t('sp_ctq_split_resend', 'Resend')}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// Customer-originated trip request summary (concierge intake). Shows the dates,
// group, rooms, preferred-course hints and notes the golfer submitted. On a
// still-'requested' quote it offers "Start building" to convert it to editable.
function CustomerRequestPanel({ quote, requestCourses, onStart, onAutoBuild, autoBuilding, buildReport, t }) {
  const fmtWin = (w) => {
    if (!w) return null
    const h = Number(String(w).slice(0, 2))
    if (!Number.isFinite(h)) return w
    const ap = (hh) => { const p = hh >= 12 ? 'PM' : 'AM'; const hr = hh % 12 || 12; return `${hr} ${p}` }
    return `${ap(h)} – ${ap(h + 1)}`
  }
  const rows = [
    [t('sp_ctr_f_arrival', 'Arrival date'), (quote.requestArriveDate || quote.tripStartDate) ? fmtDay(toYmd(quote.requestArriveDate || quote.tripStartDate)) : null],
    [t('sp_ctr_f_depart', 'Departure date'), quote.requestDepartDate ? fmtDay(toYmd(quote.requestDepartDate)) : null],
    [t('sp_ctr_f_destination', 'Destination'), quote.requestDestination],
    [t('sp_ctr_f_nights', 'Nights'), quote.requestNights],
    [t('sp_ctr_f_golfdays', 'Golf days'), quote.requestGolfDays],
    [t('sp_ctr_f_golfers', 'Golfers'), quote.requestGolfers || quote.numGolfers],
    [t('sp_ctr_f_rooms', 'Rooms'), quote.requestRooms],
    [t('sp_ctr_f_beds', 'Beds'), quote.requestBeds],
    [t('sp_ctr_f_hotel_plan', 'Hotel plan'), quote.requestHotelStyle === 'multiple' ? t('sp_ctr_hotel_multiple', 'Multiple hotels') : quote.requestHotelStyle === 'central' ? t('sp_ctr_hotel_central', 'One central hotel') : null],
    [t('sp_ctr_f_rounds_pg', 'Rounds / golfer'), quote.requestRoundsPerGolfer],
    [t('sp_ctr_f_pref_time', 'Preferred start'), fmtWin(quote.requestPreferredTime)],
    [t('sp_ctq_req_courses', 'Preferred courses'), requestCourses.length ? requestCourses.map(c => c.name).join(', ') : null],
  ].filter(([, v]) => v != null && v !== '')
  return (
    <div style={{ marginBottom: 12, padding: '12px 16px', background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13.5, fontWeight: 800, color: '#5b21b6' }}>📋 {t('sp_ctq_req_panel_title', 'Customer request')}</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {onAutoBuild && (
            <button onClick={onAutoBuild} disabled={autoBuilding}
              title={t('sp_ctq_autobuild_hint', 'Draft the trip from this request — rounds from the requested courses + a central hotel. You verify before sending; nothing books.')}
              style={{ padding: '7px 14px', borderRadius: 8, fontSize: 13, fontWeight: 700, border: '1px solid #6d28d9', background: '#fff', color: '#6d28d9', cursor: autoBuilding ? 'wait' : 'pointer', opacity: autoBuilding ? 0.7 : 1 }}>
              {autoBuilding ? t('sp_ctq_autobuild_busy', 'Building draft…') : `✨ ${t('sp_ctq_autobuild', 'Auto-build draft')}`}
            </button>
          )}
          {onStart && (
            <button onClick={onStart} style={{ padding: '7px 14px', borderRadius: 8, fontSize: 13, fontWeight: 700, border: 'none', background: '#6d28d9', color: '#fff', cursor: 'pointer' }}>
              {t('sp_ctq_req_start', 'Start building')} →
            </button>
          )}
        </div>
      </div>
      {buildReport && <AutoBuildReport report={buildReport} t={t} />}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 20px', marginTop: 8 }}>
        {rows.map(([k, v]) => (
          <div key={k} style={{ fontSize: 13 }}>
            <span style={{ color: '#7c6ba8' }}>{k}: </span>
            <span style={{ color: '#0f172a', fontWeight: 600 }}>{v}</span>
          </div>
        ))}
      </div>
      {Array.isArray(quote.requestRoundPicks) && quote.requestRoundPicks.length > 0 && (
        <div style={{ marginTop: 8, fontSize: 13 }}>
          <span style={{ color: '#7c6ba8', fontWeight: 600 }}>{t('sp_ctr_round_picks', 'Requested rounds')}:</span>
          <ul style={{ margin: '4px 0 0', paddingLeft: 18, color: '#0f172a' }}>
            {quote.requestRoundPicks.map((p, i) => (
              <li key={i}>{p.courseName || `Course #${p.coursesId}`}{p.date ? ` — ${fmtDay(toYmd(p.date))}` : ''}</li>
            ))}
          </ul>
        </div>
      )}
      {Array.isArray(quote.requestExtraGolfers) && quote.requestExtraGolfers.length > 0 && (
        <div style={{ marginTop: 8, fontSize: 13 }}>
          <span style={{ color: '#7c6ba8', fontWeight: 600 }}>{t('sp_ctr_extra_golfers', 'Additional golfers')}: </span>
          <span style={{ color: '#0f172a' }}>{quote.requestExtraGolfers.map(g => g.name || g.email).filter(Boolean).join(', ')}</span>
        </div>
      )}
      {quote.requestNotes && (
        <div style={{ marginTop: 8, padding: '8px 12px', background: '#fff', border: '1px solid #ede9fe', borderRadius: 8, fontSize: 13, color: '#475569', whiteSpace: 'pre-wrap' }}>{quote.requestNotes}</div>
      )}
    </div>
  )
}

// Summary of what Auto-build just drafted — each line flagged with a confidence
// dot so the reseller knows what to double-check. Nothing here is booked/sent.
function AutoBuildReport({ report, t }) {
  const dot = (c) => ({ high: '#16a34a', medium: '#d97706', low: '#dc2626' }[c] || '#94a3b8')
  const Line = ({ c, children }) => (
    <li style={{ display: 'flex', gap: 8, alignItems: 'baseline', margin: '2px 0' }}>
      {c && <span style={{ width: 8, height: 8, borderRadius: '50%', background: dot(c), flex: '0 0 auto', transform: 'translateY(1px)' }} />}
      <span>{children}</span>
    </li>
  )
  const rounds = report.rounds || []
  const built = (report.added || 0) > 0
  return (
    <div style={{ marginTop: 10, padding: '10px 12px', background: '#fff', border: '1px solid #ddd6fe', borderRadius: 8, fontSize: 13, color: '#0f172a' }}>
      <div style={{ fontWeight: 700, color: '#5b21b6', marginBottom: 4 }}>
        ✨ {built
          ? t('sp_ctq_autobuild_done', 'Drafted {n} item(s) — review each line below, then adjust as needed.', { n: report.added })
          : t('sp_ctq_autobuild_none', 'Nothing new to add — see notes below.')}
      </div>
      <ul style={{ margin: '4px 0 0', padding: 0, listStyle: 'none' }}>
        {report.hotel && (
          <Line c={report.hotel.confidence}>
            🏨 {report.hotel.name} — {report.hotel.nights}n × {report.hotel.rooms} {t('sp_ctr_f_rooms', 'Rooms').toLowerCase()}
            {report.hotel.total != null ? ` · $${report.hotel.total}` : ''}
            {report.hotel.distanceKm != null ? ` · ${report.hotel.distanceKm} km` : ''}
          </Line>
        )}
        {rounds.map((r, i) => (
          <Line key={i} c={r.confidence}>
            ⛳ {r.course}{r.date ? ` — ${fmtDay(toYmd(r.date))}` : ''}
            {r.teeTime ? ` · ${r.teeTime}` : ''}{r.price != null ? ` · $${r.price}/golfer` : ''}
            <span style={{ color: '#64748b' }}>{r.note ? ` — ${r.note}` : ''}</span>
          </Line>
        ))}
      </ul>
      {(report.notes || []).map((n, i) => (
        <div key={`n${i}`} style={{ color: '#64748b', marginTop: 4 }}>• {n}</div>
      ))}
      {(report.warnings || []).map((w, i) => (
        <div key={`w${i}`} style={{ color: '#b45309', marginTop: 4 }}>⚠ {w}</div>
      ))}
    </div>
  )
}

function SentLinkBanner({ quote, link, t }) {
  const [copied, setCopied] = useState(false)
  const validUntil = quote.validUntil ? fmtDay(toYmd(quote.validUntil)) : null
  return (
    <div style={{ marginBottom: 16, padding: '12px 14px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10 }}>
      <div style={{ fontSize: 13.5, fontWeight: 700, color: '#1e40af', marginBottom: 6 }}>
        ✅ {t('sp_ctq_sent_title', 'Quote sent to the customer')}{validUntil ? ` · ${t('sp_ctq_valid_until', 'valid until {d}', { d: validUntil })}` : ''}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <input readOnly value={link} onFocus={e => e.target.select()}
          style={{ flex: 1, minWidth: 240, padding: '6px 9px', border: '1px solid #bfdbfe', borderRadius: 6, fontSize: 12.5, color: '#334155', background: '#fff' }} />
        <button onClick={() => { navigator.clipboard?.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1500) }}
          style={{ padding: '6px 12px', borderRadius: 6, fontSize: 12.5, fontWeight: 600, border: 'none', background: '#1e40af', color: '#fff', cursor: 'pointer' }}>
          {copied ? t('sp_ctq_copied', 'Copied!') : t('sp_ctq_copy_link', 'Copy link')}
        </button>
        <a href={link} target="_blank" rel="noreferrer" style={{ fontSize: 12.5, fontWeight: 600, color: '#1e40af' }}>{t('sp_ctq_preview', 'Preview')}</a>
      </div>
    </div>
  )
}

// The customer asked to cancel an accepted trip. The reseller reviews the
// server-computed refund cap (collected − non-refundable) and approves: the
// refund is issued (capped) and the trip moves to Cancelled. The reseller may
// refund LESS than the cap (e.g. keep a cancellation fee) but never more.
function CancelRequestPanel({ quote, refundContext, busy, onApprove, refundLimit, t }) {
  const cur = quote.currency || 'CAD'
  const cap = refundContext ? Number(refundContext.maxRefund) : null
  const captured = refundContext ? Number(refundContext.captured) : null
  const nonRef = refundContext ? Number(refundContext.nonRefundable) : null
  const [amount, setAmount] = useState('') // blank = refund the full cap
  const [confirming, setConfirming] = useState(false)
  const asked = amount === '' ? cap : Math.min(cap ?? 0, Math.max(0, Number(amount) || 0))
  // Over the caller's refund ceiling? It'll be parked for owner approval (#236).
  const needsApproval = refundLimit != null && (asked ?? 0) > refundLimit
  return (
    <div style={{ marginBottom: 12, padding: '12px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, fontSize: 13, color: '#991b1b' }}>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>⚠ {t('sp_ctq_cancel_req_title', 'Customer requested a cancellation')}</div>
      {quote.cancelReason && <div style={{ marginBottom: 8, color: '#7f1d1d' }}>“{quote.cancelReason}”</div>}
      {refundContext && (
        <div style={{ fontSize: 12.5, color: '#7f1d1d', marginBottom: 10 }}>
          {t('sp_ctq_cancel_collected', 'Collected')}: {cur} ${amt2(captured)}
          {nonRef > 0 ? ` · ${t('sp_ctq_cancel_nonref', 'non-refundable')}: ${cur} $${amt2(nonRef)}` : ''}
          {' · '}<strong>{t('sp_ctq_cancel_cap', 'refundable up to')} {cur} ${amt2(cap)}</strong>
        </div>
      )}
      {!confirming ? (
        <button onClick={() => setConfirming(true)} disabled={busy}
          style={{ padding: '7px 14px', borderRadius: 6, fontSize: 13, fontWeight: 700, border: 'none', background: '#dc2626', color: '#fff', cursor: busy ? 'not-allowed' : 'pointer' }}>
          {t('sp_ctq_cancel_review', 'Review & refund…')}
        </button>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <label style={{ fontSize: 12.5, color: '#7f1d1d' }}>{t('sp_ctq_cancel_refund_label', 'Refund amount')}</label>
          <input type="number" min="0" max={cap ?? undefined} step="0.01" value={amount}
            onChange={e => setAmount(e.target.value)} placeholder={cap != null ? cap.toFixed(2) : ''}
            style={{ width: 110, padding: '6px 9px', border: '1px solid #fecaca', borderRadius: 6, fontSize: 13 }} />
          <button onClick={() => onApprove(amount === '' ? null : asked)} disabled={busy}
            style={{ padding: '7px 14px', borderRadius: 6, fontSize: 13, fontWeight: 700, border: 'none', background: needsApproval ? '#b45309' : '#dc2626', color: '#fff', cursor: busy ? 'not-allowed' : 'pointer' }}>
            {busy ? t('sp_ctq_cancel_working', 'Processing…') : (needsApproval ? t('pr_submit_for_approval', 'Send for approval') : t('sp_ctq_cancel_confirm', 'Refund {cur} ${amt} & cancel', { cur, amt: amt2(asked ?? 0) }))}
          </button>
          {needsApproval && (
            <span style={{ flexBasis: '100%', fontSize: 12, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '6px 10px' }}>
              ⚠ {t('pr_needs_approval_warn', "Over your {limit} limit — this refund will need owner approval before it's issued.", { limit: money(refundLimit, cur) })}
            </span>
          )}
          <button onClick={() => { setConfirming(false); setAmount('') }} disabled={busy}
            style={{ padding: '7px 12px', borderRadius: 6, fontSize: 13, fontWeight: 600, border: '1px solid #fecaca', background: '#fff', color: '#991b1b', cursor: 'pointer' }}>
            {t('cancel', 'Cancel')}
          </button>
          {amount !== '' && cap != null && Number(amount) > cap && (
            <div style={{ flexBasis: '100%', fontSize: 12, color: '#b91c1c', marginTop: 2 }}>
              ⚠ {t('sp_ctq_cancel_over_cap', 'The most you can refund is {cur} ${cap} — the rest was non-refundable, so it will be capped.', { cur, cap: cap.toFixed(2) })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// Customer change-request thread: per-item change/removal requests + comments
// the customer submitted from their quote page, each resolvable, plus a reply
// box (replies show on the customer's quote page). Amber while anything is open.
function CustomerRequestsPanel({ messages, onResolve, onReply, t }) {
  const [reply, setReply] = useState('')
  const [emailCust, setEmailCust] = useState(true)
  const [busy, setBusy] = useState(false)
  const unresolved = messages.filter(m => m.isChangeRequest && !m.resolved).length
  const fmtWhen = (v) => { if (!v) return ''; try { return new Date(String(v).replace(' ', 'T')).toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) } catch { return '' } }
  async function send() {
    const body = reply.trim(); if (!body) return
    setBusy(true)
    try { await onReply(body, emailCust); setReply('') } catch (e) { alert(e?.response?.data?.message || e.message) } finally { setBusy(false) }
  }
  return (
    <div style={{ marginBottom: 16, border: `1px solid ${unresolved ? '#fde68a' : '#e2e8f0'}`, borderRadius: 12, overflow: 'hidden' }}>
      <div style={{ padding: '10px 14px', background: unresolved ? '#fffbeb' : '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 13.5, fontWeight: 700, color: unresolved ? '#92400e' : '#334155' }}>💬 {t('sp_ctq_cust_requests', 'Customer requests')}</span>
        {unresolved > 0 && (
          <span style={{ fontSize: 11.5, fontWeight: 700, color: '#92400e', background: '#fef3c7', padding: '2px 8px', borderRadius: 999 }}>
            {t('sp_ctq_unresolved', '{n} to address', { n: unresolved })}
          </span>
        )}
      </div>
      <div style={{ padding: 14, background: '#fff' }}>
        {messages.map(m => (
          <div key={m.id} style={{ padding: '10px 0', borderTop: '1px solid #f1f5f9' }}>
            {m.authorType === 'reseller' ? (
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', marginBottom: 2 }}>
                  {t('sp_ctq_you_replied', 'You replied')}{m.authorName ? ` · ${m.authorName}` : ''} <span style={{ color: '#94a3b8', fontWeight: 400 }}>{fmtWhen(m.createdAt)}</span>
                </div>
                <div style={{ fontSize: 13.5, color: '#334155', whiteSpace: 'pre-wrap' }}>{m.body}</div>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: '#fff', background: m.requestKind === 'remove' ? '#dc2626' : m.requestKind === 'comment' ? '#64748b' : '#d97706', padding: '3px 8px', borderRadius: 6, whiteSpace: 'nowrap', marginTop: 1 }}>
                  {m.requestKind === 'remove' ? t('sp_ctq_cr_remove', 'REMOVE') : m.requestKind === 'comment' ? t('sp_ctq_cr_note', 'NOTE') : t('sp_ctq_cr_change', 'CHANGE')}
                </span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: '#0f172a' }}>
                    {m.itemLabel || t('sp_ctq_general_comment', 'General comment')} <span style={{ color: '#94a3b8', fontWeight: 400, fontSize: 12 }}>{fmtWhen(m.createdAt)}</span>
                  </div>
                  {m.body && <div style={{ fontSize: 13, color: '#475569', marginTop: 1, whiteSpace: 'pre-wrap' }}>{m.body}</div>}
                </div>
                <button onClick={() => onResolve(m.id, !m.resolved)}
                  style={{ padding: '4px 10px', borderRadius: 999, fontSize: 11.5, fontWeight: 700, cursor: 'pointer', flexShrink: 0, border: `1px solid ${m.resolved ? '#bbf7d0' : '#fde68a'}`, background: m.resolved ? '#dcfce7' : '#fffbeb', color: m.resolved ? '#166534' : '#92400e' }}>
                  {m.resolved ? t('sp_ctq_resolved', '✓ Resolved') : t('sp_ctq_mark_resolved', 'Mark resolved')}
                </button>
              </div>
            )}
          </div>
        ))}
        <div style={{ marginTop: 12, borderTop: '1px solid #f1f5f9', paddingTop: 12 }}>
          <textarea value={reply} onChange={e => setReply(e.target.value)}
            placeholder={t('sp_ctq_reply_ph', 'Reply to the customer (they see this on their quote page)…')}
            style={{ width: '100%', padding: 10, border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13.5, fontFamily: 'inherit', resize: 'vertical', minHeight: 56, boxSizing: 'border-box' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, gap: 12, flexWrap: 'wrap' }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: '#475569', cursor: 'pointer' }}>
              <input type="checkbox" checked={emailCust} onChange={e => setEmailCust(e.target.checked)} />
              {t('sp_ctq_email_customer', 'Email the customer')}
            </label>
            <button onClick={send} disabled={busy || !reply.trim()}
              style={{ padding: '7px 16px', borderRadius: 8, fontSize: 13, fontWeight: 700, border: 'none', background: '#0f172a', color: '#fff', cursor: busy || !reply.trim() ? 'default' : 'pointer', opacity: busy || !reply.trim() ? 0.6 : 1 }}>
              {busy ? t('sp_ctq_sending', 'Sending…') : t('sp_ctq_send_reply', 'Send reply')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Summary shown after "Verify availability": green if everything held, amber if
// prices/tee times were refreshed, red if something is no longer available.
function VerifyBanner({ result, cur, onClose, t }) {
  const r = result.report || []
  const unavailable = r.filter(x => x.status === 'unavailable')
  const changed = r.filter(x => x.status === 'changed')
  const manual = r.filter(x => x.status === 'manual')
  const tone = unavailable.length ? { bg: '#fef2f2', bd: '#fecaca', fg: '#991b1b' }
    : changed.length ? { bg: '#fffbeb', bd: '#fde68a', fg: '#92400e' }
    : { bg: '#f0fdf4', bd: '#bbf7d0', fg: '#166534' }
  const headline = unavailable.length
    ? t('sp_ctq_verify_issues', '{n} item(s) are no longer available — please review before sending.', { n: unavailable.length })
    : changed.length ? t('sp_ctq_verify_changed', 'Still available — {n} price(s)/tee time(s) refreshed to current.', { n: changed.length })
      : t('sp_ctq_verify_ok', 'All good — everything is still available at the current prices.')
  return (
    <div style={{ marginBottom: 16, padding: '12px 14px', background: tone.bg, border: `1px solid ${tone.bd}`, borderRadius: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: tone.fg }}>{headline}</div>
        <button onClick={onClose} style={{ border: 'none', background: 'none', color: tone.fg, cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>&times;</button>
      </div>
      {(unavailable.length > 0 || changed.length > 0) && (
        <div style={{ marginTop: 8, display: 'grid', gap: 4 }}>
          {unavailable.map(x => (
            <div key={x.id} style={{ fontSize: 12.5, color: '#991b1b' }}>
              ✗ <b>{x.name || (x.type === 'hotel' ? t('sp_ctq_hotel', 'Hotel') : t('sp_ctq_round', 'Round'))}</b> — {t('sp_ctq_verify_gone', 'no longer available at these dates')}
            </div>
          ))}
          {changed.map(x => (
            <div key={x.id} style={{ fontSize: 12.5, color: '#92400e' }}>
              ⚠ <b>{x.name}</b>{x.teeTimeGone && x.oldTeeTime ? ` — ${t('sp_ctq_verify_tee', 'tee time {a} taken, moved to {b}', { a: fmtTee(x.oldTeeTime), b: fmtTee(x.newTeeTime) })}` : ''}
              {x.oldPrice != null && x.newPrice != null && x.oldPrice !== x.newPrice
                ? ` — ${t('sp_ctq_verify_price', 'price {a} → {b}', { a: money(x.oldPrice, cur), b: money(x.newPrice, cur) })}`
                : ''}
            </div>
          ))}
        </div>
      )}
      {manual.length > 0 && (
        <div style={{ marginTop: 6, fontSize: 11.5, color: '#64748b' }}>
          {t('sp_ctq_verify_manual', '{n} manual item(s) (typed price or custom line) were not checked.', { n: manual.length })}
        </div>
      )}
    </div>
  )
}

function StatusBadge({ status }) {
  const map = {
    requested: ['#ede9fe', '#6d28d9', 'New request'],
    in_progress: ['#f1f5f9', '#475569', 'In progress'], sent: ['#dbeafe', '#1e40af', 'Sent'],
    change_requested: ['#fef3c7', '#92400e', 'Change requested'], accepted_partial: ['#dcfce7', '#166534', 'Accepted (partial)'],
    accepted_paid: ['#dcfce7', '#166534', 'Accepted (paid)'], expired: ['#f1f5f9', '#94a3b8', 'Expired'], declined_cancelled: ['#fee2e2', '#991b1b', 'Cancelled'],
  }
  const [bg, fg, label] = map[status] || ['#f1f5f9', '#475569', status]
  return <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 999, background: bg, color: fg }}>{label}</span>
}

// Small line icons for the search-bar cells (inherit the label's color).
const _svg = { width: 15, height: 15, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' }
const PinIcon = () => <svg {..._svg}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>
const CalIcon = () => <svg {..._svg}><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>
const PersonIcon = () => <svg {..._svg}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>

function SetupForm({ quote, onSave, t }) {
  const startInit = quote.tripStartDate ? String(quote.tripStartDate).slice(0, 10) : ''
  const [f, setF] = useState({
    tripStartDate: startInit,
    checkOutDate: (startInit && quote.numDays) ? addDays(startInit, quote.numDays) : '',
    numGolfers: quote.numGolfers || 4,
    budgetPerGolfer: quote.budgetPerGolfer || '',
    centerLat: quote.centerLat ?? null, centerLng: quote.centerLng ?? null,
    centerLabel: quote.centerLabel || '', centerCountryCode: quote.centerCountryCode || null,
    radiusKm: quote.radiusKm || 25,
  })
  const [attempted, setAttempted] = useState(false)
  const nights = daysBetween(f.tripStartDate, f.checkOutDate)
  const datesOk = f.tripStartDate && f.checkOutDate && nights >= 1 && nights <= 60
  const golfersOk = !!f.numGolfers
  const canBuild = datesOk && golfersOk
  // Only flag a field once the person has tried to build — no red borders on first paint.
  const checkinMissing = attempted && !f.tripStartDate
  const checkoutMissing = attempted && !f.checkOutDate
  const golfersMissing = attempted && !golfersOk
  function handleBuild() {
    if (!canBuild) { setAttempted(true); return }
    onSave({ tripStartDate: f.tripStartDate, numDays: nights, numGolfers: Number(f.numGolfers), budgetPerGolfer: f.budgetPerGolfer === '' ? null : Number(f.budgetPerGolfer), centerLat: f.centerLat, centerLng: f.centerLng, centerLabel: f.centerLabel || null, centerCountryCode: f.centerCountryCode || null, radiusKm: Number(f.radiusKm) })
  }
  // Search-bar cell styles (Expedia-style pills inside one bar).
  const cell = (bad, grow) => ({
    display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 3,
    flex: grow, minWidth: 0, padding: '10px 14px', borderRadius: 12, background: '#fff',
    border: `1px solid ${bad ? '#ef4444' : '#e2e8f0'}`,
    boxShadow: bad ? '0 0 0 3px rgba(239,68,68,0.10)' : 'none', boxSizing: 'border-box',
  })
  const cellLabel = { display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, fontWeight: 600, color: '#64748b' }
  const cellInput = { border: 'none', outline: 'none', background: 'transparent', fontSize: 14.5, color: '#0f172a', width: '100%', padding: '1px 0', boxSizing: 'border-box' }
  return (
    <div style={{ maxWidth: 980 }}>
      <h3 style={{ margin: '0 0 4px', fontSize: 18, color: '#0f172a' }}>{t('sp_ctq_setup_title', 'Start the trip')}</h3>
      <p style={{ margin: '0 0 14px', fontSize: 13, color: '#64748b' }}>{t('sp_ctq_setup_sub', 'A few basics and we build the day grid. You can change these later.')}</p>

      {/* One search bar: Destination · Check-in · Check-out · Golfers · Build */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'stretch', gap: 6, padding: 6, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, boxShadow: '0 6px 24px rgba(15,23,42,0.07)' }}>
        <div style={cell(false, '3 1 240px')}>
          <div style={cellLabel}><PinIcon /> {t('sp_ctq_location', 'Destination')}</div>
          <LocationPicker label={f.centerLabel} onPick={p => setF({ ...f, centerLat: p.lat, centerLng: p.lng, centerLabel: p.label, centerCountryCode: p.country || null })} t={t} inputStyle={cellInput} />
        </div>
        <div style={cell(checkinMissing, '1 1 140px')}>
          <div style={cellLabel}><CalIcon /> {t('sp_ctq_checkin_date', 'Check-in')} <span style={{ color: '#ef4444' }}>*</span></div>
          <input type="date" value={f.tripStartDate} onChange={e => setF({ ...f, tripStartDate: e.target.value })} style={cellInput} />
        </div>
        <div style={cell(checkoutMissing, '1 1 140px')}>
          <div style={cellLabel}><CalIcon /> {t('sp_ctq_checkout_date', 'Check-out')} <span style={{ color: '#ef4444' }}>*</span></div>
          <input type="date" value={f.checkOutDate} min={f.tripStartDate || undefined} onChange={e => setF({ ...f, checkOutDate: e.target.value })} style={cellInput} />
        </div>
        <div style={cell(golfersMissing, '0 1 118px')}>
          <div style={cellLabel}><PersonIcon /> {t('sp_ctq_golfers', 'Golfers')} <span style={{ color: '#ef4444' }}>*</span></div>
          <input type="number" min="1" max="64" value={f.numGolfers} onChange={e => setF({ ...f, numGolfers: e.target.value })} style={cellInput} />
        </div>
        <button
          onClick={handleBuild}
          aria-disabled={!canBuild}
          style={{ flex: '1 1 150px', minHeight: 62, padding: '0 26px', borderRadius: 12, fontWeight: 700, fontSize: 15, border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', opacity: canBuild ? 1 : 0.55 }}>
          {t('sp_ctq_build', 'Build the grid')}
        </button>
      </div>

      {/* Validation / nights readout */}
      <div style={{ minHeight: 18, marginTop: 6 }}>
        {(checkinMissing || checkoutMissing)
          ? <span style={{ fontSize: 12, color: '#b91c1c', fontWeight: 600 }}>{t('sp_ctq_dates_required', 'Check-in and check-out dates are required.')}</span>
          : golfersMissing
            ? <span style={{ fontSize: 12, color: '#b91c1c', fontWeight: 600 }}>{t('sp_ctq_golfers_required', 'Enter the number of golfers.')}</span>
            : f.tripStartDate && f.checkOutDate && (nights < 1
              ? <span style={{ fontSize: 12, color: '#b91c1c' }}>{t('sp_ctq_checkout_after', 'Check-out must be after check-in.')}</span>
              : nights > 60
                ? <span style={{ fontSize: 12, color: '#b91c1c' }}>{t('sp_ctq_max_nights', 'Trips are limited to 60 nights.')}</span>
                : <span style={{ fontSize: 12, color: '#64748b' }}>{nights} {nights === 1 ? t('sp_ctq_night', 'night') : t('sp_ctq_nights', 'nights')}</span>)}
      </div>

      {/* Secondary controls: search radius + budget (kept) */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, marginTop: 8, alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: '1 1 320px', minWidth: 240 }}>
          <span style={{ fontSize: 12.5, color: '#64748b', whiteSpace: 'nowrap' }}>{t('sp_ctq_radius', 'Search radius')}</span>
          <input type="range" min="10" max="200" step="5" value={f.radiusKm} onChange={e => setF({ ...f, radiusKm: Number(e.target.value) })} style={{ flex: 1 }} />
          <span style={{ fontSize: 12.5, fontWeight: 600, width: 54, textAlign: 'right' }}>{f.radiusKm} km</span>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: '#334155' }}>
          {t('sp_ctq_budget', 'Budget per golfer (optional)')}
          <input type="number" min="0" value={f.budgetPerGolfer} onChange={e => setF({ ...f, budgetPerGolfer: e.target.value })} placeholder="2500"
            style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14, width: 120 }} />
        </label>
      </div>
    </div>
  )
}

// Type-ahead destination picker backed by the shared /lookup/autocomplete
// geocoder (same one the grouping map uses). Calls onPick({lat,lng,label}).
function LocationPicker({ label, onPick, t, inputStyle }) {
  const [q, setQ] = useState(label || '')
  const [sugg, setSugg] = useState([])
  const [open, setOpen] = useState(false)
  useEffect(() => {
    if (!q || q.trim().length < 3 || q === label) { setSugg([]); return }
    const tmr = setTimeout(async () => {
      try {
        const { data } = await portalApi.get('/lookup/autocomplete', { params: { q: q.trim(), countryCodes: 'ca,us', limit: 6 } })
        setSugg(Array.isArray(data) ? data : []); setOpen(true)
      } catch { setSugg([]) }
    }, 300)
    return () => clearTimeout(tmr)
  }, [q, label])
  return (
    <div style={{ position: 'relative' }}>
      <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('sp_ctq_location_ph', 'City or region, e.g. Ben Eoin, NS')}
        style={inputStyle || { padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14, width: '100%' }} />
      {open && sugg.length > 0 && (
        <div style={{ position: 'absolute', zIndex: 20, top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, marginTop: 4, boxShadow: '0 6px 20px rgba(0,0,0,0.08)', maxHeight: 220, overflowY: 'auto' }}>
          {sugg.map((s, i) => (
            <div key={i} onClick={() => { onPick({ lat: s.latitude, lng: s.longitude, label: s.displayName, country: s.address?.countryCode || null }); setQ(s.displayName); setOpen(false); setSugg([]) }}
              style={{ padding: '8px 10px', fontSize: 12.5, color: '#334155', cursor: 'pointer', borderTop: i ? '1px solid #f1f5f9' : 'none' }}
              onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
              {s.displayName}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// Compact location line in the builder header with an inline editor.
function HeaderLocation({ quote, onSave, t }) {
  const [editing, setEditing] = useState(false)
  const [radiusKm, setRadiusKm] = useState(quote.radiusKm || 25)
  // Display-only override for the destination NAME the customer sees (the
  // geocoder sometimes labels a town as its county/region). Search is unchanged.
  const [displayName, setDisplayName] = useState(quote.destinationLabelOverride || '')
  useEffect(() => { setDisplayName(quote.destinationLabelOverride || '') }, [quote.destinationLabelOverride])
  // What the customer actually sees = override, else the geocoded label.
  const shown = quote.destinationLabelOverride || quote.centerLabel
  const commitDisplayName = () => {
    const v = displayName.trim()
    if (v !== (quote.destinationLabelOverride || '')) onSave({ destinationLabelOverride: v || null })
  }
  if (!editing) {
    return (
      <div style={{ fontSize: 12.5, color: '#475569', marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
        <span>📍 {shown ? `${shown.split(',').slice(0, 2).join(',')} · ${quote.radiusKm || 25} km` : t('sp_ctq_no_location', 'No destination set')}</span>
        <button onClick={() => setEditing(true)} style={{ border: 'none', background: 'none', color: '#2563eb', cursor: 'pointer', fontSize: 12.5 }}>{quote.centerLabel ? t('sp_ctq_change', 'change') : t('sp_ctq_set', 'set')}</button>
      </div>
    )
  }
  return (
    <div style={{ marginTop: 8, padding: 10, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, maxWidth: 420 }}>
      <LocationPicker label={quote.centerLabel} t={t}
        onPick={p => { onSave({ centerLat: p.lat, centerLng: p.lng, centerLabel: p.label, centerCountryCode: p.country || null, radiusKm }); }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
        <span style={{ fontSize: 12, color: '#64748b' }}>{t('sp_ctq_radius', 'Search radius')}</span>
        <input type="range" min="10" max="200" step="5" value={radiusKm} onChange={e => setRadiusKm(Number(e.target.value))} onMouseUp={() => onSave({ radiusKm })} style={{ flex: 1 }} />
        <span style={{ fontSize: 12.5, fontWeight: 600, width: 54, textAlign: 'right' }}>{radiusKm} km</span>
        <button onClick={() => { commitDisplayName(); setEditing(false) }} style={{ fontSize: 12, border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff', padding: '4px 8px', cursor: 'pointer' }}>{t('sp_ctq_done', 'Done')}</button>
      </div>
      {quote.centerLabel && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>{t('sp_ctq_display_name', 'Display name (optional)')}</div>
          <input
            value={displayName}
            onChange={e => setDisplayName(e.target.value)}
            onBlur={commitDisplayName}
            placeholder={quote.centerLabel.split(',').slice(0, 2).join(',')}
            maxLength={255}
            style={{ width: '100%', boxSizing: 'border-box', fontSize: 13, padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 6 }}
          />
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4, lineHeight: 1.5 }}>
            {t('sp_ctq_display_name_hint', 'What the customer sees on the quote. Leave blank to use the detected location. This does not change where we search.')}
          </div>
        </div>
      )}
    </div>
  )
}

// Prominent, unmissable destination setter shown in the builder body whenever a
// trip has no center set yet. Without a destination, +Hotel / +Round can't
// search (they 422 "Set a destination first"), so the tiny header "set" link
// was too easy to overlook — this puts a working geocode picker front-and-centre.
function DestinationBanner({ quote, onSave, t }) {
  const [radiusKm, setRadiusKm] = useState(quote.radiusKm || 25)
  return (
    <div style={{ marginBottom: 16, background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 12, padding: 16 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: '#92400e' }}>📍 {t('sp_ctq_set_dest_title', 'Set your trip destination')}</div>
      <div style={{ fontSize: 12.5, color: '#b45309', marginTop: 4, marginBottom: 10 }}>
        {t('sp_ctq_set_dest_hint', 'Choose the city or region for this trip so we can find hotels and courses nearby. You can’t add a hotel or round until a destination is set.')}
      </div>
      <div style={{ maxWidth: 440 }}>
        <LocationPicker label={quote.centerLabel} t={t}
          onPick={p => onSave({ centerLat: p.lat, centerLng: p.lng, centerLabel: p.label, centerCountryCode: p.country || null, radiusKm })} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
          <span style={{ fontSize: 12, color: '#92400e' }}>{t('sp_ctq_radius', 'Search radius')}</span>
          <input type="range" min="10" max="200" step="5" value={radiusKm} onChange={e => setRadiusKm(Number(e.target.value))} style={{ flex: 1 }} />
          <span style={{ fontSize: 12.5, fontWeight: 600, width: 54, textAlign: 'right', color: '#92400e' }}>{radiusKm} km</span>
        </div>
      </div>
    </div>
  )
}

// Master-contact capture in the builder header: who the quote is for + how many
// golfers. Not required to build, but required before the quote can be sent.
// Each field saves on blur (only if it changed). Number of golfers re-prices.
function HeaderContact({ quote, onSave, onReschedule, t }) {
  const inp = { padding: '6px 9px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, background: '#fff', width: '100%', boxSizing: 'border-box' }
  const saveIf = (key) => (e) => { const v = e.target.value; if ((v || '') !== (quote[key] || '')) onSave({ [key]: v }) }
  const checkin = toYmd(quote.tripStartDate)
  const checkout = addDays(checkin, quote.numDays)
  return (
    <div style={{ marginTop: 12, padding: 12, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, maxWidth: 560 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: 0.4, marginBottom: 8 }}>{t('sp_ctq_customer', 'CUSTOMER')}</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <input defaultValue={quote.masterFirstName || ''} placeholder={t('sp_ctq_first_name', 'First name')} onBlur={saveIf('masterFirstName')} style={inp} />
        <input defaultValue={quote.masterLastName || ''} placeholder={t('sp_ctq_last_name', 'Last name')} onBlur={saveIf('masterLastName')} style={inp} />
        <input type="email" defaultValue={quote.masterEmail || ''} placeholder={t('sp_ctq_email', 'Email')} onBlur={saveIf('masterEmail')} style={inp} />
        <input defaultValue={displayPhone(quote.masterPhone || '')} placeholder={t('sp_ctq_phone', 'Phone')}
          onBlur={e => { const f = formatPhone(e.target.value); e.target.value = f; if ((f || '') !== (quote.masterPhone || '')) onSave({ masterPhone: f }) }}
          style={inp} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginTop: 10, flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12.5, color: '#475569' }}>{t('sp_ctq_num_golfers', 'Number of golfers')}</span>
          <input type="number" min="1" max="64" defaultValue={quote.numGolfers || 1}
            onBlur={e => { const n = Math.max(1, Math.min(64, Number(e.target.value) || 1)); if (n !== Number(quote.numGolfers)) onSave({ numGolfers: n }) }}
            style={{ ...inp, width: 72 }} />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12.5, color: '#475569' }}>{t('sp_ctq_budget_pg', 'Budget / golfer')}</span>
          <input type="number" min="0" defaultValue={quote.budgetPerGolfer ?? ''} placeholder={t('sp_ctq_no_budget', 'none')}
            onBlur={e => { const raw = e.target.value; const v = raw === '' ? null : Math.max(0, Number(raw) || 0); if (v !== (quote.budgetPerGolfer ?? null)) onSave({ budgetPerGolfer: v }) }}
            style={{ ...inp, width: 96 }} />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12.5, color: '#475569' }}>{t('sp_ctq_checkin', 'Check-in')}</span>
          <input type="date" value={checkin || ''} onChange={e => { if (e.target.value) onReschedule({ tripStartDate: e.target.value }) }} style={{ ...inp, width: 150 }} />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12.5, color: '#475569' }}>{t('sp_ctq_checkout', 'Check-out')}</span>
          <input type="date" value={checkout || ''} min={addDays(checkin, 1) || undefined}
            onChange={e => { if (!e.target.value) return; const nd = daysBetween(checkin, e.target.value); if (nd >= 1) onReschedule({ numDays: nd }); else alert(t('sp_ctq_checkout_after', 'Check-out must be after check-in.')) }}
            style={{ ...inp, width: 150 }} />
        </label>
      </div>
    </div>
  )
}

function ItemCard({ item, cur, accent, children, t, onDragStart, onEdit, onRemove }) {
  const tr = t || ((k, f) => f)
  return (
    <div draggable onDragStart={onDragStart}
      style={{ position: 'relative', background: '#fff', border: '1px solid #e2e8f0', borderLeft: `3px solid ${accent}`, borderRadius: 8, padding: '6px 8px', marginBottom: 6, fontSize: 11.5, color: '#334155', cursor: 'grab' }}>
      {children}
      <div style={{ marginTop: 3, fontWeight: 700, color: accent }}>{moneyc(item.TotalPrice, cur)}</div>
      <div style={{ position: 'absolute', top: 2, right: 4, display: 'flex', gap: 2 }}>
        {onEdit && <button onClick={onEdit} title={tr('sp_ctq_edit', 'Edit')} aria-label={tr('sp_ctq_edit', 'Edit')}
          style={{ border: 'none', background: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 12, lineHeight: 1, padding: 0 }}>&#9998;</button>}
        <button onClick={onRemove} title={tr('sp_ctq_remove', 'Remove')} aria-label={tr('sp_ctq_remove', 'Remove')}
          style={{ border: 'none', background: 'none', color: '#cbd5e1', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0 }}>&times;</button>
      </div>
    </div>
  )
}

function MiniAdd({ children, onClick, style }) {
  return <button onClick={onClick} style={{ flex: 1, padding: '5px 6px', fontSize: 11.5, fontWeight: 600, border: '1px dashed #cbd5e1', borderRadius: 6, background: '#fff', color: '#475569', cursor: 'pointer', ...style }}>{children}</button>
}

function AddItemForm({ type, numGolfers, onAdd, onCancel, t }) {
  const [f, setF] = useState(type === 'hotel'
    ? { hotelName: '', nights: 1, rooms: Math.max(1, Math.ceil((numGolfers || 2) / 2)), totalPrice: '' }
    : { courseName: '', teeTime: '', golfersOnRound: numGolfers || '', totalPrice: '' })
  const inp = { padding: '5px 7px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12, width: '100%', marginTop: 4 }
  return (
    <div style={{ marginTop: 8, padding: 8, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }}>
      {type === 'hotel' ? (
        <>
          <input placeholder={t('sp_ctq_hotel_name', 'Hotel name')} value={f.hotelName} onChange={e => setF({ ...f, hotelName: e.target.value })} style={inp} />
          <div style={{ display: 'flex', gap: 6 }}>
            <input type="number" min="1" placeholder={t('sp_ctq_nights', 'nights')} value={f.nights} onChange={e => setF({ ...f, nights: e.target.value })} style={inp} />
            <input type="number" min="1" placeholder={t('sp_ctq_rooms', 'rm')} value={f.rooms} onChange={e => setF({ ...f, rooms: e.target.value })} style={inp} />
          </div>
        </>
      ) : (
        <>
          <input placeholder={t('sp_ctq_course', 'Course')} value={f.courseName} onChange={e => setF({ ...f, courseName: e.target.value })} style={inp} />
          <div style={{ display: 'flex', gap: 6 }}>
            <input placeholder={t('sp_ctq_tee', 'tee time')} value={f.teeTime} onChange={e => setF({ ...f, teeTime: e.target.value })} style={inp} />
            <input type="number" min="1" placeholder={t('sp_ctq_golfers_word', 'golfers')} value={f.golfersOnRound} onChange={e => setF({ ...f, golfersOnRound: e.target.value })} style={inp} />
          </div>
        </>
      )}
      <input type="number" min="0" placeholder={t('sp_ctq_total_price', 'total price')} value={f.totalPrice} onChange={e => setF({ ...f, totalPrice: e.target.value })} style={inp} />
      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
        <button onClick={() => onAdd(f)} disabled={!f.totalPrice} style={{ flex: 1, padding: '5px', fontSize: 12, fontWeight: 600, border: 'none', borderRadius: 6, background: '#0f172a', color: '#fff', cursor: 'pointer' }}>{t('sp_ctq_add', 'Add')}</button>
        <button onClick={onCancel} style={{ padding: '5px 8px', fontSize: 12, border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff', cursor: 'pointer' }}>{t('sp_ctq_cancel', 'Cancel')}</button>
      </div>
    </div>
  )
}

// Net-rate (concierge) hotel entered by hand inside the Add-a-hotel modal — for
// a property with no supplier API (Cabot). Net is what we pay the property; sell
// is the customer total. Sell below net needs a staff override (mirrors the
// backend netRateLineError). Posts hotelSource:'net_rate' so booking parks it
// awaiting_confirmation instead of calling a supplier.
// Shown sell / room / night for a catalog rate: explicit sell wins, else
// net * (1 + markup/100). Mirrors sellPerNight on the catalog page so the
// builder preview and the catalog list agree. Returns null when neither a sell
// nor a usable markup is present (backend requires one, so that is defensive).
function nrSellPerNight(net, sell, markup) {
  if (sell != null && sell !== '') { const v = Number(sell); return Number.isFinite(v) ? v : null }
  const n = Number(net), m = Number(markup)
  if (Number.isFinite(n) && markup != null && markup !== '' && Number.isFinite(m)) return n * (1 + m / 100)
  return null
}

// `initial` (optional) seeds the form from a catalog pick (computed party net +
// sell totals, suggested rooms, refundable). Everything stays editable, so the
// sell-below-net override guard still applies after a prefill.
function NetRateHotelForm({ cur, dayIndex, nights, rooms, stopLabel, initial, onAdd, t }) {
  const [name, setName] = useState(initial?.name || '')
  const [nt, setNt] = useState(initial?.nights || nights || 1)
  const [rm, setRm] = useState(initial?.rooms || rooms || 1)
  const [net, setNet] = useState(initial?.net != null ? String(initial.net) : '')
  const [sell, setSell] = useState(initial?.sell != null ? String(initial.sell) : '')
  const [refundable, setRefundable] = useState(initial?.refundable ?? false)
  const [override, setOverride] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  // A CATALOG prefill carries the per-room-per-night net + sell, so a change to
  // nights or rooms recomputes BOTH party totals from those figures (rounded to
  // cents) — the settled formula is per-room-per-night x submitted rooms x
  // nights. A direct edit of net or sell stands until nights/rooms moves again,
  // when the totals come from the catalog per-room price once more. null for a
  // blank manual entry, where nights/rooms never recompute the typed totals.
  const perRoom = initial && initial.perRoomNet != null
    ? { net: Number(initial.perRoomNet), sell: initial.perRoomSell != null ? Number(initial.perRoomSell) : null }
    : null
  const round2 = (x) => Math.round(x * 100) / 100
  function recomputeTotals(nextNights, nextRooms) {
    if (!perRoom) return
    const n = Math.max(1, Math.trunc(Number(nextNights)) || 1)
    const r = Math.max(1, Math.trunc(Number(nextRooms)) || 1)
    if (Number.isFinite(perRoom.net)) setNet(String(round2(perRoom.net * r * n)))
    setSell(perRoom.sell != null && Number.isFinite(perRoom.sell) ? String(round2(perRoom.sell * r * n)) : '')
  }
  const netN = Number(net), sellN = Number(sell)
  const netOk = net !== '' && Number.isFinite(netN) && netN >= 0
  const sellOk = sell !== '' && Number.isFinite(sellN) && sellN >= 0
  const below = netOk && sellOk && sellN < netN
  const inp = { padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, width: '100%', marginTop: 4 }
  async function submit() {
    if (!name.trim()) { setErr(t('sp_ctq_nr_need_name', 'Enter the property name.')); return }
    if (!netOk) { setErr(t('sp_ctq_nr_need_net', 'Enter the net cost we pay the property.')); return }
    if (!sellOk) { setErr(t('sp_ctq_nr_need_sell', 'Enter the customer price.')); return }
    if (below && !override) { setErr(t('sp_ctq_nr_need_override', 'Customer price is below our net — tick the override to proceed.')); return }
    setBusy(true); setErr(null)
    try {
      await onAdd({
        dayIndex, stopLabel,
        hotelSource: 'net_rate', hotelName: name.trim(),
        nights: Math.max(1, Math.trunc(Number(nt)) || 1),
        rooms: Math.max(1, Math.trunc(Number(rm)) || 1),
        hotelNetCost: netN, totalPrice: sellN,
        refundable, netCostOverride: below ? override : false,
      })
    } catch (e) { setErr(e?.response?.data?.message || e.message); setBusy(false) }
  }
  return (
    <div style={{ padding: 12, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
      <div style={{ fontSize: 13.5, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>{t('sp_ctq_nr_heading', 'Add a net-rate property')}</div>
      <input placeholder={t('sp_ctq_nr_name_ph', 'Property name (e.g. Cabot Cape Breton)')} value={name} onChange={e => setName(e.target.value)} style={inp} />
      <div style={{ display: 'flex', gap: 8 }}>
        <label style={{ flex: 1, fontSize: 12, color: '#475569' }}>{t('sp_ctq_nights', 'nights')}
          <input type="number" min="1" value={nt} onChange={e => { setNt(e.target.value); recomputeTotals(e.target.value, rm) }} style={inp} /></label>
        <label style={{ flex: 1, fontSize: 12, color: '#475569' }}>{t('sp_ctq_rooms', 'rooms')}
          <input type="number" min="1" value={rm} onChange={e => { setRm(e.target.value); recomputeTotals(nt, e.target.value) }} style={inp} /></label>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <label style={{ flex: 1, fontSize: 12, color: '#475569' }}>{t('sp_ctq_nr_net', 'Net cost (we pay the property)')}
          <input type="number" min="0" step="0.01" value={net} onChange={e => setNet(e.target.value)} style={inp} /></label>
        <label style={{ flex: 1, fontSize: 12, color: '#475569' }}>{t('sp_ctq_nr_sell', 'Customer price')}
          <input type="number" min="0" step="0.01" value={sell} onChange={e => setSell(e.target.value)} style={inp} /></label>
      </div>
      <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12.5, color: '#475569', marginTop: 8 }}>
        <input type="checkbox" checked={refundable} onChange={e => setRefundable(e.target.checked)} /> {t('sp_ctq_nr_refundable', 'Refundable')}
      </label>
      {below && (
        <div style={{ marginTop: 8, padding: 8, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6 }}>
          <div style={{ fontSize: 12, color: '#92400e', marginBottom: 6 }}>{t('sp_ctq_nr_below_warn', 'Customer price {sell} is below our net {net}. This loses margin.', { sell: money(sellN, cur), net: money(netN, cur) })}</div>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12.5, color: '#92400e', fontWeight: 600 }}>
            <input type="checkbox" checked={override} onChange={e => setOverride(e.target.checked)} /> {t('sp_ctq_nr_override', 'Record override — sell below net')}
          </label>
        </div>
      )}
      {err && <div style={{ marginTop: 8, fontSize: 12, color: '#991b1b' }}>{err}</div>}
      <button onClick={submit} disabled={busy} style={{ marginTop: 10, width: '100%', padding: '8px', fontSize: 13, fontWeight: 700, border: 'none', borderRadius: 6, background: '#0f172a', color: '#fff', cursor: busy ? 'default' : 'pointer' }}>
        {busy ? t('sp_ctq_adding', 'Adding…') : t('sp_ctq_nr_add', 'Add net-rate hotel')}
      </button>
    </div>
  )
}

// Pick a SAVED net-rate catalog rate (the reseller's own rates + shared house
// rates) and PREFILL the net-rate form with the computed stay line, rather than
// re-typing net/sell by hand. Money math: HotelNetCost = net/room/night x rooms
// x nights; TotalPrice = sell/room/night x rooms x nights (sell = explicit sell
// else net*(1+markup/100)). Occupancy suggests a room count (ceil(golfers/occ)).
// Rates outside their date window, or in a different currency than the quote,
// are flagged but still pickable — the reseller verifies in the prefilled form
// (which keeps the sell>=net override guard), so nothing books silently.
function NetRateCatalogPick({ cur, nights, rooms, golfers, checkin, checkout, onPrefill, t }) {
  const [list, setList] = useState(null)   // property list; null = still loading
  const [err, setErr] = useState(null)
  const [sel, setSel] = useState(null)     // { property, rates, myAccountId } for the open property
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    let alive = true
    tournamentApi.get('/portal/reseller/net-rate-properties')
      .then(r => { if (alive) setList(r.data?.properties || []) })
      .catch(e => { if (alive) setErr(e?.response?.data?.message || e.message) })
    return () => { alive = false }
  }, [])
  function open(id) {
    setBusy(true); setErr(null)
    tournamentApi.get(`/portal/reseller/net-rate-properties/${id}`)
      .then(r => setSel(r.data))
      .catch(e => setErr(e?.response?.data?.message || e.message))
      .finally(() => setBusy(false))
  }
  const box = { padding: 12, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }
  const head = { fontSize: 13.5, fontWeight: 700, color: '#0f172a', marginBottom: 8 }
  if (err) return <div style={box}><div style={{ color: '#991b1b', fontSize: 13 }}>{err}</div></div>
  if (list == null) return <div style={box}><div style={{ color: '#94a3b8', fontSize: 13 }}>{t('sp_ctq_nrc_loading', 'Loading your catalog...')}</div></div>

  if (!sel) {
    return (
      <div style={box}>
        <div style={head}>{t('sp_ctq_nrc_heading', 'Pick a saved net-rate property')}</div>
        {list.length === 0 ? (
          <div style={{ color: '#94a3b8', fontSize: 13 }}>
            {t('sp_ctq_nrc_empty', 'No net-rate properties in your catalog yet. Add one from Net-rate properties.')}
            {' '}
            <a href="/portal/reseller/net-rate-properties" target="_blank" rel="noopener noreferrer"
              style={{ color: '#6d28d9', fontWeight: 600, textDecoration: 'underline' }}>
              {t('sp_ctq_nrc_open_catalog', 'Open Net-rate properties')} →
            </a>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 6, maxHeight: 320, overflowY: 'auto' }}>
            {list.map(p => (
              <button key={p.ID_NO} onClick={() => open(p.ID_NO)} disabled={busy}
                style={{ textAlign: 'left', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', cursor: busy ? 'default' : 'pointer' }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#0f172a' }}>{p.Name}</div>
                {p.AddressLine && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{p.AddressLine}</div>}
              </button>
            ))}
          </div>
        )}
      </div>
    )
  }

  // A property is open — show its rates (mine + house) with the computed stay line.
  const n = Math.max(1, Number(nights) || 1)
  const g = Math.max(1, Number(golfers) || 1)
  const myId = sel.myAccountId
  const rateRow = (r) => {
    const net = Number(r.NetPerRoomPerNight)
    const sellN = nrSellPerNight(r.NetPerRoomPerNight, r.SellPerRoomPerNight, r.MarkupPct)
    const occ = r.Occupancy != null ? Number(r.Occupancy) : null
    const rm = (occ && occ >= 1) ? Math.max(1, Math.ceil(g / occ)) : Math.max(1, Number(rooms) || 1)
    const netTotal = Number.isFinite(net) ? Math.round(net * rm * n * 100) / 100 : null
    const sellTotal = (sellN != null) ? Math.round(sellN * rm * n * 100) / 100 : null
    const from = r.ValidFrom ? String(r.ValidFrom).slice(0, 10) : null
    const to = r.ValidTo ? String(r.ValidTo).slice(0, 10) : null
    const outWindow = (from && checkin < from) || (to && checkout > to)
    const ccyWarn = !!(r.Currency && cur && String(r.Currency).toUpperCase() !== String(cur).toUpperCase())
    const canUse = netTotal != null && sellTotal != null
    return (
      <div key={r.ID_NO} style={{ padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 13, color: '#0f172a', fontWeight: 600 }}>
              {r.RoomType || '—'}
              {occ ? ` · ${t('sp_ctq_nrc_sleeps', 'sleeps {n}', { n: occ })}` : ''}
              {!r.Refundable ? ` · ${t('sp_nrc_nonref', 'Non-refundable')}` : ''}
            </div>
            <div style={{ fontSize: 12, color: '#475569', marginTop: 2 }}>
              {t('sp_ctq_nrc_stay', 'Stay: {net} net / {sell} sell / {rooms} rm x {nights} nt', {
                net: money(netTotal, r.Currency || cur), sell: money(sellTotal, r.Currency || cur), rooms: rm, nights: n })}
            </div>
            {outWindow && <div style={{ fontSize: 11.5, color: '#92400e', marginTop: 3 }}>{t('sp_ctq_nrc_out_window', "Outside this rate's window ({from} - {to}) - check before adding.", { from: from || '?', to: to || '?' })}</div>}
            {ccyWarn && <div style={{ fontSize: 11.5, color: '#92400e', marginTop: 3 }}>{t('sp_ctq_nrc_ccy_warn', 'Rate is in {rateCur}, the quote is in {quoteCur}. Amounts are not converted - verify.', { rateCur: r.Currency, quoteCur: cur })}</div>}
          </div>
          <button disabled={!canUse}
            onClick={() => onPrefill({ name: sel.property.Name, nights: n, rooms: rm, net: netTotal, sell: sellTotal, refundable: !!r.Refundable, rateId: r.ID_NO, perRoomNet: net, perRoomSell: sellN })}
            style={{ flexShrink: 0, padding: '6px 10px', fontSize: 12.5, fontWeight: 700, border: 'none', borderRadius: 6, background: canUse ? '#0f172a' : '#cbd5e1', color: '#fff', cursor: canUse ? 'pointer' : 'default' }}>
            {t('sp_ctq_nrc_use_rate', 'Use this rate')}
          </button>
        </div>
      </div>
    )
  }
  const mine = (sel.rates || []).filter(r => r.ResellerAccount_ID != null && Number(r.ResellerAccount_ID) === Number(myId) && r.IsActive)
  const house = (sel.rates || []).filter(r => r.ResellerAccount_ID == null && r.IsActive)
  return (
    <div style={box}>
      <button onClick={() => setSel(null)} style={{ background: 'none', border: 'none', color: '#6d28d9', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', padding: 0, marginBottom: 8 }}>{t('sp_ctq_nrc_back_props', '< All properties')}</button>
      <div style={head}>{sel.property.Name}</div>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', margin: '6px 0 4px' }}>{t('sp_ctq_nrc_my_rates', 'Your rates')}</div>
      {mine.length ? <div style={{ display: 'grid', gap: 6 }}>{mine.map(rateRow)}</div>
        : (
          <div>
            <div style={{ fontSize: 12.5, color: '#94a3b8' }}>{t('sp_ctq_nrc_no_rates', 'No rates saved for this property.')}</div>
            {/* Dead-end made actionable: a one-off rate for THIS quote (prefills
                the manual form with the property name), or save a reusable rate
                in the Net-rate properties catalog (new tab — keeps this quote). */}
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 6 }}>
              <button onClick={() => onPrefill({ name: sel.property.Name })}
                style={{ background: 'none', border: 'none', color: '#6d28d9', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>
                {t('sp_ctq_nrc_add_oneoff', 'Enter a one-off rate for this quote')}
              </button>
              <a href="/portal/reseller/net-rate-properties" target="_blank" rel="noopener noreferrer"
                style={{ color: '#6d28d9', fontSize: 12.5, fontWeight: 600, textDecoration: 'underline' }}>
                {t('sp_ctq_nrc_save_reusable', 'Save a reusable rate')} →
              </a>
            </div>
          </div>
        )}
      {house.length > 0 && (<>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', margin: '10px 0 4px' }}>{t('sp_ctq_nrc_house_rates', 'House rates (InteGolf)')}</div>
        <div style={{ display: 'grid', gap: 6 }}>{house.map(rateRow)}</div>
      </>)}
    </div>
  )
}

// One hotel in the search results; expands to its individual room options so
// the reseller picks a specific room/rate (like the Stay & Play hotel picker),
// rather than us auto-choosing the cheapest.

// ─── Hotel sort + scan badges ───────────────────────────────────────────────
// All data (price, distance, stars, guest rating) is already in the search
// payload, so sorting + badges are computed client-side — flipping the sort is
// instant and never re-runs the (slow) live search.
const REC_W = { price: 0.5, distance: 0.2, quality: 0.3 } // "Recommended" blend (lower score = better)
const REC_FREE_KM = 3          // distance within this is "close enough" — no penalty
const LODGING_FRACTION = 0.55  // share of the per-golfer budget assumed for lodging ("Within budget")
const GREAT_RATING = 8.5       // guest rating (/10) that earns a "Great rating" badge
const DIST_FAR_KM = 40         // distance pill turns amber beyond this (a notable drive)
const BADGE_META = {
  value:    { key: 'sp_ctq_badge_value',    txt: 'Best value',   c: '#166534', bg: '#f0fdf4', bd: '#bbf7d0' },
  cheapest: { key: 'sp_ctq_badge_cheapest', txt: 'Lowest price', c: '#1e40af', bg: '#eff6ff', bd: '#bfdbfe' },
  closest:  { key: 'sp_ctq_badge_closest',  txt: 'Closest',      c: '#475569', bg: '#f1f5f9', bd: '#e2e8f0' },
  rating:   { key: 'sp_ctq_badge_rating',   txt: 'Great rating', c: '#0f766e', bg: '#f0fdfa', bd: '#99f6e4' },
  budget:   { key: 'sp_ctq_badge_budget',   txt: 'Under budget', c: '#065f46', bg: '#ecfdf5', bd: '#a7f3d0' },
}
// Quality 0..1 from stars (/5) + guest rating (/10); null when the hotel has
// neither, so it's treated as neutral (median) rather than punished or boosted.
function hotelQuality(h) {
  const parts = []
  const s = Number(h.starRating); if (s > 0) parts.push(Math.min(1, s / 5))
  const g = Number(h.guestRating); if (g > 0) parts.push(Math.min(1, g / 10))
  return parts.length ? parts.reduce((a, b) => a + b, 0) / parts.length : null
}
// Enrich + sort hotels for the chosen mode, and tag each with scan badges.
// Returns [{ h, partyTotal, perNight, perGolfer, underBudget, badges }].
function rankHotels(hotels, { sortMode, nights, golfers, rooms, lodgingBudgetPerGolfer }) {
  const g = Math.max(1, Number(golfers) || 1)
  const n = Math.max(1, Number(nights) || 1)
  const rows = (hotels || []).map(h => {
    const r0 = (h.rates || [])[0]
    const bookedRooms = r0 && r0.searchRooms != null ? Math.max(1, Number(r0.searchRooms)) : Math.max(1, Number(rooms) || 1)
    const partyTotal = h.cheapestTotal != null ? Math.round(Number(h.cheapestTotal) * bookedRooms * 100) / 100 : null
    const perNight = partyTotal != null ? partyTotal / n : null
    // PER-ROOM nightly (cheapest room's stay total / nights) — shown on the card
    // headline, labelled "per room", to match the per-room room-row prices.
    const perRoomNight = h.cheapestTotal != null ? Number(h.cheapestTotal) / n : null
    const perGolfer = partyTotal != null ? partyTotal / g : null
    const underBudget = (lodgingBudgetPerGolfer != null && perGolfer != null) ? (perGolfer <= lodgingBudgetPerGolfer) : null
    const distanceKm = h.distanceKm != null ? Number(h.distanceKm) : null
    return { h, partyTotal, perNight, perRoomNight, perGolfer, underBudget, quality: hotelQuality(h), distanceKm }
  })
  const priced = rows.filter(r => r.partyTotal != null)
  const prices = priced.map(r => r.partyTotal)
  const pMin = prices.length ? Math.min(...prices) : 0
  const pMax = prices.length ? Math.max(...prices) : 0
  const dPens = rows.map(r => (r.distanceKm != null ? Math.max(0, r.distanceKm - REC_FREE_KM) : 0))
  const dMax = Math.max(1, ...dPens)
  const nrm = (v, lo, hi) => (hi > lo ? (v - lo) / (hi - lo) : 0)
  rows.forEach(r => {
    const priceN = r.partyTotal != null ? nrm(r.partyTotal, pMin, pMax) : 1
    const distN = (r.distanceKm != null ? Math.max(0, r.distanceKm - REC_FREE_KM) : dMax) / dMax
    const qualN = r.quality != null ? r.quality : 0.5
    r.rec = REC_W.price * priceN + REC_W.distance * distN - REC_W.quality * qualN
  })
  const bestValue = priced.slice().sort((a, b) => a.rec - b.rec)[0]
  const cheapest = priced.slice().sort((a, b) => a.partyTotal - b.partyTotal)[0]
  const closest = rows.filter(r => r.distanceKm != null).sort((a, b) => a.distanceKm - b.distanceKm)[0]
  rows.forEach(r => {
    const b = []
    if (bestValue && r.h.propertyId === bestValue.h.propertyId) b.push('value')
    if (cheapest && r.h.propertyId === cheapest.h.propertyId) b.push('cheapest')
    if (closest && r.h.propertyId === closest.h.propertyId) b.push('closest')
    if (Number(r.h.guestRating) >= GREAT_RATING) b.push('rating')
    if (r.underBudget === true) b.push('budget')
    r.badges = b
  })
  const INF = Number.POSITIVE_INFINITY
  const byRec = (a, b) => a.rec - b.rec
  if (sortMode === 'price') rows.sort((a, b) => (a.partyTotal ?? INF) - (b.partyTotal ?? INF) || byRec(a, b))
  else if (sortMode === 'price_high') rows.sort((a, b) => ((b.partyTotal ?? -INF) - (a.partyTotal ?? -INF)) || byRec(a, b))
  else if (sortMode === 'rating') rows.sort((a, b) => ((Number(b.h.guestRating) || -1) - (Number(a.h.guestRating) || -1)) || byRec(a, b))
  else if (sortMode === 'closest') rows.sort((a, b) => (a.distanceKm ?? INF) - (b.distanceKm ?? INF) || byRec(a, b))
  else if (sortMode === 'budget' && lodgingBudgetPerGolfer != null) {
    rows.sort((a, b) => ((a.underBudget === true ? 0 : 1) - (b.underBudget === true ? 0 : 1)) || byRec(a, b))
  } else rows.sort(byRec) // recommended (also the budget fallback when no budget is set)
  return rows
}

// ─── Facet filters (star rating · property type · rooms-per-unit) ────────────
// Property-type buckets. The backend translates each supplier's own vocabulary
// into our canonical `propertyType` token (see tournament-api canonical.js), so
// the UI just maps that token to a label — it never parses supplier strings.
// These keys MUST stay in lockstep with PROPERTY_TYPES in canonical.js.
const PROPERTY_TYPE_META = {
  hotel:      { key: 'sp_ctq_pt_hotel',      txt: 'Hotel' },
  resort:     { key: 'sp_ctq_pt_resort',     txt: 'Resort' },
  aparthotel: { key: 'sp_ctq_pt_aparthotel', txt: 'Apart-hotel' },
  apartment:  { key: 'sp_ctq_pt_apartment',  txt: 'Apartment' },
  guesthouse: { key: 'sp_ctq_pt_guesthouse', txt: 'Guesthouse / B&B' },
  cottage:    { key: 'sp_ctq_pt_cottage',    txt: 'Cottage / Villa' },
  hostel:     { key: 'sp_ctq_pt_hostel',     txt: 'Hostel' },
  boutique:   { key: 'sp_ctq_pt_boutique',   txt: 'Boutique' },
  camping:    { key: 'sp_ctq_pt_camping',    txt: 'Campground' },
}
function propertyTypeOf(h) {
  // Canonical token straight from the backend; unknown/legacy → 'hotel'.
  const k = String(h?.propertyType || '').toLowerCase()
  return PROPERTY_TYPE_META[k] ? k : 'hotel'
}
// Star bucket: 5/4/3/2, and 1 = "1★ or without rating" (<1.5 or unrated).
function starBucketOf(h) {
  const s = Number(h?.starRating)
  if (!Number.isFinite(s) || s < 1.5) return 1
  return Math.min(5, Math.round(s))
}
// Bedrooms in ONE unit from a room-option name — "2 Bedrooms"/"2BR"/
// "Two-Bedroom"/"Studio" → N; a plain hotel room (no bedroom mention) → 1.
// "2 Queen Beds" is 1 room (beds, not bedrooms), so only "bedroom"/"BR" counts.
const _WORD_NUM = { one: 1, two: 2, three: 3, four: 4, five: 5, six: 6 }
function bedroomsOfRoom(roomType) {
  const s = String(roomType || '').toLowerCase()
  let m = s.match(/(\d+)\s*(?:-|\s)?\s*bedroom/) || s.match(/(\d+)\s*br\b/)
  if (m) return Math.max(1, Math.min(6, Number(m[1])))
  m = s.match(/\b(one|two|three|four|five|six)[-\s]bedroom/)
  if (m) return _WORD_NUM[m[1]] || 1
  return 1 // studios + plain rooms = a single room/unit
}
// Distinct bedroom counts a hotel offers across its room options.
function hotelRoomCounts(h) {
  const set = new Set()
  for (const r of (h.rates || [])) set.add(bedroomsOfRoom(r.roomType))
  if (!set.size) set.add(1)
  return set
}
// ─── Provider facet (which supplier a hotel's rates come from) ───────────────
// A single hotel can carry rates merged from more than one provider, so this is
// a set. Tokens are lowercase (ratehawk | nuitee | expedia | bookingcom | …);
// the display label falls back to the capitalised token for anything new.
const PROVIDER_META = {
  ratehawk:   'RateHawk',
  nuitee:     'Nuitée',
  expedia:    'Expedia',
  bookingcom: 'Booking.com',
  opera:      'Opera',
}
const providerLabel = (p) => PROVIDER_META[p] || (p ? p.charAt(0).toUpperCase() + p.slice(1) : p)
function providersOf(h) {
  const set = new Set()
  for (const r of (h.rates || [])) { const p = String(r.provider || '').toLowerCase(); if (p) set.add(p) }
  if (!set.size) { const p = String(h.source || h.provider || '').toLowerCase(); if (p) set.add(p) }
  return set
}

// STAFF ONLY: deep link to Expedia's live consumer page for this property on the
// selected dates/occupancy, so InteGolf staff can eyeball Expedia's retail price
// vs our rate. The Expedia Rapid property id is the same id expedia.com uses
// (…hNNN.Hotel-Information). For a merged card we use the Expedia RATE's hotelRef
// (the card can be headlined by another supplier). null when no Expedia rate.
function expediaHotelUrl(h, checkin, checkout, golfers, rooms) {
  if (!checkin || !checkout) return null
  let exId = null
  for (const r of (h.rates || [])) {
    if (String(r.provider || '').toLowerCase() === 'expedia') {
      const ref = String(r.hotelRef || '')
      exId = ref.toLowerCase().startsWith('expedia:') ? ref.slice(8) : (ref || null)
      if (exId) break
    }
  }
  if (!exId && String(h.source || h.provider || '').toLowerCase() === 'expedia') exId = h.propertyId
  if (!exId) return null
  const rm = Math.max(1, Number(rooms) || 1)
  const adults = Math.max(1, Math.ceil((Number(golfers) || rm * 2) / rm))
  const rmParams = Array.from({ length: rm }, (_, i) => `rm${i + 1}=a${adults}`).join('&')
  return `https://www.expedia.com/h${encodeURIComponent(exId)}.Hotel-Information?chkin=${checkin}&chkout=${checkout}&${rmParams}`
}

// Expedia AP4: the "Package rate" label applies ONLY to an Expedia package-env
// rate. A hotel_only (standalone) Expedia rate — and every non-Expedia rate — is
// not a package rate. Legacy/untagged Expedia rates (null) are package (they
// predate dual-env, when we only requested hotel_package).
function isExpediaPackageRate(r) {
  if (!r || String(r.provider || '').toLowerCase() !== 'expedia') return false
  return String(r.salesEnvironment || 'hotel_package').toLowerCase() !== 'hotel_only'
}

function HotelRow({ h, cur, adding, onPick, onDetails, t, golfers, fallbackRooms, perRoomNight, perGolfer, expediaHref, badges = [], dimmed = false }) {
  const [open, setOpen] = useState(false)
  const [provFilter, setProvFilter] = useState(null) // null = all suppliers
  const rates = h.rates || []
  const noRate = h.cheapestTotal == null
  // Per-supplier best price (cheapest-first) — drives the provider badge, the
  // "also available from" chips, and the room-list filter.
  const byProv = ratesByProvider(rates)
  const headlineProvider = rates[0]?.provider || h.source || h.provider
  const multiProvider = byProv.length > 1
  const shownRates = provFilter ? rates.filter(r => String(r.provider || 'expedia').toLowerCase() === provFilter) : rates
  // Refundability at a glance, without expanding the room list.
  const anyRef = rates.some(r => r.refundable)
  const allRef = rates.length > 0 && rates.every(r => r.refundable)
  const refBadge = noRate ? null
    : allRef ? { txt: t('sp_ctq_refundable', 'refundable'), c: '#16a34a', bg: '#f0fdf4' }
      : anyRef ? { txt: t('sp_ctq_some_refundable', 'some refundable'), c: '#0369a1', bg: '#eff6ff' }
        : { txt: t('sp_ctq_nonref', 'non-refundable'), c: '#b45309', bg: '#fffbeb' }
  const nameStyle = { fontSize: 13.5, fontWeight: 600, color: '#0f172a', cursor: 'pointer', textDecoration: 'underline', textDecorationColor: '#cbd5e1', textUnderlineOffset: 2 }
  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 10, opacity: dimmed ? 0.55 : 1 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <div onClick={() => onDetails(h)} title={t('sp_ctq_view_details', 'View hotel details')} style={{ flexShrink: 0, cursor: 'pointer' }}>
          {h.image ? <img src={h.image} alt="" style={{ width: 72, height: 56, objectFit: 'cover', borderRadius: 6, display: 'block' }} /> : <div style={{ width: 72, height: 56, background: '#f1f5f9', borderRadius: 6 }} />}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span onClick={() => onDetails(h)} title={t('sp_ctq_view_details', 'View hotel details')} style={nameStyle}>{h.name}</span>
            {headlineProvider && <ProviderBadge provider={headlineProvider} size="xs" title={t('sp_ctq_supplied_by', 'Supplied by {name}', { name: providerMeta(headlineProvider).label })} />}
            {multiProvider && <span style={{ fontSize: 10.5, color: '#64748b' }}>+{byProv.length - 1} {t('sp_ctq_more_suppliers', 'more')}</span>}
            {h.wholePartyOneUnit && (
              <span title={t('sp_ctq_one_unit_help', 'This property rents as a single unit that sleeps your whole group — booked as 1 unit, not multiple rooms.')}
                style={{ fontSize: 10, fontWeight: 700, color: '#7c3aed', background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 999, padding: '1px 8px' }}>
                {t('sp_ctq_one_unit_badge', '{n} guests · 1 unit', { n: h.partyGuests })}
              </span>
            )}
          </div>
          {badges.length > 0 && (
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 3 }}>
              {badges.map(b => { const m = BADGE_META[b]; return m ? (
                <span key={b} style={{ fontSize: 9.5, fontWeight: 700, color: m.c, background: m.bg, border: `1px solid ${m.bd}`, borderRadius: 999, padding: '1px 7px' }}>{t(m.key, m.txt)}</span>
              ) : null })}
            </div>
          )}
          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
            {h.starRating ? `${h.starRating}★ · ` : ''}{h.address?.city || ''}
            {h.guestRating ? <> · {Number(h.guestRating).toFixed(1)}/10{h.guestReviewCount ? ` (${h.guestReviewCount})` : ''}</> : null}
          </div>
          {(perRoomNight != null || perGolfer != null) && (
            <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 1 }}>
              {perRoomNight != null ? `${money(Math.round(perRoomNight), cur)}/${t('sp_ctq_night', 'night')} ${t('sp_ctq_per_room', 'per room')}` : ''}
              {perRoomNight != null && perGolfer != null ? ' · ' : ''}
              {perGolfer != null ? `${money(Math.round(perGolfer), cur)}/${t('sp_ctq_golfer', 'golfer')}` : ''}
            </div>
          )}
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {noRate ? <span style={{ color: '#b45309' }}>{t('sp_ctq_no_rate', 'no live rate')}</span>
              : <span>{rates.length} {t('sp_ctq_room_options', 'room option(s)')}</span>}
            {refBadge && <span style={{ fontSize: 11, fontWeight: 600, color: refBadge.c, background: refBadge.bg, border: `1px solid ${refBadge.c}22`, borderRadius: 999, padding: '1px 8px' }}>{refBadge.txt}</span>}
            <button onClick={() => onDetails(h)} style={{ border: 'none', background: 'none', color: '#2563eb', cursor: 'pointer', fontSize: 12, padding: 0 }}>{t('sp_ctq_details', 'Details')}</button>
            {expediaHref && <a href={expediaHref} target="_blank" rel="noopener noreferrer"
              title={t('sp_ctq_view_expedia_t', 'Open this hotel on Expedia for the selected dates (InteGolf staff)')}
              style={{ color: '#7c3aed', fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>{t('sp_ctq_view_expedia', 'Expedia ↗')}</a>}
          </div>
        </div>
        <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 6, minWidth: 106 }}>
          {h.distanceKm != null && (() => {
            const far = Number(h.distanceKm) > DIST_FAR_KM
            const c = far ? { fg: '#b45309', bg: '#fffbeb', bd: '#fde68a' } : { fg: '#0f766e', bg: '#f0fdfa', bd: '#99f6e4' }
            return (
              <div title={t('sp_ctq_distance_from', 'Distance from {place}', { place: h.address?.city || t('sp_ctq_search_centre', 'the search area') })}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, fontSize: 12.5, fontWeight: 700, color: c.fg, background: c.bg, border: `1px solid ${c.bd}`, borderRadius: 8, padding: '3px 8px', whiteSpace: 'nowrap' }}>
                <span aria-hidden>📍</span>{h.distanceKm} {t('sp_ctq_km', 'km')}
              </div>
            )
          })()}
          <button onClick={() => setOpen(o => !o)} disabled={noRate}
            title={noRate ? undefined : t('sp_ctq_starting_title_room', 'Cheapest room — per room, whole stay')}
            style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid ' + (noRate ? '#e2e8f0' : '#0f172a'), background: '#fff', color: noRate ? '#94a3b8' : '#0f172a', cursor: noRate ? 'default' : 'pointer', textAlign: 'center', lineHeight: 1.25 }}>
            {noRate ? <span style={{ fontSize: 13, fontWeight: 600 }}>{t('sp_ctq_select_room', 'Select room')}</span>
              : open ? <span style={{ fontSize: 13, fontWeight: 600 }}>{t('sp_ctq_hide_rooms', 'Hide rooms')}</span>
                : <>
                    <div style={{ fontSize: 9.5, fontWeight: 500, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.3 }}>{t('sp_ctq_starting_at', 'Starting at')}</div>
                    <div style={{ fontSize: 14.5, fontWeight: 700 }}>{moneyc(h.cheapestTotal, cur)}</div>
                    <div style={{ fontSize: 9, fontWeight: 600, color: '#94a3b8' }}>{t('sp_ctq_per_room', 'per room')}</div>
                    <div style={{ fontSize: 10.5, fontWeight: 600, color: '#2563eb' }}>{t('sp_ctq_select_room', 'Select room')} ›</div>
                  </>}
          </button>
        </div>
      </div>
      {open && (
        <div style={{ marginTop: 8, borderTop: '1px solid #f1f5f9', paddingTop: 6, display: 'grid', gap: 4 }}>
          {/* Cheapest headline is shown above; here the reseller sees each supplier's
              best price and can filter the room list to one supplier. */}
          {multiProvider && (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', paddingBottom: 6 }}>
              <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>{t('sp_ctq_also_from', 'Also available from:')}</span>
              <ProvChip active={!provFilter} onClick={() => setProvFilter(null)} label={t('sp_ctq_all_suppliers', 'All')} />
              {byProv.map(p => (
                <ProvChip key={p.provider} active={provFilter === p.provider} onClick={() => setProvFilter(provFilter === p.provider ? null : p.provider)}
                  label={p.label} price={p.cheapest != null ? moneyc(p.cheapest, cur) : null} provider={p.provider} />
              ))}
            </div>
          )}
          {shownRates.map((r, i) => {
            const key = `${h.propertyId}:${r.rateKey || ''}`
            // Price for the number of rooms the reseller SELECTED (capacity is shown
            // per room so they choose the count; we book exactly what they pick).
            // Single-unit rates already cover the whole party in 1 unit.
            const rm = r.searchRooms != null ? Math.max(1, Number(r.searchRooms)) : Math.max(1, Number(fallbackRooms) || 1)
            const partyTotal = Math.round((Number(r.total) || 0) * rm * 100) / 100
            return (
              <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '5px 4px', borderTop: i ? '1px solid #f8fafc' : 'none' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, color: '#334155', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span>{r.roomType || t('sp_ctq_room', 'Room')}</span>
                    <ProviderBadge provider={r.provider} size="xs" />
                    {/* Expedia AP4: label the package rate per-rate (a hotel now
                        carries both package + standalone rates). */}
                    {isExpediaPackageRate(r) && (
                      <span style={{ fontSize: 9.5, fontWeight: 700, color: '#6d28d9', background: '#f3e8ff', border: '1px solid #e9d5ff', borderRadius: 999, padding: '1px 7px' }}>{t('sp_ctq_package_rate', 'Package rate')}</span>
                    )}
                  </div>
                  <div style={{ fontSize: 11.5, color: '#94a3b8' }}>
                    {r.bedConfig ? r.bedConfig + ' · ' : ''}
                    {Number(r.roomCapacity) > 0 ? <><span style={{ color: '#64748b', fontWeight: 600 }}>{t('sp_ctq_sleeps', 'sleeps {n}', { n: Number(r.roomCapacity) })}</span> · </> : ''}
                    {r.refundable ? <span style={{ color: '#16a34a' }}>{t('sp_ctq_refundable', 'refundable')}</span> : <span style={{ color: '#b45309' }}>{t('sp_ctq_nonref', 'non-refundable')}</span>}
                  </div>
                </div>
                {/* Dual price (like Expedia's own site): lead with the PER-ROOM
                    price, then the ALL-ROOMS total for the selected room count, so
                    there's no confusion over what a single number means. Single-unit
                    whole-party rates (searchRooms) + single-room searches have no
                    ambiguity, so they just show the total + nightly. */}
                <div style={{ fontSize: 12.5, color: '#0f172a', textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <b>{moneyc(r.total, cur)}</b>
                  {rm > 1 && r.searchRooms == null && (
                    <span style={{ fontSize: 10.5, fontWeight: 600, color: '#64748b' }}> {t('sp_ctq_per_room', 'per room')}</span>
                  )}
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>
                    {rm > 1 && r.searchRooms == null
                      ? `${money(partyTotal, cur)} ${t('sp_ctq_total_for_rooms', 'total for {n} rooms', { n: rm })}`
                      : `${money(r.nightly, cur)}/${t('sp_ctq_night', 'night')}`}
                  </div>
                </div>
                <button onClick={() => onPick(h, r)} disabled={adding === key}
                  style={{ flexShrink: 0, padding: '6px 12px', borderRadius: 6, fontWeight: 600, fontSize: 12.5, border: 'none', background: '#0f172a', color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  {adding === key ? '…'
                    : r.searchRooms != null ? t('sp_ctq_add_one_unit', 'Add 1 unit')
                      : rm === 1 ? t('sp_ctq_add_one_room', 'Add 1 room')
                        : t('sp_ctq_add_n_rooms', 'Add {n} rooms', { n: rm })}
                </button>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// A supplier filter pill in the "also available from" row: provider name + its
// cheapest price; click to show only that supplier's rooms (click again = all).
function ProvChip({ active, onClick, label, price, provider }) {
  const m = provider ? providerMeta(provider) : null
  return (
    <button type="button" onClick={onClick}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 999, cursor: 'pointer', fontSize: 11.5, fontWeight: 600,
        border: `1px solid ${active ? (m?.fg || '#0f172a') : '#e2e8f0'}`,
        background: active ? (m?.bg || '#f1f5f9') : '#fff',
        color: active ? (m?.fg || '#0f172a') : '#475569' }}>
      <span>{label}</span>
      {price != null && <span style={{ fontWeight: 700 }}>{price}</span>}
    </button>
  )
}

// A compact facet filter — a button that opens a checklist popover (options +
// live counts). Used for Stars / Property type / Rooms. `selected` is a Set;
// onToggle(value) flips one; onClear() empties it. Options with a 0 count are
// greyed + disabled (unless already selected).
function FacetDropdown({ label, options, selected, onToggle, onClear, t }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    if (!open) return
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [open])
  const n = selected.size
  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ padding: '7px 12px', fontSize: 12.5, fontWeight: 600, borderRadius: 8, cursor: 'pointer',
          border: `1px solid ${n ? '#0f172a' : '#cbd5e1'}`, background: n ? '#0f172a' : '#fff', color: n ? '#fff' : '#475569' }}>
        {label}{n ? ` · ${n}` : ''} ▾
      </button>
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 30, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, boxShadow: '0 10px 28px rgba(0,0,0,0.14)', padding: 6, minWidth: 220, maxHeight: 300, overflowY: 'auto' }}>
          {options.map(o => {
            const disabled = !o.count && !selected.has(o.value)
            return (
              <label key={o.value} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', fontSize: 13, borderRadius: 6, cursor: disabled ? 'default' : 'pointer', color: disabled ? '#cbd5e1' : '#334155' }}>
                <input type="checkbox" checked={selected.has(o.value)} disabled={disabled} onChange={() => onToggle(o.value)} />
                <span style={{ flex: 1 }}>{o.label}</span>
                <span style={{ color: '#94a3b8', fontSize: 12 }}>{o.count}</span>
              </label>
            )
          })}
          {n > 0 && <button onClick={() => { onClear(); setOpen(false) }} style={{ width: '100%', marginTop: 4, padding: '6px 8px', fontSize: 12, border: 'none', background: 'none', color: '#2563eb', cursor: 'pointer', textAlign: 'left' }}>{t('sp_ctq_facet_clear', 'Clear')}</button>}
        </div>
      )}
    </div>
  )
}

// Live hotel search scoped to the trip destination, for the clicked day as
// check-in. Rates from every active supplier (RateHawk, Nuitée, Expedia) are
// merged per physical hotel; picking a room drops in a real hotel item with its
// marked-up price, room type, refundable flag, rate key, and SUPPLIER (so the
// booking dispatches to the right provider).
function HotelSearchModal({ quote, cur, start, initialDayIndex, onClose, onPick, t }) {
  const [dayIndex, setDayIndex] = useState(initialDayIndex || 0)
  // Default check-out to the trip's last day (departure) so the common case —
  // one hotel for the whole stay — needs no extra clicks. numDays is the
  // departure column index. Never less than one night past check-in.
  const [checkoutIdx, setCheckoutIdx] = useState(() => {
    const di = initialDayIndex || 0
    return Math.max(di + 1, Number(quote.numDays) || di + 1)
  })
  const checkin = addDays(start, dayIndex)
  const nights = Math.max(1, checkoutIdx - dayIndex)
  const checkout = addDays(start, checkoutIdx)
  // InteGolf staff (email @integolf.com) get price-audit tools: a "Download JSON"
  // export (incl. our net cost, returned by the backend only to staff) + a
  // "View on Expedia" deep link per Expedia hotel. Hidden from resellers.
  const { user } = useAuth()
  const isStaff = /@integolf\.com$/i.test(String(user?.email || '').trim())
  const [rooms, setRooms] = useState(Math.max(1, Math.ceil((quote.numGolfers || 2) / 2)))
  // Golfers per room sent to the supplier search (per-room occupancy). Defaults to
  // the party spread evenly across the room count; editable so the reseller can
  // lower it (e.g. 1/room) to surface smaller rooms + correct prices. Re-defaults
  // when the room count changes.
  const [adultsPerRoom, setAdultsPerRoom] = useState(() => {
    const g = Math.max(1, Number(quote.numGolfers) || 1)
    const r = Math.max(1, Math.ceil((quote.numGolfers || 2) / 2))
    return Math.max(1, Math.min(8, Math.ceil(g / r)))
  })
  // Per-search radius + optional location override. A tighter radius returns
  // fewer places and is faster + more reliable (esp. for a specific hotel you
  // know is near the destination); the location override lets the reseller search
  // a different spot without changing the trip's saved destination.
  const tripRadius = Math.max(10, Math.min(200, Number(quote.radiusKm) || 75))
  // Hotels default to a TIGHT radius (fast + relevant for a golf trip), decoupled
  // from the trip's destination radius (which is wider so it can find COURSES
  // across a region). A 75km hotel net pulls in stays an hour away AND makes a
  // supplier's id-set large/slow — which can starve a provider from the search.
  // The reseller can still widen per-search via the dropdown. Use the trip radius
  // if it's already tighter than the default.
  const HOTEL_DEFAULT_RADIUS = 25
  const radiusOptions = [...new Set([15, 25, 50, 75, 100, 150, 200, tripRadius])].sort((a, b) => a - b)
  const [radiusKm, setRadiusKm] = useState(Math.min(tripRadius, HOTEL_DEFAULT_RADIUS))
  const [loc, setLoc] = useState(null) // {lat,lng,label} override; null = trip centre
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [elapsed, setElapsed] = useState(0) // seconds the current search has been running (for feedback)
  const [err, setErr] = useState(null)
  const [adding, setAdding] = useState(null)
  const [netMode, setNetMode] = useState('search') // 'search' | 'manual' | 'catalog' (net-rate entry)
  const [nrPrefill, setNrPrefill] = useState(null)  // catalog-picked values seeding the manual form
  const [detailFor, setDetailFor] = useState(null) // hotel object whose Expedia details are open
  // Client-side result filters (apply to the loaded page, no re-fetch).
  const [nameQ, setNameQ] = useState('')
  const [refFilter, setRefFilter] = useState('all') // 'all' | 'ref' | 'nonref'
  // Facet filters (multi-select Sets): star bucket · property type · rooms-per-unit.
  const [starFilter, setStarFilter] = useState(() => new Set())
  const [typeFilter, setTypeFilter] = useState(() => new Set())
  const [roomsFilter, setRoomsFilter] = useState(() => new Set())
  const [providerFilter, setProviderFilter] = useState(() => new Set())
  const toggleIn = (setter) => (v) => setter(prev => { const n = new Set(prev); n.has(v) ? n.delete(v) : n.add(v); return n })
  // Sort mode — remembered across searches for this reseller (localStorage).
  const [sortMode, setSortMode] = useState(() => {
    try { return localStorage.getItem('ctqHotelSort') || 'recommended' } catch { return 'recommended' }
  })
  useEffect(() => { try { localStorage.setItem('ctqHotelSort', sortMode) } catch { /* ignore */ } }, [sortMode])
  // Lodging share of the per-golfer trip budget — drives "Within budget" + the
  // "Under budget" badge. Null when the trip has no budget → budget sort falls
  // back to Recommended and no budget badges show.
  const golfers = Math.max(1, Number(quote.numGolfers) || 1)
  // Re-default golfers/room to the even spread whenever the room count changes.
  useEffect(() => { setAdultsPerRoom(Math.max(1, Math.min(8, Math.ceil(golfers / Math.max(1, rooms))))) }, [rooms, golfers])
  const lodgingBudgetPerGolfer = (quote.budgetPerGolfer != null && Number(quote.budgetPerGolfer) > 0)
    ? Number(quote.budgetPerGolfer) * LODGING_FRACTION : null

  // Filter the loaded hotels by name + refundability. For a refundability
  // filter we also trim each hotel's room list to matching rooms and recompute
  // its "starting at" from what's left, so the row, badge, and expanded rooms
  // all agree with the active filter.
  const visibleHotels = useMemo(() => {
    const all = data?.hotels || []
    const q = nameQ.trim().toLowerCase()
    const out = []
    for (const h of all) {
      if (q && !String(h.name || '').toLowerCase().includes(q)) continue
      if (refFilter === 'all') { out.push(h); continue }
      const wantRef = refFilter === 'ref'
      const rates = (h.rates || []).filter(r => !!r.refundable === wantRef)
      if (!rates.length) continue // hotel has no room matching the filter → hide it
      let cheapestTotal = null
      for (const r of rates) { const v = Number(r.total); if (Number.isFinite(v)) cheapestTotal = cheapestTotal == null ? v : Math.min(cheapestTotal, v) }
      out.push({ ...h, rates, cheapestTotal })
    }
    return out
  }, [data, nameQ, refFilter])

  // Facet option lists with LIVE counts, computed from the name/refundability
  // base (so counts reflect what's actually loaded). Each facet's count ignores
  // its OWN selection but respects the others (standard facet behaviour).
  const facets = useMemo(() => {
    const passType = (h) => !typeFilter.size || typeFilter.has(propertyTypeOf(h))
    const passStar = (h) => !starFilter.size || starFilter.has(starBucketOf(h))
    const passRooms = (h) => !roomsFilter.size || [...hotelRoomCounts(h)].some(c => roomsFilter.has(c))
    const passProv = (h) => !providerFilter.size || [...providersOf(h)].some(p => providerFilter.has(p))
    const starCount = {}, typeCount = {}, roomCount = {}, provCount = {}
    for (const h of visibleHotels) {
      if (passType(h) && passRooms(h) && passProv(h)) { const b = starBucketOf(h); starCount[b] = (starCount[b] || 0) + 1 }
      if (passStar(h) && passRooms(h) && passProv(h)) { const tt = propertyTypeOf(h); typeCount[tt] = (typeCount[tt] || 0) + 1 }
      if (passStar(h) && passType(h) && passProv(h)) { for (const c of hotelRoomCounts(h)) roomCount[c] = (roomCount[c] || 0) + 1 }
      if (passStar(h) && passType(h) && passRooms(h)) { for (const p of providersOf(h)) provCount[p] = (provCount[p] || 0) + 1 }
    }
    const stars = [5, 4, 3, 2, 1].map(s => ({
      value: s, count: starCount[s] || 0,
      label: s === 1 ? `★ ${t('sp_ctq_star_or_none', 'or without rating')}` : '★'.repeat(s),
    }))
    const types = Object.keys(PROPERTY_TYPE_META)
      .map(k => ({ value: k, count: typeCount[k] || 0, label: t(PROPERTY_TYPE_META[k].key, PROPERTY_TYPE_META[k].txt) }))
      .filter(o => o.count > 0 || typeFilter.has(o.value))
    const roomOpts = [1, 2, 3, 4, 5, 6].map(r => ({
      value: r, count: roomCount[r] || 0,
      label: `${r} ${r === 1 ? t('sp_ctq_room_sg', 'room') : t('sp_ctq_rooms_pl', 'rooms')}`,
    })).filter(o => o.count > 0 || roomsFilter.has(o.value))
    const providers = Object.keys(provCount)
      .map(p => ({ value: p, count: provCount[p] || 0, label: providerLabel(p) }))
      .filter(o => o.count > 0 || providerFilter.has(o.value))
      .sort((a, b) => b.count - a.count)
    return { stars, types, rooms: roomOpts, providers }
  }, [visibleHotels, starFilter, typeFilter, roomsFilter, providerFilter, t])

  // Apply the selected facets, then enrich + sort for the chosen mode.
  const rankedHotels = useMemo(() => {
    const kept = visibleHotels.filter(h =>
      (!starFilter.size || starFilter.has(starBucketOf(h))) &&
      (!typeFilter.size || typeFilter.has(propertyTypeOf(h))) &&
      (!roomsFilter.size || [...hotelRoomCounts(h)].some(c => roomsFilter.has(c))) &&
      (!providerFilter.size || [...providersOf(h)].some(p => providerFilter.has(p)))
    )
    return rankHotels(kept, { sortMode, nights, golfers, rooms, lodgingBudgetPerGolfer })
  }, [visibleHotels, starFilter, typeFilter, roomsFilter, providerFilter, sortMode, nights, golfers, rooms, lodgingBudgetPerGolfer])

  // Overlapping-search guard: changing check-in/checkout auto-re-runs the search,
  // so a search can start while one is still in flight. Abort the previous request
  // and ignore any stale response (by sequence number) so the LATEST search always
  // wins — otherwise a slow earlier request could overwrite newer results.
  const searchSeq = useRef(0)
  const searchAbort = useRef(null)
  async function run() {
    const seq = ++searchSeq.current
    if (searchAbort.current) { try { searchAbort.current.abort() } catch { /* ignore */ } }
    const ac = new AbortController()
    searchAbort.current = ac
    setLoading(true); setErr(null)
    try {
      const params = { checkin, nights, rooms, adults: adultsPerRoom, radiusKm }
      if (loc && loc.lat != null && loc.lng != null) { params.lat = loc.lat; params.lng = loc.lng }
      const { data } = await tournamentApi.get(`/portal/reseller/quotes/${quote.id}/hotel-search`, { params, signal: ac.signal })
      if (seq === searchSeq.current) setData(data)
    } catch (e) {
      if (ac.signal.aborted || e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') return // superseded by a newer search
      if (seq === searchSeq.current) { setErr(e?.response?.data?.message || e.message); setData(null) }
    } finally {
      if (seq === searchSeq.current) setLoading(false)
    }
  }
  // Re-run the search whenever a search parameter changes (check-in/out, rooms,
  // radius, OR the location override) so the result list always matches the
  // controls. Picking a "Search near" location (or Reset to trip) changes `loc`,
  // which re-fires the search here — otherwise the header would update to the new
  // place but the results would stay on the old centre (mirrors the +Round modal,
  // which searches on pick). The abort + latest-wins guard in run() keeps rapid
  // changes from racing.
  useEffect(() => { run() }, [dayIndex, checkoutIdx, rooms, adultsPerRoom, radiusKm, loc]) // eslint-disable-line
  // Tick an elapsed-seconds counter while a search is in flight so the loading
  // state can show progress + an escalating "this is taking a while" hint
  // (a cold search — e.g. right after a provider/cache change, or a US metro —
  // can run tens of seconds; a bare "Searching…" looks hung).
  useEffect(() => {
    if (!loading) { setElapsed(0); return }
    setElapsed(0)
    const t0 = Date.now()
    const id = setInterval(() => setElapsed(Math.round((Date.now() - t0) / 1000)), 1000)
    return () => clearInterval(id)
  }, [loading])

  // STAFF: export the current results (incl. our net cost — the backend returns it
  // only to @integolf.com callers) + the search params to a JSON file, for
  // comparing providers / checking we're getting the best Expedia rate.
  function downloadJson() {
    const payload = {
      generatedAt: new Date().toISOString(),
      quoteId: quote.id,
      currency: cur,
      search: {
        checkin, checkout, nights, rooms, radiusKm,
        searchNear: loc?.label || quote.centerLabel || null,
        lat: loc?.lat ?? (quote.centerLat != null ? Number(quote.centerLat) : null),
        lng: loc?.lng ?? (quote.centerLng != null ? Number(quote.centerLng) : null),
        golfers: quote.numGolfers || null,
      },
      note: 'InteGolf staff price audit. netCost = our PER-ROOM net supplier cost (pre-markup); total/nightly are PER-ROOM sell (marked up); markupPct = markup applied. Multiply by rooms for all-rooms.',
      hotelCount: (data?.hotels || []).length,
      hotels: data?.hotels || [],
    }
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    const place = String(loc?.label || quote.centerLabel || 'hotels').split(',')[0].trim().replace(/[^\w-]+/g, '-') || 'hotels'
    a.download = `hotel-audit-${place}-${checkin}-${nights}n-${rooms}rm.json`
    document.body.appendChild(a); a.click(); a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 1500)
  }

  async function pick(h, rate) {
    setAdding(`${h.propertyId}:${rate?.rateKey || ''}`)
    try {
      // Book the number of rooms the reseller SELECTED in the search (the room
      // list shows each room's sleeping capacity so they choose the right count).
      // Single-unit "whole party in 1 unit" rates carry searchRooms:1 — the rate
      // is already the one-unit total for the full party, so book exactly 1 unit
      // and don't re-multiply (that would double-charge).
      const bookedRooms = rate?.searchRooms != null
        ? Math.max(1, Number(rate.searchRooms))
        : Math.max(1, Number(rooms) || 1)
      // Book the SELECTED rate's supplier — a merged hotel card unions rates from
      // multiple providers, so the chosen rate (not the card's owner) decides who
      // we book through. rate.hotelRef is namespaced "provider:nativeId"; strip the
      // prefix so HotelExternalId is the supplier's own hotel id (what the adapter
      // + re-price search expect). Fall back to the card id for legacy single-rate.
      const provider = rate?.provider || h.source || h.provider || 'expedia'
      const nativeId = rate?.hotelRef && provider && String(rate.hotelRef).startsWith(provider + ':')
        ? String(rate.hotelRef).slice(provider.length + 1)
        : (rate?.hotelRef || h.propertyId)
      await onPick({
        dayIndex,
        // Leg/stop label: the location this stay was searched under (override, else
        // the trip centre). Groups the itinerary into legs; display-only.
        stopLabel: loc?.label || quote.centerLabel || null,
        hotelName: h.name, hotelExternalId: nativeId, hotelSource: provider,
        nights, rooms: bookedRooms,
        roomType: rate?.roomType || null,
        refundable: rate?.refundable ? true : false,
        rateKey: rate?.rateKey || rate?.priceCheckHref || null,
        priceCheckHref: rate?.priceCheckHref || null,   // Expedia re-quote handle (needed to book at acceptance)
        salesEnvironment: rate?.salesEnvironment || null,   // Expedia env (hotel_package|hotel_only) — pins the reprice + drives the Package-rate badge
        // Rich content for the customer trip page (photo + ratings).
        imageUrl: h.image || null,
        stars: h.starRating != null ? Number(h.starRating) : null,
        guestRating: h.guestRating != null ? Number(h.guestRating) : null,
        reviewCount: h.guestReviewCount != null ? Number(h.guestReviewCount) : null,
        // Resort/property fee (paid at the hotel) — shown to the customer on the quote.
        resortFee: rate?.resortFee != null ? Number(rate.resortFee) : (rate?.feesDueAtProperty?.resortFee != null ? Number(rate.feesDueAtProperty.resortFee) : null),
        resortFeeCurrency: rate?.resortFeeCurrency || rate?.feesDueAtProperty?.currency || null,
        // Expedia-cert disclosures snapshotted on the quote item: cancellation
        // schedule (property-local dates + penalty nights) + fees due at property.
        cancellation: rate?.cancellation || null,
        feesDueAtProperty: rate?.feesDueAtProperty || null,
        totalPrice: Math.round((rate?.total ?? h.cheapestTotal ?? 0) * bookedRooms * 100) / 100, // per-room rate x rooms-for-party
      })
    } catch (e) { alert(e?.response?.data?.message || e.message); setAdding(null) }
  }

  const sel = { padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13 }
  // City part of the search centre, for the empty-state hints (so a reseller can
  // tell at a glance WHERE we searched — the usual cause of "can't find it" is a
  // mis-picked / far-off destination, not missing coverage).
  const placeLabel = ((loc?.label || quote.centerLabel || '').split(',')[0] || '').trim() || t('sp_ctq_this_area', 'this area')
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 24, zIndex: 60, overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 18, width: '100%', maxWidth: 720, boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16 }}>{t('sp_ctq_add_hotel', 'Add a hotel')}</h3>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{fmtDay(checkin)} → {fmtDay(checkout)} · {nights} {nights === 1 ? t('sp_ctq_night', 'night') : t('sp_ctq_nights', 'nights')} · {(loc?.label || quote.centerLabel || '').split(',')[0]} · {radiusKm} km</div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {isStaff && data && (data.hotels || []).length > 0 && (
              <button onClick={downloadJson}
                title={t('sp_ctq_dl_json_t', 'Download all results incl. our net cost per provider (InteGolf staff)')}
                style={{ border: '1px solid #7c3aed', borderRadius: 6, background: '#f5f3ff', color: '#6d28d9', padding: '6px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                {t('sp_ctq_dl_json', 'Download JSON')}
              </button>
            )}
            <button onClick={onClose} style={{ border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff', padding: '6px 12px', cursor: 'pointer' }}>{t('sp_ctq_close', 'Close')}</button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
          <label style={{ fontSize: 12.5, color: '#475569', display: 'flex', gap: 6, alignItems: 'center' }}>{t('sp_ctq_checkin_day', 'check-in')}
            <select value={dayIndex} onChange={e => { const nd = Number(e.target.value); setDayIndex(nd); if (checkoutIdx <= nd) setCheckoutIdx(Math.min(quote.numDays, nd + 1)) }} style={sel}>
              {Array.from({ length: quote.numDays }, (_, i) => <option key={i} value={i}>{fmtDay(addDays(start, i))}</option>)}
            </select></label>
          <label style={{ fontSize: 12.5, color: '#475569', display: 'flex', gap: 6, alignItems: 'center' }}>{t('sp_ctq_checkout', 'check-out')}
            <select value={checkoutIdx} onChange={e => setCheckoutIdx(Number(e.target.value))} style={sel}>
              {Array.from({ length: quote.numDays - dayIndex }, (_, i) => { const idx = dayIndex + 1 + i; return <option key={idx} value={idx}>{fmtDay(addDays(start, idx))}</option> })}
            </select></label>
          <label style={{ fontSize: 12.5, color: '#475569', display: 'flex', gap: 6, alignItems: 'center' }}>{t('sp_ctq_rooms_full', 'rooms')}
            <select value={rooms} onChange={e => setRooms(Number(e.target.value))} style={sel}>{Array.from({ length: 8 }, (_, i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}</select></label>
          <label style={{ fontSize: 12.5, color: '#475569', display: 'flex', gap: 6, alignItems: 'center' }}>{t('sp_ctq_golfers_room', 'golfers/room')}
            <select value={adultsPerRoom} onChange={e => setAdultsPerRoom(Number(e.target.value))} style={sel}>{Array.from({ length: 8 }, (_, i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}</select></label>
          <label style={{ fontSize: 12.5, color: '#475569', display: 'flex', gap: 6, alignItems: 'center' }}>{t('sp_ctq_radius_full', 'radius')}
            <select value={radiusKm} onChange={e => setRadiusKm(Number(e.target.value))} style={sel}>{radiusOptions.map(r => <option key={r} value={r}>{r} km</option>)}</select></label>
          <button onClick={run} disabled={loading} style={{ padding: '7px 14px', borderRadius: 6, fontWeight: 600, fontSize: 13, border: 'none', background: '#0f172a', color: '#fff', cursor: 'pointer' }}>{loading ? t('sp_ctq_searching', 'Searching…') : t('sp_ctq_search', 'Search')}</button>
        </div>

        {/* Location override — search a different spot without changing the trip's
            saved destination. Tighter radius above + a precise centre here make a
            known hotel reliably findable. Picking a place (or Reset to trip) re-runs
            the search immediately. */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12.5, color: '#64748b', flexShrink: 0 }}>{t('sp_ctq_search_near', 'Search near')}</span>
          <div style={{ flex: '1 1 260px', minWidth: 200 }}>
            <LocationPicker key={loc ? 'ov' : 'trip'} label={loc?.label || quote.centerLabel || ''} t={t}
              inputStyle={{ padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13, width: '100%', boxSizing: 'border-box' }}
              onPick={p => setLoc({ lat: p.lat, lng: p.lng, label: p.label })} />
          </div>
          {loc && <button onClick={() => { setLoc(null) }} style={{ fontSize: 12, border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff', padding: '6px 10px', cursor: 'pointer', flexShrink: 0 }}>{t('sp_ctq_reset_to_trip', 'Reset to trip')}</button>}
        </div>

        {/* Result filters: hotel-name search + refundability. Client-side on the loaded page. */}
        {!loading && data && (data.hotels || []).length > 0 && (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', flex: '1 1 220px', minWidth: 160 }}>
              <input value={nameQ} onChange={e => setNameQ(e.target.value)} placeholder={t('sp_ctq_filter_name_ph', 'Filter by hotel name')}
                style={{ width: '100%', padding: '7px 28px 7px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
              {nameQ && <button onClick={() => setNameQ('')} aria-label={t('sp_ctq_filter_clear', 'Clear')} title={t('sp_ctq_filter_clear', 'Clear')}
                style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 15, lineHeight: 1, padding: 2 }}>×</button>}
            </div>
            <div style={{ display: 'inline-flex', borderRadius: 8, border: '1px solid #cbd5e1', overflow: 'hidden', flexShrink: 0 }}>
              {[['all', t('sp_ctq_filter_all', 'All')], ['ref', t('sp_ctq_refundable_cap', 'Refundable')], ['nonref', t('sp_ctq_nonref_cap', 'Non-refundable')]].map(([val, label], i) => (
                <button key={val} onClick={() => setRefFilter(val)}
                  style={{ padding: '7px 12px', fontSize: 12.5, fontWeight: 600, border: 'none', borderLeft: i ? '1px solid #e2e8f0' : 'none', cursor: 'pointer', background: refFilter === val ? '#0f172a' : '#fff', color: refFilter === val ? '#fff' : '#475569' }}>
                  {label}
                </button>
              ))}
            </div>
            <label style={{ fontSize: 12.5, color: '#475569', display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>{t('sp_ctq_sort', 'Sort')}
              <select value={sortMode} onChange={e => setSortMode(e.target.value)} style={{ padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13 }}>
                <option value="recommended">{t('sp_ctq_sort_recommended', 'Recommended')}</option>
                <option value="budget">{t('sp_ctq_sort_budget', 'Within budget')}</option>
                <option value="price">{t('sp_ctq_sort_price', 'Lowest price')}</option>
                <option value="price_high">{t('sp_ctq_sort_price_high', 'Price (high to low)')}</option>
                <option value="closest">{t('sp_ctq_sort_closest', 'Closest')}</option>
                <option value="rating">{t('sp_ctq_sort_rating', 'Guest rating (high to low)')}</option>
              </select></label>
          </div>
        )}
        {/* Facet filters: star rating · property type · rooms-per-unit (with live counts). */}
        {!loading && data && (data.hotels || []).length > 0 && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
            <FacetDropdown label={t('sp_ctq_facet_stars', 'Stars')} options={facets.stars} selected={starFilter} onToggle={toggleIn(setStarFilter)} onClear={() => setStarFilter(new Set())} t={t} />
            <FacetDropdown label={t('sp_ctq_facet_type', 'Property type')} options={facets.types} selected={typeFilter} onToggle={toggleIn(setTypeFilter)} onClear={() => setTypeFilter(new Set())} t={t} />
            <FacetDropdown label={t('sp_ctq_facet_rooms', 'Rooms')} options={facets.rooms} selected={roomsFilter} onToggle={toggleIn(setRoomsFilter)} onClear={() => setRoomsFilter(new Set())} t={t} />
            {(facets.providers.length > 1 || providerFilter.size > 0) && (
              <FacetDropdown label={t('sp_ctq_facet_provider', 'Provider')} options={facets.providers} selected={providerFilter} onToggle={toggleIn(setProviderFilter)} onClear={() => setProviderFilter(new Set())} t={t} />
            )}
            {(starFilter.size + typeFilter.size + roomsFilter.size + providerFilter.size) > 0 && (
              <button onClick={() => { setStarFilter(new Set()); setTypeFilter(new Set()); setRoomsFilter(new Set()); setProviderFilter(new Set()) }}
                style={{ fontSize: 12, border: 'none', background: 'none', color: '#2563eb', cursor: 'pointer' }}>{t('sp_ctq_facet_clear_all', 'Clear filters')}</button>
            )}
          </div>
        )}
        {!loading && data && (data.hotels || []).length > 0 && (
          <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: -4, marginBottom: 10 }}>
            {sortMode === 'recommended' ? t('sp_ctq_sort_cap_rec', 'Sorted by the best balance of price, location & quality.')
              : sortMode === 'budget' ? (lodgingBudgetPerGolfer != null ? t('sp_ctq_sort_cap_budget', 'Best value within the budget first; over-budget shown last.') : t('sp_ctq_sort_cap_nobudget', 'No budget set on this trip — showing Recommended. Set a budget/golfer to use this.'))
              : sortMode === 'closest' ? t('sp_ctq_sort_cap_closest', 'Sorted by distance from the search centre.')
              : sortMode === 'price_high' ? t('sp_ctq_sort_cap_price_high', 'Sorted by highest total price.')
              : sortMode === 'rating' ? t('sp_ctq_sort_cap_rating', 'Sorted by guest rating, highest first.')
              : t('sp_ctq_sort_cap_price', 'Sorted by lowest total price.')}
          </div>
        )}

        {err && <div style={{ padding: 10, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#991b1b', fontSize: 13 }}>{err}</div>}
        <div style={{ margin: '2px 0 8px', display: 'flex', gap: 14, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          {netMode === 'search' ? (<>
            <button onClick={() => setNetMode('catalog')} style={{ background: 'none', border: 'none', color: '#6d28d9', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}>
              {t('sp_ctq_nrc_link', 'Use a saved net-rate property')}
            </button>
            <button onClick={() => { setNrPrefill(null); setNetMode('manual') }} style={{ background: 'none', border: 'none', color: '#6d28d9', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}>
              {t('sp_ctq_nr_link', "Can't find it? Add a property at a net rate")}
            </button>
          </>) : (
            <button onClick={() => { setNetMode('search'); setNrPrefill(null) }} style={{ background: 'none', border: 'none', color: '#6d28d9', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}>
              {t('sp_ctq_nr_back', '← Back to search')}
            </button>
          )}
        </div>
        {netMode === 'catalog' ? (
          <NetRateCatalogPick cur={cur} nights={nights} rooms={rooms} golfers={golfers}
            checkin={checkin} checkout={checkout}
            onPrefill={(vals) => { setNrPrefill(vals); setNetMode('manual') }} t={t} />
        ) : netMode === 'manual' ? (
          <NetRateHotelForm key={nrPrefill ? `pf-${nrPrefill.rateId}` : 'blank'}
            cur={cur} dayIndex={dayIndex} nights={nights} rooms={rooms}
            stopLabel={loc?.label || quote.centerLabel || null}
            initial={nrPrefill} onAdd={onPick} t={t} />
        ) : loading ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#64748b' }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>
                {t('sp_ctq_searching', 'Searching…')}{elapsed >= 2 ? ` (${elapsed}s)` : ''}
              </div>
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6, lineHeight: 1.5, maxWidth: 420, marginLeft: 'auto', marginRight: 'auto' }}>
                {t('sp_ctq_searching_hint', 'Checking live rates across your hotel suppliers for these dates.')}
                {elapsed >= 12 ? ' ' + t('sp_ctq_searching_slow', 'A first search for a new area, or right after a provider or cache change, can take up to a minute.') : ''}
              </div>
            </div>
          )
          : data && (data.hotels || []).length === 0 ? <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 13, lineHeight: 1.5 }}>{t('sp_ctq_no_hotels', 'No hotels found near {place} within {radius} km for these dates. Try a larger radius, or re-check the destination above.', { place: placeLabel, radius: radiusKm })}</div>
            : data && rankedHotels.length === 0 ? <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 13, lineHeight: 1.5 }}>{t('sp_ctq_filter_none', 'None of the {count} hotels found near {place} match your filters. Clear a filter, widen the radius, or re-check the destination above.', { count: (data.hotels || []).length, place: placeLabel })}</div>
            : (
              <div style={{ maxHeight: 440, overflowY: 'auto', display: 'grid', gap: 8 }}>
                {rankedHotels.map(e => (
                  <HotelRow key={e.h.propertyId} h={e.h} cur={cur} adding={adding} onPick={pick} onDetails={setDetailFor} t={t} golfers={quote.numGolfers} fallbackRooms={rooms}
                    perRoomNight={e.perRoomNight} perGolfer={e.perGolfer} badges={e.badges}
                    expediaHref={isStaff ? expediaHotelUrl(e.h, checkin, checkout, quote.numGolfers, rooms) : null}
                    dimmed={sortMode === 'budget' && e.underBudget === false} />
                ))}
              </div>
            )}
      </div>
      {detailFor && <HotelDetailModal quoteId={quote.id} hotel={detailFor} cur={cur} onClose={() => setDetailFor(null)} t={t} />}
    </div>
  )
}

// Full Expedia rich content for one property (photo gallery, descriptions,
// amenities, check-in/out, fees, policies). Fetched on open from the CTQ
// hotel-detail endpoint (cache-first, live fallback). Read-only — booking still
// happens via "Select room" on the search row. Renders above the search modal.
function HotelDetailModal({ quoteId, hotel, cur, onClose, t }) {
  const [d, setD] = useState(null)
  const [courses, setCourses] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)
  const [lightbox, setLightbox] = useState(null) // photo URL shown full-size, or null
  useEffect(() => {
    let alive = true
    setLoading(true); setErr(null)
    tournamentApi.get(`/portal/reseller/quotes/${quoteId}/hotel/${encodeURIComponent(hotel.propertyId)}`)
      .then(r => { if (alive) { setD(r.data.hotel); setCourses(Array.isArray(r.data.courses) ? r.data.courses : []) } })
      .catch(e => { if (alive) setErr(e?.response?.data?.message || e.message) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [quoteId, hotel.propertyId])

  // Photos — de-dupe the detail photos + the search-card photo, and drop any
  // that fail to load (RateHawk sometimes returns a dead image URL). Shown as
  // small click-to-enlarge thumbnails beside the map, not a big hero.
  const [badImgs, setBadImgs] = useState({})
  const allImgs = [...new Set([...(d?.images || []).map(im => im && im.url), hotel.image].filter(Boolean))]
  const goodImgs = allImgs.filter(u => !badImgs[u])
  const addr = d?.address || hotel.address || {}
  const addrLine = [addr.line1, addr.city, addr.state, addr.postalCode].filter(Boolean).join(', ')
  // Location for the map — detail coords, else the search-card coords (always present).
  const loc = d?.location || hotel.location || null
  const lat = loc ? Number(loc.lat ?? loc.latitude) : NaN
  const lng = loc ? Number(loc.lng ?? loc.longitude) : NaN
  const hasLoc = Number.isFinite(lat) && Number.isFinite(lng)

  return (
    <div onClick={e => { e.stopPropagation(); onClose() }} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 24, zIndex: 70, overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 760, boxShadow: '0 10px 40px rgba(0,0,0,0.28)', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, padding: '16px 18px 8px' }}>
          <div style={{ minWidth: 0 }}>
            <h3 style={{ margin: 0, fontSize: 17, color: '#0f172a' }}>{d?.name || hotel.name}
              {/* Expedia AP4: package rates carry a unique label. Shown only when
                  this hotel actually offers a package-env rate (a hotel can now
                  carry both package + standalone rates; the per-rate label in the
                  room list is the authoritative per-rate badge). */}
              {(hotel.rates || []).some(isExpediaPackageRate) && (
                <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, color: '#6d28d9', background: '#f3e8ff', border: '1px solid #e9d5ff', padding: '2px 7px', borderRadius: 6, verticalAlign: 'middle' }}>{t('sp_ctq_package_rate', 'Package rate')}</span>
              )}
            </h3>
            <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 3 }}>
              {(d?.starRating || hotel.starRating) ? `${d?.starRating || hotel.starRating}★` : ''}
              {(d?.guestRating || hotel.guestRating) ? `${(d?.starRating || hotel.starRating) ? ' · ' : ''}${Number(d?.guestRating || hotel.guestRating).toFixed(1)}/10${(d?.guestReviewCount || hotel.guestReviewCount) ? ` (${d?.guestReviewCount || hotel.guestReviewCount} ${t('sp_ctq_reviews', 'reviews')})` : ''}` : ''}
              {d?.category ? ` · ${d.category}` : ''}
            </div>
            {addrLine && <div style={{ fontSize: 12.5, color: '#94a3b8', marginTop: 2 }}>📍 {addrLine}</div>}
          </div>
          <button onClick={onClose} style={{ flexShrink: 0, border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff', padding: '6px 12px', cursor: 'pointer', fontSize: 13 }}>{t('sp_ctq_close', 'Close')}</button>
        </div>

        {loading ? <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>{t('sp_ctq_loading', 'Loading…')}</div> : (
          <div style={{ padding: '0 18px 18px' }}>
            {err && <div style={{ padding: 10, marginBottom: 10, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#991b1b', fontSize: 13 }}>{err}</div>}

            {/* Map (the main view) + small photo thumbnails to the side. The map
                plots the hotel + the trip's golf courses, so the reseller sees how
                the hotel sits relative to the golf. Photos are click-to-enlarge. */}
            {(hasLoc || goodImgs.length > 0) && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'stretch' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {hasLoc
                    ? <HotelLocationMap hotel={{ lat, lng, name: d?.name || hotel.name }} courses={courses} height={goodImgs.length > 0 ? 340 : 300} />
                    : <div style={{ height: 340, borderRadius: 10, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 13 }}>{t('sp_ctq_no_map', 'Location not available')}</div>}
                  {hasLoc && (
                    <div style={{ marginTop: 5, display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 11, color: '#64748b' }}>
                      <span><span aria-hidden>🏨</span> {t('sp_ctq_map_hotel', 'Hotel')}</span>
                      <span><span style={{ color: '#16a34a' }}>●</span> {t('sp_ctq_map_connected', 'Connected course')}</span>
                      <span><span style={{ color: '#f59e0b' }}>●</span> {t('sp_ctq_map_request', 'Request course')}</span>
                      <a href={`https://www.google.com/maps/search/?api=1&query=${lat}%2C${lng}`} target="_blank" rel="noreferrer" style={{ color: '#2563eb', textDecoration: 'none' }}>{t('sp_ctq_view_larger_map', 'View larger map')} ›</a>
                    </div>
                  )}
                </div>
                {goodImgs.length > 0 && (
                  <div style={{ width: 92, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 340, overflowY: 'auto' }}>
                    {goodImgs.map(u => (
                      <img key={u} src={u} alt="" onClick={() => setLightbox(u)} onError={() => setBadImgs(b => ({ ...b, [u]: true }))}
                        title={t('sp_ctq_view_photo', 'View photo')}
                        style={{ width: '100%', height: 68, objectFit: 'cover', borderRadius: 6, cursor: 'pointer', border: '1px solid #e2e8f0', flexShrink: 0 }} />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Descriptions */}
            {(d?.descriptions || []).map((b, i) => (
              <div key={i} style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', textTransform: 'capitalize', marginBottom: 2 }}>{b.label}</div>
                <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: b.text }} />
              </div>
            ))}

            {/* Check-in / out */}
            {(d?.checkin?.beginTime || d?.checkout?.time) && (
              <div style={{ fontSize: 12.5, color: '#475569', marginBottom: 10, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                {d?.checkin?.beginTime && <span><b>{t('sp_ctq_checkin_day', 'check-in')}:</b> {d.checkin.beginTime}{d.checkin.endTime ? `–${d.checkin.endTime}` : ''}</span>}
                {d?.checkout?.time && <span><b>{t('sp_ctq_checkout', 'check-out')}:</b> {d.checkout.time}</span>}
                {d?.checkin?.minAge && <span><b>{t('sp_ctq_min_age', 'min age')}:</b> {d.checkin.minAge}</span>}
              </div>
            )}

            {/* Amenities */}
            {(d?.amenities || []).length > 0 && (
              <div style={{ marginBottom: 6 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>{t('sp_ctq_amenities', 'Amenities')}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {d.amenities.map((a, i) => (
                    <span key={i} style={{ fontSize: 11.5, color: '#334155', background: '#f1f5f9', borderRadius: 999, padding: '2px 10px' }}>{a}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Expedia AP3/BP2: the agent must see the fees + policies + special
                check-in instructions when reviewing a package-rate hotel. */}
            {(d?.checkin?.instructions || d?.checkin?.specialInstructions) && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>{t('sp_ctq_checkin_instructions', 'Check-in instructions')}</div>
                <div style={{ fontSize: 12.5, color: '#475569', lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: d.checkin.instructions || d.checkin.specialInstructions }} />
              </div>
            )}
            {(d?.fees?.mandatory || d?.fees?.optional) && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>{t('sp_ctq_fees', 'Fees')}</div>
                {d.fees.mandatory && <div style={{ fontSize: 12.5, color: '#475569', lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: d.fees.mandatory }} />}
                {d.fees.optional && <div style={{ fontSize: 12.5, color: '#475569', lineHeight: 1.5, marginTop: 4 }} dangerouslySetInnerHTML={{ __html: d.fees.optional }} />}
              </div>
            )}
            {(d?.policies?.know_before_you_go || d?.policies?.knowBeforeYouGo) && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>{t('sp_ctq_policies', 'Policies')}</div>
                <div style={{ fontSize: 12.5, color: '#475569', lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: d.policies.know_before_you_go || d.policies.knowBeforeYouGo }} />
              </div>
            )}

            {!d?.descriptions?.length && !(d?.amenities || []).length && !goodImgs.length && !hasLoc && !err && !d?.fees && !d?.policies && (
              <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>{t('sp_ctq_no_details', 'No extra details available for this hotel.')}</div>
            )}
          </div>
        )}
      </div>
      {/* Photo lightbox — click a thumbnail to view it full-size; click to close.
          zIndex must clear Leaflet's panes/controls (up to ~1000) or the map
          renders through the photo. */}
      {lightbox && (
        <div onClick={(e) => { e.stopPropagation(); setLightbox(null) }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32, zIndex: 10000, cursor: 'zoom-out' }}>
          <img src={lightbox} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 8 }} />
        </div>
      )}
    </div>
  )
}

// +Round: pick a connected course near the destination, then a play date. We
// pull LIVE tee times + reseller sell price when the course's tee sheet supports
// it; otherwise fall back to an estimated tee-time window + typed price (per
// Todd: default to live rates, only type in when there's no live tee sheet).
function RoundSearchModal({ quote, cur, playDate, onClose, onPick, t, heading, preselectCourseId, initialGolfers, presetCourseName, presetTeeTime, presetPrice, presetNetRate }) {
  // A manually-entered round stores its time as a "~<window>" band (e.g.
  // "~08:00-09:00") instead of a real slot; a live round stores the slot time.
  const presetWindow = presetTeeTime && String(presetTeeTime).startsWith('~') ? String(presetTeeTime).replace(/^~/, '') : null
  // Pure manual = a window time AND no listed course. A listed course with a
  // window (added via the no-live-times fallback) still preselects the course;
  // its window + price just pre-fill the fallback form if it appears.
  const isManualPreset = !!presetWindow && preselectCourseId == null
  // Only a real (live) slot time is worth highlighting/checking in the list.
  const highlightTee = presetTeeTime && !presetWindow ? presetTeeTime : null
  const [courses, setCourses] = useState(null)
  const [loadingCourses, setLoadingCourses] = useState(true)
  const [err, setErr] = useState(null)
  const [course, setCourse] = useState(isManualPreset ? 'manual' : null) // course object | 'manual' | null
  const [date, setDate] = useState(playDate || '')
  const [golfers, setGolfers] = useState(initialGolfers || quote.numGolfers || 1)
  const [tee, setTee] = useState(null)
  const [loadingTee, setLoadingTee] = useState(false)
  const [adding, setAdding] = useState(false)
  // Hand-confirm (net rate): mark this round so it is never live-verified or
  // live-booked at checkout — staff confirm the tee time by hand (mirrors a
  // net-rate hotel). Carried as `netRate` on the item; the backend predicate
  // (roundBookability) treats a marked round as net-rate at all 7 gates.
  const [netRate, setNetRate] = useState(!!presetNetRate)
  const [manualName, setManualName] = useState(isManualPreset ? (presetCourseName || '') : '')
  const [manualTime, setManualTime] = useState(presetWindow || '08:00-09:00') // preferred hour-band window
  const [manualPrice, setManualPrice] = useState(presetWindow && presetPrice != null ? String(presetPrice) : '')
  // Multi-stop: search connected courses near a different leg without moving the
  // trip centre. null = trip centre. The chosen label becomes the round's StopLabel.
  const [loc, setLoc] = useState(null)
  const [searchRadiusKm, setSearchRadiusKm] = useState(quote.radiusKm || 25)
  const [searchingArea, setSearchingArea] = useState(false)
  async function searchArea(nextLoc, nextRadius) {
    const l = nextLoc !== undefined ? nextLoc : loc
    const rad = nextRadius !== undefined ? nextRadius : searchRadiusKm
    setSearchingArea(true); setErr(null)
    try {
      const params = l ? { lat: l.lat, lng: l.lng, radiusKm: rad } : {}
      const r = await tournamentApi.get(`/portal/reseller/quotes/${quote.id}/course-search`, { params })
      setCourses(r.data.courses || [])
    } catch (e) { setErr(e?.response?.data?.message || e.message) }
    finally { setSearchingArea(false) }
  }

  useEffect(() => {
    tournamentApi.get(`/portal/reseller/quotes/${quote.id}/course-search`)
      .then(r => {
        const list = r.data.courses || []
        setCourses(list)
        // Editing a round on a listed course: jump straight to its tee sheet for
        // the day (the reseller can still "← courses" to switch). Falls back to
        // the list when the course isn't in range anymore. Pure manual rounds
        // open the manual form pre-filled (handled via initial state above).
        if (preselectCourseId != null) {
          const match = list.find(c => String(c.coursesId) === String(preselectCourseId))
          if (match) loadTee(match, playDate || date)
        }
      })
      .catch(e => setErr(e?.response?.data?.message || e.message))
      .finally(() => setLoadingCourses(false))
  }, []) // eslint-disable-line

  async function loadTee(c, d) {
    setCourse(c); setDate(d); setTee(null); setLoadingTee(true)
    try {
      const { data } = await tournamentApi.get(`/portal/reseller/quotes/${quote.id}/course/${c.coursesId}/tee-times`, { params: { date: d, golfers } })
      setTee(data)
      // Request-only (concierge) course: pre-fill the reseller's fixed price so
      // they don't have to re-type it; they still pick a preferred time.
      if (data.requestOnly && data.requestPrice != null) setManualPrice(String(data.requestPrice))
    } catch (e) { setTee({ slots: [], fallback: true, message: e?.response?.data?.message }) }
    finally { setLoadingTee(false) }
  }

  async function addRound(fields) {
    setAdding(true)
    try { await onPick(fields) } catch (e) { alert(e?.response?.data?.message || e.message); setAdding(false) }
  }
  // Leg/stop label: the location this round was searched under (override, else
  // the trip centre). Groups the itinerary into legs; display-only.
  const stopLabel = loc?.label || quote.centerLabel || null
  const pickLive = (s) => addRound({ stopLabel, coursesId: course.coursesId, courseName: course.name, teeTime: s.teeTime, golfersOnRound: golfers, pricePerGolfer: s.pricePerGolfer, totalPrice: Math.round(s.pricePerGolfer * golfers * 100) / 100, netRate })
  const addManual = () => {
    const p = Number(manualPrice); if (!p) return
    addRound({
      stopLabel,
      coursesId: course === 'manual' ? null : course?.coursesId, courseName: course === 'manual' ? manualName : course?.name,
      teeTime: manualTime ? `~${manualTime}` : null, golfersOnRound: golfers,
      pricePerGolfer: p, totalPrice: Math.round(p * golfers * 100) / 100, netRate,
    })
  }

  const inp = { padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13 }
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 24, zIndex: 60, overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 18, width: '100%', maxWidth: 640, boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16 }}>{heading || t('sp_ctq_add_round', 'Add a round')}</h3>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{fmtDay(playDate)} · {quote.centerLabel ? quote.centerLabel.split(',')[0] : ''} · {quote.radiusKm || 25} km</div>
          </div>
          <button onClick={onClose} style={{ border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff', padding: '6px 12px', cursor: 'pointer' }}>{t('sp_ctq_close', 'Close')}</button>
        </div>

        {err && <div style={{ padding: 10, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#991b1b', fontSize: 13 }}>{err}</div>}

        {/* Search near a different leg — find connected courses around another
            location without moving the trip centre. The chosen label groups this
            round into that leg on the quote. */}
        {!course && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '4px 0 12px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, color: '#64748b', flexShrink: 0 }}>{t('sp_ctq_search_near', 'Search near')}</span>
            <div style={{ flex: '1 1 240px', minWidth: 180 }}>
              <LocationPicker key={loc ? 'ov' : 'trip'} label={loc?.label || quote.centerLabel || ''} t={t}
                inputStyle={{ padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13, width: '100%', boxSizing: 'border-box' }}
                onPick={p => { const nl = { lat: p.lat, lng: p.lng, label: p.label }; setLoc(nl); searchArea(nl) }} />
            </div>
            <select value={searchRadiusKm} onChange={e => { const r = Number(e.target.value); setSearchRadiusKm(r); if (loc) searchArea(loc, r) }} style={inp}>
              {[25, 50, 75, 100, 150].map(r => <option key={r} value={r}>{r} km</option>)}
            </select>
            {loc && <button onClick={() => { setLoc(null); searchArea(null) }} style={{ fontSize: 12, border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff', padding: '6px 10px', cursor: 'pointer', flexShrink: 0 }}>{t('sp_ctq_reset_to_trip', 'Reset to trip')}</button>}
            {searchingArea && <span style={{ fontSize: 12, color: '#94a3b8' }}>{t('sp_ctq_searching', 'Searching…')}</span>}
          </div>
        )}

        {!course && (loadingCourses
          ? <div style={{ padding: 24, textAlign: 'center', color: '#64748b' }}>{t('sp_ctq_loading_courses', 'Finding courses…')}</div>
          : (
            <div style={{ display: 'grid', gap: 6, maxHeight: 380, overflowY: 'auto' }}>
              {(courses || []).length === 0 && <div style={{ fontSize: 12.5, color: '#94a3b8', padding: 8 }}>{t('sp_ctq_no_courses', 'No connected courses in this area.')}</div>}
              {(courses || []).map(c => (
                <button key={c.coursesId} onClick={() => loadTee(c, date)} style={{ textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, border: '1px solid #e2e8f0', borderRadius: 8, padding: '9px 12px', background: '#fff', cursor: 'pointer' }}>
                  <span><b style={{ fontSize: 13, color: '#0f172a' }}>{c.name}</b> <span style={{ fontSize: 12, color: '#94a3b8' }}>· {c.city || ''} · {c.distanceKm} km</span></span>
                  {c.requestOnly
                    ? <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: '#ede9fe', color: '#6d28d9' }}>{t('sp_ctq_request_only', 'request')}</span>
                    : <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: c.hasLiveBooking ? '#dcfce7' : '#f1f5f9', color: c.hasLiveBooking ? '#166534' : '#94a3b8' }}>{c.hasLiveBooking ? t('sp_ctq_live', 'live rates') : t('sp_ctq_manual', 'manual')}</span>}
                </button>
              ))}
              <button onClick={() => setCourse('manual')} style={{ textAlign: 'left', border: '1px dashed #cbd5e1', borderRadius: 8, padding: '9px 12px', background: '#fff', cursor: 'pointer', fontSize: 12.5, color: '#475569' }}>
                {t('sp_ctq_course_manual', 'Course not listed? Enter it manually')}
              </button>
            </div>
          ))}

        {course && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
              <button onClick={() => { setCourse(null); setTee(null) }} style={{ border: 'none', background: 'none', color: '#2563eb', cursor: 'pointer', fontSize: 12.5 }}>&larr; {t('sp_ctq_courses', 'courses')}</button>
              <b style={{ fontSize: 13.5 }}>{course === 'manual' ? t('sp_ctq_manual_course', 'Manual course') : course.name}</b>
              <label style={{ fontSize: 12.5, color: '#475569', display: 'flex', gap: 6, alignItems: 'center' }}>{t('sp_ctq_play_date', 'date')}
                <input type="date" value={date} onChange={e => { const d = e.target.value; if (course !== 'manual') loadTee(course, d); else setDate(d) }} style={inp} /></label>
              <label style={{ fontSize: 12.5, color: '#475569', display: 'flex', gap: 6, alignItems: 'center' }}>{t('sp_ctq_golfers_word', 'golfers')}
                <input type="number" min="1" value={golfers} onChange={e => setGolfers(Number(e.target.value))} style={{ ...inp, width: 60 }} /></label>
              {/* Hand-confirm (net rate): the reseller marks a round so it is never
                  live-booked/verified at checkout even when it carries a real tee
                  time — staff confirm it by hand. This is the settable half of the
                  golf net-rate predicate (S1 column NetRateRound). */}
              <label style={{ fontSize: 12.5, color: '#475569', display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="checkbox" checked={netRate} onChange={e => setNetRate(e.target.checked)} />
                {t('sp_ctq_round_netrate', 'Hand-confirm (net rate)')}
              </label>
            </div>
            {netRate && <div style={{ fontSize: 11.5, color: '#92400e', marginTop: -6, marginBottom: 10 }}>{t('sp_ctq_round_netrate_help', 'We confirm this round by hand; it is not auto-booked or re-priced at checkout.')}</div>}

            {course !== 'manual' && loadingTee && <div style={{ padding: 20, textAlign: 'center', color: '#64748b' }}>{t('sp_ctq_checking_tee', 'Checking live tee times…')}</div>}

            {course !== 'manual' && tee && tee.slots?.length > 0 && (
              <div style={{ display: 'grid', gap: 4, maxHeight: 300, overflowY: 'auto' }}>
                {[...tee.slots].sort((a, b) => teeMinutes(a.teeTime) - teeMinutes(b.teeTime)).map((s, i) => {
                  // Highlight + check the slot this round is currently booked on.
                  const sel = highlightTee && teeMinutes(s.teeTime) === teeMinutes(highlightTee)
                  return (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: `1px solid ${sel ? '#16a34a' : '#e2e8f0'}`, background: sel ? '#f0fdf4' : '#fff', boxShadow: sel ? '0 0 0 2px rgba(22,163,74,0.15)' : 'none', borderRadius: 8, padding: '7px 10px' }}>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>
                      {sel && <span style={{ color: '#16a34a', marginRight: 5 }} title={t('sp_ctq_current_pick', 'Currently selected')}>&#10003;</span>}
                      {fmtTee(s.teeTime)} <span style={{ fontSize: 11.5, color: '#94a3b8', fontWeight: 400 }}>· {s.holes || 18} {t('sp_ctq_holes', 'holes')} · {s.availableSlots} {t('sp_ctq_open', 'open')}</span></span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 12.5 }}><b>{moneyc(s.pricePerGolfer, cur)}</b> / {t('sp_ctq_golfer_one', 'golfer')}</span>
                      <button onClick={() => pickLive(s)} disabled={adding} style={{ padding: '6px 12px', borderRadius: 6, fontWeight: 600, fontSize: 12.5, border: sel ? '1px solid #16a34a' : 'none', background: sel ? '#16a34a' : '#0f172a', color: '#fff', cursor: 'pointer' }}>{sel ? t('sp_ctq_current_pick', 'Current') : t('sp_ctq_add', 'Add')}</button>
                    </span>
                  </div>
                  )
                })}
              </div>
            )}

            {(course === 'manual' || (tee && (tee.fallback || tee.slots?.length === 0))) && (
              <div style={{ marginTop: 10, padding: 12, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8 }}>
                <div style={{ fontSize: 12.5, color: '#92400e', marginBottom: 8 }}>
                  {course === 'manual' ? t('sp_ctq_manual_hint', 'No live tee sheet — enter an estimated time and price.') : (tee?.message || t('sp_ctq_no_live', 'No live tee times — enter an estimated time and price.'))}
                </div>
                {course === 'manual' && <input placeholder={t('sp_ctq_course', 'Course')} value={manualName} onChange={e => setManualName(e.target.value)} style={{ ...inp, width: '100%', marginBottom: 8 }} />}
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <label style={{ fontSize: 12.5, color: '#475569', display: 'flex', gap: 6, alignItems: 'center' }}>{t('sp_ro_preferred_window', 'Preferred time window')}
                    <select value={manualTime} onChange={e => setManualTime(e.target.value)} style={inp}>
                      {TIME_WINDOWS.map(w => <option key={w.value} value={w.value}>{w.label}</option>)}
                    </select></label>
                  <label style={{ fontSize: 12.5, color: '#475569', display: 'flex', gap: 6, alignItems: 'center' }}>{t('sp_ctq_price_glf', '$/golfer')}
                    <input type="number" min="0" value={manualPrice} onChange={e => setManualPrice(e.target.value)} style={{ ...inp, width: 90 }} placeholder="120" /></label>
                  <button onClick={addManual} disabled={adding || !manualPrice || (course === 'manual' && !manualName)} style={{ padding: '7px 14px', borderRadius: 6, fontWeight: 600, fontSize: 12.5, border: 'none', background: '#0f172a', color: '#fff', cursor: 'pointer' }}>{t('sp_ctq_add', 'Add')}</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function AddCustomForm({ onAdd, onCancel, t }) {
  const [f, setF] = useState({ label: '', totalPrice: '' })
  const inp = { padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12.5 }
  return (
    <div style={{ display: 'flex', gap: 6, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
      <input placeholder={t('sp_ctq_label', 'Label (e.g. Airport transfer)')} value={f.label} onChange={e => setF({ ...f, label: e.target.value })} style={{ ...inp, flex: 1, minWidth: 180 }} />
      <input type="number" min="0" placeholder={t('sp_ctq_amount', 'amount')} value={f.totalPrice} onChange={e => setF({ ...f, totalPrice: e.target.value })} style={{ ...inp, width: 100 }} />
      <button onClick={() => onAdd(f)} disabled={!f.totalPrice} style={{ padding: '6px 12px', fontSize: 12.5, fontWeight: 600, border: 'none', borderRadius: 6, background: '#0f172a', color: '#fff', cursor: 'pointer' }}>{t('sp_ctq_add', 'Add')}</button>
      <button onClick={onCancel} style={{ padding: '6px 10px', fontSize: 12.5, border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff', cursor: 'pointer' }}>{t('sp_ctq_cancel', 'Cancel')}</button>
    </div>
  )
}

function RefundsPanel({ refunds, refundedTotal, cur, t }) {
  return (
    <div style={{ marginBottom: 12, padding: '12px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#92400e' }}>↩ {t('pr_refunds_heading', 'Refunds issued')}</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#92400e' }}>{money(refundedTotal, cur)}</span>
      </div>
      <div style={{ marginTop: 8, display: 'grid', gap: 6 }}>
        {refunds.map((r, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, fontSize: 12.5, color: '#78350f' }}>
            <span style={{ minWidth: 0 }}>
              <strong>{money(r.amount, cur)}</strong>
              {r.payerName ? ` → ${r.payerName}` : ''}
              {r.reason ? <span style={{ color: '#a16207' }}> · {r.reason}</span> : ''}
            </span>
            <span style={{ color: '#a16207', flexShrink: 0, whiteSpace: 'nowrap' }}>{r.createdAt ? new Date(r.createdAt).toLocaleDateString() : ''}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function SummaryCard({ quote, totals, cur, refundedTotal = 0, onServiceFee, t, hasExpediaHotel = false }) {
  // Expedia BP11: the agent accepts the Package Rate Rules when the trip includes
  // an Expedia package-rate hotel.
  const [pkgRulesAccepted, setPkgRulesAccepted] = useState(false)
  const T = totals || {}
  const over = T.overBudgetPerGolfer
  const row = (label, val, bold) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '3px 0', fontWeight: bold ? 700 : 400, color: bold ? '#0f172a' : '#475569' }}>
      <span>{label}</span><span>{val}</span>
    </div>
  )
  return (
    <div style={{ position: 'sticky', top: 12, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <span style={{ fontSize: 13, fontWeight: 700 }}>{t('sp_ctq_summary', 'Trip total')}</span>
        {/* Trip currency — fixed by the destination country (US→USD, CA→CAD).
            Display-only: the reseller can't override it. */}
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: '#64748b' }}>
          {t('sp_ctq_trip_currency', 'Trip currency')}
          <span style={{ fontSize: 12, fontWeight: 600, color: '#0f172a' }}>{cur}</span>
        </span>
      </div>
      {row(t('sp_ctq_package_price', 'Package price'), money(T.itemsSubtotal, cur))}

      {/* Service fee editor */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, padding: '3px 0', color: '#475569' }}>
        <span>{quote.serviceFeeLabel || t('sp_ctq_service_fee', 'Professional Services')}</span>
        <span>{money(T.serviceFee, cur)}</span>
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', margin: '4px 0 8px', flexWrap: 'wrap' }}>
        <select value={quote.serviceFeeType || ''} onChange={e => onServiceFee({ serviceFeeType: e.target.value || null })}
          style={{ fontSize: 12, padding: '3px 4px', border: '1px solid #e2e8f0', borderRadius: 6 }}>
          <option value="">{t('sp_ctq_none', 'none')}</option>
          <option value="percent">%</option>
          <option value="amount">{t('sp_ctq_per_golfer_opt', '$ / golfer')}</option>
        </select>
        {quote.serviceFeeType && (
          // key by type so switching %/$ re-mounts with the saved value (remembers it)
          <input type="number" min="0" key={quote.serviceFeeType} defaultValue={quote.serviceFeeValue ?? ''} placeholder={quote.serviceFeeType === 'percent' ? '5' : '100'}
            onBlur={e => onServiceFee({ serviceFeeValue: e.target.value === '' ? null : Number(e.target.value) })}
            style={{ fontSize: 12, padding: '3px 6px', border: '1px solid #e2e8f0', borderRadius: 6, width: 70 }} />
        )}
        {quote.serviceFeeType === 'amount' && (T.numGolfers > 0 || quote.numGolfers > 0) && (
          <span style={{ fontSize: 11, color: '#94a3b8' }}>× {T.numGolfers || quote.numGolfers} {t('sp_ctq_golfers_word', 'golfers')}</span>
        )}
      </div>

      {/* Combine the professional fee into the customer's package price (display
          only — you still see it itemized above; the total never changes). */}
      {quote.serviceFeeType && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', margin: '0 0 8px' }}>
          <div style={{ marginTop: 1 }}>
            <Toggle size="sm" checked={!!quote.combineServiceFee}
              onChange={next => onServiceFee({ combineServiceFee: next })} />
          </div>
          <span style={{ fontSize: 12, color: '#475569', lineHeight: 1.4 }}>
            {t('sp_ctq_combine_fee_quote', 'Show as one combined price to the customer')}
            <span style={{ display: 'block', fontSize: 11, color: '#94a3b8' }}>
              {t('sp_ctq_combine_fee_quote_help', 'Folds the professional fee into the package price on the customer quote. You still see it itemized here.')}
            </span>
          </span>
        </div>
      )}

      {/* USD conversion markup on hotels + golf (USD trips only) — folded into
          the customer's package price; shown here so the reseller sees it. */}
      {T.usdFxMarkup > 0 && (
        <div style={{ padding: '3px 0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#475569' }}>
            <span>{t('sp_ctq_usd_fx', 'USD conversion')}{T.usdFxPct ? ` (${+((T.usdFxPct) * 100).toFixed(2)}%)` : ''}</span>
            <span>{money(T.usdFxMarkup, cur)}</span>
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1, lineHeight: 1.35 }}>
            {t('sp_ctq_usd_fx_note', 'Added to hotel + golf on USD trips to cover the USD→CAD conversion. Rolled into the customer’s package price.')}
          </div>
        </div>
      )}

      {/* InteGolf platform fee — added to the taxable base + total. The customer
          sees it rolled into the package price, not as a separate line. */}
      {T.integolfFee > 0 && (
        <div style={{ padding: '3px 0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#475569' }}>
            <span>{t('sp_ctq_integolf_fee', 'InteGolf fees')}{T.integolfFeePct ? ` (${+((T.integolfFeePct) * 100).toFixed(2)}%)` : ''}</span>
            <span>{money(T.integolfFee, cur)}</span>
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1, lineHeight: 1.35 }}>
            {t('sp_ctq_integolf_note', 'Rolled into the package price the customer sees — not shown to them as a separate line.')}
          </div>
        </div>
      )}

      {row(`${t('sp_ctq_tax', 'Taxes and fees')}${T.taxRate ? ` (${(T.taxRate * 100).toFixed(1)}%)` : ''}`, money(T.tax, cur))}
      <div style={{ borderTop: '1px solid #e2e8f0', margin: '6px 0' }} />
      {row(t('sp_ctq_total', 'Total'), `${money(T.total, cur)} ${cur}`, true)}
      {T.numGolfers > 0 && row(`${t('sp_ctq_per_golfer', 'Per golfer')} (${T.numGolfers})`, money(T.perGolfer, cur))}

      {refundedTotal > 0 && (
        <>
          <div style={{ borderTop: '1px solid #e2e8f0', margin: '6px 0' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '3px 0', color: '#b45309' }}>
            <span>{t('pr_refunded_line', 'Refunded')}</span><span>−{money(refundedTotal, cur)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '3px 0', fontWeight: 700, color: '#0f172a' }}>
            <span>{t('pr_net_after_refunds', 'Net after refunds')}</span><span>{money(Math.max(0, (Number(T.total) || 0) - refundedTotal), cur)}</span>
          </div>
        </>
      )}

      {over != null && (
        <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 8, fontSize: 12.5,
          background: over > 0 ? '#fffbeb' : '#f0fdf4', color: over > 0 ? '#92400e' : '#166534', border: `1px solid ${over > 0 ? '#fde68a' : '#bbf7d0'}` }}>
          {over > 0
            ? `${money(over, cur)} ${t('sp_ctq_over', 'over the')} ${money(quote.budgetPerGolfer, cur)} ${t('sp_ctq_budget_pp', 'budget/golfer')}`
            : `${money(Math.abs(over), cur)} ${t('sp_ctq_under', 'under the')} ${money(quote.budgetPerGolfer, cur)} ${t('sp_ctq_budget_pp', 'budget/golfer')}`}
        </div>
      )}

      {/* Expedia BP11: agent accepts the Package Rate Rules (package-rate hotels). */}
      {hasExpediaHotel && (
        <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 8, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
          <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12, color: '#475569', cursor: 'pointer' }}>
            <input type="checkbox" checked={pkgRulesAccepted} onChange={e => setPkgRulesAccepted(e.target.checked)} style={{ marginTop: 2, flexShrink: 0 }} />
            <span><b>{t('sp_ctq_pkg_rules_accept', 'I accept the Package Rate Rules')}</b> — {t('sp_ctq_pkg_rules_note', 'package rates are sold as one bundled total; the hotel price is not shown separately to the traveler, and cancellation follows the property policy shown on the quote.')}</span>
          </label>
        </div>
      )}
    </div>
  )
}

// Reseller-set payment options for this quote: which plans the customer may
// choose, deposit size, and the final-payment date — with a live schedule
// preview and the always-collected up-front floor (hotels + fees + tax).
function PaymentOptionsCard({ quote, paymentPlan, cur, onSave, t }) {
  const [previewTab, setPreviewTab] = useState(null)
  const [depWarn, setDepWarn] = useState(null)
  const [dateWarn, setDateWarn] = useState(null)
  const pp = paymentPlan || {}
  const plans = pp.plans || {}
  const m = (n) => money(n, cur)
  // The deposit can't be below the up-front floor; the final payment can't be
  // within 30 days of the trip's first day.
  const floor = pp.upfrontFloor
  const depMinPct = (floor != null && pp.total > 0) ? Math.ceil(floor / pp.total * 100) : null
  const depMinLabel = quote.depositType === 'amount' ? (floor != null ? money(floor, cur) : null) : (depMinPct != null ? depMinPct + '%' : null)
  const finalMax = quote.tripStartDate ? addDays(toYmd(quote.tripStartDate), -30) : null
  const ckRow = { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#334155', padding: '3px 0', cursor: 'pointer' }
  const sel = { fontSize: 12, padding: '3px 6px', border: '1px solid #e2e8f0', borderRadius: 6 }
  // Keep at least one plan enabled.
  const toggle = (key, val) => {
    const nxt = { allowFull: quote.allowFull, allowDeposit: quote.allowDeposit, allowInstallments: quote.allowInstallments, [key]: val }
    if (!nxt.allowFull && !nxt.allowDeposit && !nxt.allowInstallments) return
    onSave({ [key]: val })
  }
  // Trip within 30 days -> no valid final-payment date -> deferred plans off.
  const insideThirty = !!pp.insideThirty
  const depOn = quote.allowDeposit && !insideThirty
  const instOn = quote.allowInstallments && !insideThirty
  const showDefer = depOn || instOn
  const whenLbl = (w) => w === 'now' ? t('sp_ctq_now', 'now') : (w ? fmtDay(String(w).slice(0, 10)) : t('sp_ctq_final_date', 'final date'))
  const preview = (p) => p ? (
    <div style={{ marginTop: 8, fontSize: 11.5, color: '#475569' }}>
      {p.schedule.map((s, i) => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <span>{i === 0 ? t('sp_ctq_first_charge', 'First charge') : `${t('sp_ctq_payment_word', 'Payment')} ${s.installmentNo || i + 1}`} · {whenLbl(s.when)}</span>
          <b>{m(s.amount)}</b>
        </div>
      ))}
    </div>
  ) : null
  const planLabels = { full: t('sp_ctq_pay_full', 'Pay in full'), deposit: t('sp_ctq_deposit_balance', 'Deposit + balance'), installments: t('sp_ctq_installments', 'Installments') }
  // Short labels so all three tabs fit on one row in the narrow sidebar.
  const tabLabels = { full: t('sp_ctq_tab_full', 'Full'), deposit: t('sp_ctq_tab_deposit', 'Deposit'), installments: t('sp_ctq_tab_installments', 'Installments') }
  const previewKeys = ['full', 'deposit', 'installments'].filter(k => plans[k])
  const previewSel = (previewTab && plans[previewTab]) ? previewTab : previewKeys[0]

  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>{t('sp_ctq_payment_options', 'Payment options')}</div>
      <div style={{ fontSize: 11.5, color: '#64748b', marginBottom: 10 }}>{t('sp_ctq_pay_hint', 'What the customer can choose at acceptance.')}</div>

      {pp.upfrontFloor != null && (
        <div style={{ padding: '8px 10px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, fontSize: 11.5, color: '#075985', marginBottom: 10, lineHeight: 1.45 }}>
          <b>{m(pp.upfrontFloor)}</b> {t('sp_ctq_upfront_note', 'is always collected on the first charge (hotels + fees + tax). Only')} <b>{m(pp.deferrableMax)}</b> {t('sp_ctq_can_defer', 'can be deferred.')}
        </div>
      )}

      <label style={ckRow}><input type="checkbox" checked={!!quote.allowFull} onChange={e => toggle('allowFull', e.target.checked)} /> {t('sp_ctq_pay_full', 'Pay in full')}</label>
      <label style={{ ...ckRow, opacity: insideThirty ? 0.5 : 1 }}><input type="checkbox" disabled={insideThirty} checked={!!quote.allowDeposit && !insideThirty} onChange={e => toggle('allowDeposit', e.target.checked)} /> {t('sp_ctq_deposit_balance', 'Deposit + balance')}</label>
      <label style={{ ...ckRow, opacity: insideThirty ? 0.5 : 1 }}><input type="checkbox" disabled={insideThirty} checked={!!quote.allowInstallments && !insideThirty} onChange={e => toggle('allowInstallments', e.target.checked)} /> {t('sp_ctq_installments', 'Installments')}</label>
      {insideThirty && <div style={{ fontSize: 11, color: '#b45309', marginTop: 2 }}>{t('sp_ctq_inside_30', 'Trip is within 30 days — only Pay in full is available.')}</div>}

      {depOn && (
        <div style={{ marginTop: 8 }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: '#475569' }}>{t('sp_ctq_deposit', 'Deposit')}</span>
            <select value={quote.depositType || 'percent'} onChange={e => onSave({ depositType: e.target.value })} style={sel}>
              <option value="percent">%</option>
              <option value="amount">$</option>
            </select>
            <input type="number" min="0" defaultValue={quote.depositValue ?? ''} key={'dep' + (quote.depositType || 'percent')}
              onBlur={e => {
                let v = e.target.value === '' ? null : Number(e.target.value)
                // Enforce the floor: raise a too-low deposit to the minimum.
                if (v != null && floor != null) {
                  const minV = quote.depositType === 'amount' ? floor : (depMinPct || v)
                  if (v < minV) { v = minV; e.target.value = String(minV); setDepWarn(t('sp_ctq_deposit_min_warn', 'Raised to the {min} minimum — the deposit must cover the hotels + fees.', { min: depMinLabel })) }
                  else setDepWarn(null)
                }
                onSave({ depositValue: v })
              }}
              placeholder={quote.depositType === 'amount' ? '1000' : '25'} style={{ ...sel, width: 64 }} />
            {depMinLabel && <span style={{ fontSize: 11, color: '#94a3b8' }}>{t('sp_ctq_min', 'min')} {depMinLabel}</span>}
          </div>
          {depWarn && <div style={{ marginTop: 4, fontSize: 11, color: '#b45309' }}>{depWarn}</div>}
        </div>
      )}

      {instOn && (
        <div style={{ marginTop: 8, display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: '#475569' }}>{t('sp_ctq_num_payments', '# of payments')}</span>
          <input type="number" min="2" max="24" defaultValue={quote.installmentCount ?? 3} key={'ins' + (quote.installmentCount ?? 3)}
            onBlur={e => onSave({ installmentCount: e.target.value === '' ? null : Number(e.target.value) })} style={{ ...sel, width: 56 }} />
        </div>
      )}

      {showDefer && (
        <div style={{ marginTop: 8 }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: '#475569' }}>{t('sp_ctq_final_by', 'Final payment by')}</span>
            <input type="date" value={quote.finalPaymentDate ? String(quote.finalPaymentDate).slice(0, 10) : ''} max={finalMax || undefined}
              onChange={e => {
                const v = e.target.value || null
                // Must be at least 30 days before the trip's first day.
                if (v && finalMax && v > finalMax) { setDateWarn(t('sp_ctq_final_max_warn', 'Must be at least 30 days before the trip — on or before {d}.', { d: fmtDay(finalMax) })); return }
                setDateWarn(null); onSave({ finalPaymentDate: v })
              }} style={sel} />
            {finalMax && <span style={{ fontSize: 11, color: '#94a3b8' }}>≤ {fmtDay(finalMax)}</span>}
          </div>
          {dateWarn && <div style={{ marginTop: 4, fontSize: 11, color: '#b45309' }}>{dateWarn}</div>}
        </div>
      )}

      {previewKeys.length > 0 && (
        <div style={{ marginTop: 12, borderTop: '1px solid #f1f5f9', paddingTop: 10 }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, color: '#94a3b8', letterSpacing: 0.4, marginBottom: 6 }}>{t('sp_ctq_preview_lbl', 'PREVIEW')}</div>
          {previewKeys.length > 1
            ? <div style={{ display: 'flex', gap: 4 }}>
                {previewKeys.map(k => (
                  <button key={k} type="button" onClick={() => setPreviewTab(k)}
                    style={{ flex: 1, padding: '4px 4px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', textAlign: 'center', whiteSpace: 'nowrap', border: `1px solid ${previewSel === k ? '#0f172a' : '#e2e8f0'}`, background: previewSel === k ? '#0f172a' : '#fff', color: previewSel === k ? '#fff' : '#475569' }}>
                    {tabLabels[k]}
                  </button>
                ))}
              </div>
            : <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{planLabels[previewSel]}</div>}
          {preview(plans[previewSel])}
        </div>
      )}
    </div>
  )
}
